import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectTeamIds, selectTeams, selectUserId, selectTeamType, selectYearOfBirth, selectSportsClubId, selectSelectedTeamId, setSelectedTeamId as setReduxSelectedTeamId, selectExpressModeEnabled, selectExpressModeThreshold } from '../store/userSlice';
import { useAuth } from '../context/AuthContext';
import {
  PreseasonGameSummaryControllerService,
  TermControllerService,
  PreseasonTimeControllerService,
  DictionaryControllerService,
  TeamControllerService,
  PreseasonGameControllerService,
  CalendarService,
  CalendarBlockControllerService,
  PreseasonGameDateProposalControllerService,
  SportsClubControllerService,
  SeasonDefinitionControllerService,
  PreseasonGameInvitationControllerService
} from '../api/index.ts';
import { toast } from 'react-toastify';
import { getStartOfWeek, formatLocalDate } from '../utils/calendarUtils';
import WeeklyCalendar from './WeeklyCalendar';
import MapPickerModal from './MapPickerModal';
import './PreseasonGamesSummary.css';

function PreseasonGamesSummary() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasRole } = useAuth();
  const isCoach = hasRole('COACH');
  const isSportsClub = hasRole('SPORTS_CLUB');
  const userId = useSelector(selectUserId);
  const teamIds = useSelector(selectTeamIds);
  const coachTeamId = teamIds?.[0];
  const teamType = useSelector(selectTeamType);
  const yearOfBirth = useSelector(selectYearOfBirth);
  const sportsClubId = useSelector(selectSportsClubId);
  const expressModeEnabled = useSelector(selectExpressModeEnabled);
  const expressModeThreshold = useSelector(selectExpressModeThreshold);
  const reduxTeams = useSelector(selectTeams); // Drużyny coacha z Redux (zawierają id, teamName, isPreseasonDefined)
  const reduxSelectedTeamId = useSelector(selectSelectedTeamId);

  // Funkcja pomocnicza do wyodrębnienia roku z yearOfBirth (np. 'YEAR_2007' -> 2007)
  const getYearFromYearOfBirth = (yob) => {
    if (!yob) return null;
    const match = yob.match(/YEAR_(\d{4})/);
    return match ? parseInt(match[1]) : null;
  };

  // Oblicz czy trener juniorski ma mniej niż 16 lat
  const currentYear = new Date().getFullYear();
  const coachBirthYear = getYearFromYearOfBirth(yearOfBirth);
  const coachAge = coachBirthYear ? currentYear - coachBirthYear : null;
  const isUnder16 = coachAge !== null && coachAge < 16;

  // Stany dla sportsclub - wybór drużyny
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Aktywne teamId - z Redux dla obu typów użytkowników
  const activeTeamId = reduxSelectedTeamId || coachTeamId || null;

  // Tryb aktywnej drużyny (SEASONAL / YEAR_ROUND)
  const getActiveTeamMode = () => {
    if (isSportsClub && teams.length > 0) {
      const activeTeam = teams.find(t => t.teamId === activeTeamId);
      return activeTeam?.teamMode || 'SEASONAL';
    }
    if (isCoach && reduxTeams?.length > 0) {
      const activeTeam = reduxTeams.find(t => t.id === activeTeamId);
      return activeTeam?.teamMode || 'SEASONAL';
    }
    return 'SEASONAL';
  };
  const isYearRound = getActiveTeamMode() === 'YEAR_ROUND';

  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, game: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [addGameInTermModal, setAddGameInTermModal] = useState({ isOpen: false, schedule: null });
  const [isAddingGameInTerm, setIsAddingGameInTerm] = useState(false);
  const [removeGameFromTermModal, setRemoveGameFromTermModal] = useState({ isOpen: false, schedule: null });
  const [isRemovingGameFromTerm, setIsRemovingGameFromTerm] = useState(false);

  // Stany dla modalu edycji okresu
  const [editPeriodModal, setEditPeriodModal] = useState({ isOpen: false });
  const [editPeriodData, setEditPeriodData] = useState({ startDate: '', endDate: '' });
  const [isSavingPeriod, setIsSavingPeriod] = useState(false);
  const [editPeriodError, setEditPeriodError] = useState(null);

  // Stany dla modalu udostępniania linku zaproszenia (PreseasonGameInvitation)
  const [shareInvitationModal, setShareInvitationModal] = useState({ isOpen: false });
  const [shareSelectedTermIds, setShareSelectedTermIds] = useState([]);
  const [shareGenerated, setShareGenerated] = useState(null);
  const [shareIsGenerating, setShareIsGenerating] = useState(false);
  const [shareList, setShareList] = useState([]);
  const [shareListLoading, setShareListLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareDeleteConfirm, setShareDeleteConfirm] = useState({ isOpen: false, invitation: null });
  const [isDeletingShare, setIsDeletingShare] = useState(false);

  // Stany dla modalu dodawania terminu
  const [addTermModal, setAddTermModal] = useState({ isOpen: false });
  const [addTermData, setAddTermData] = useState({ startDate: '', endDate: '', seniorTiers: [] });
  const [isSavingTerm, setIsSavingTerm] = useState(false);
  const [addTermError, setAddTermError] = useState(null);
  const [addTermSeasonDef, setAddTermSeasonDef] = useState(null);
  const [editTermModal, setEditTermModal] = useState({ isOpen: false, schedule: null });
  const [editTermData, setEditTermData] = useState({ startDate: '', endDate: '' });
  const [isSavingEditTerm, setIsSavingEditTerm] = useState(false);
  const [editTermError, setEditTermError] = useState(null);
  const [editTermSeasonDef, setEditTermSeasonDef] = useState(null);
  const [seniorTiers, setSeniorTiers] = useState([]);

  // Stany dla modalu edycji wymaganego przeciwnika
  const [editOpponentModal, setEditOpponentModal] = useState({ isOpen: false, game: null });
  const [editOpponentData, setEditOpponentData] = useState({
    opponentType: { senior: false, junior: false },
    opponentTiers: [],
    opponentYouthTiers: [],
    opponentYearOfBirths: []
  });
  const [isSavingOpponent, setIsSavingOpponent] = useState(false);
  const [editOpponentError, setEditOpponentError] = useState(null);
  const [youthTiers, setYouthTiers] = useState([]);
  const [yearOfBirths, setYearOfBirths] = useState([]);

  // Stan dla przełącznika widoków
  const [viewMode, setViewMode] = useState('operational'); // 'operational' lub 'calendar'
  const [mobileTab, setMobileTab] = useState('sparingi'); // 'sparingi', 'kalendarz', 'ustawienia'

  // Stany dla widoku kalendarza
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [previewCalendarData, setPreviewCalendarData] = useState([]);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);

  // Stan dla nawigatora przedziałów dat
  const [currentDateRangeIndex, setCurrentDateRangeIndex] = useState(0);
  const [mobileTermMenuOpen, setMobileTermMenuOpen] = useState(false);
  const preserveDateRangeIndexRef = useRef(false); // Ref do zachowania indeksu po akcji w modalu

  // Stany dla modalu wiadomości
  const [messagesModal, setMessagesModal] = useState({ isOpen: false, game: null });
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [gameDetails, setGameDetails] = useState(null);

  // Stany dla modalu anulowania sparingu
  const [cancelGameModal, setCancelGameModal] = useState({ isOpen: false, game: null });
  const [cancelReason, setCancelReason] = useState('');
  const [isCancellingGame, setIsCancellingGame] = useState(false);

  // Stany dla modalu blokowania godzin
  const [blockHoursModal, setBlockHoursModal] = useState({ isOpen: false, schedule: null });
  const [blockHoursData, setBlockHoursData] = useState({}); // { 'YYYY-MM-DD': [{ startTime: '', endTime: '' }] }
  const [isSavingBlockHours, setIsSavingBlockHours] = useState(false);
  const [blockHoursError, setBlockHoursError] = useState(null);

  // Stany dla modalu wyniku
  const [scoreModal, setScoreModal] = useState({ isOpen: false, game: null });
  const [scoreData, setScoreData] = useState({ scoreMyTeam: '', scoreOpponentTeam: '' });
  const [isSavingScore, setIsSavingScore] = useState(false);

  // Stany dla modalu "Znajdź" (wizard szukania przeciwnika)
  const [findOpponentModal, setFindOpponentModal] = useState({ isOpen: false, schedule: null });
  const [findOpponentStep, setFindOpponentStep] = useState(1);
  const [findOpponentSelectedTeam, setFindOpponentSelectedTeam] = useState('');
  const [findOpponentHasPitchAvailable, setFindOpponentHasPitchAvailable] = useState(false);
  const [findOpponentLocation, setFindOpponentLocation] = useState('');
  const [findOpponentPitchLatitude, setFindOpponentPitchLatitude] = useState(null);
  const [findOpponentPitchLongitude, setFindOpponentPitchLongitude] = useState(null);
  const [findOpponentMapPickerOpen, setFindOpponentMapPickerOpen] = useState(false);
  const [findOpponentSearchCriteria, setFindOpponentSearchCriteria] = useState({
    county: '',
    voivodeship: '',
    seniorTier: ''
  });
  const [findOpponentSearchResults, setFindOpponentSearchResults] = useState([]);
  const [findOpponentIsLoadingSearch, setFindOpponentIsLoadingSearch] = useState(false);
  const [findOpponentSearchError, setFindOpponentSearchError] = useState('');
  const [findOpponentSelectedOpponent, setFindOpponentSelectedOpponent] = useState(null);
  const [findOpponentMatchDate, setFindOpponentMatchDate] = useState('');
  const [findOpponentMatchTime, setFindOpponentMatchTime] = useState({ hour: 18, minute: 0 });
  const [findOpponentMatchEndTime, setFindOpponentMatchEndTime] = useState({ hour: 20, minute: 0 });
  const [findOpponentMatchMessage, setFindOpponentMatchMessage] = useState('');
  const [findOpponentSelectedTimeSlot, setFindOpponentSelectedTimeSlot] = useState(null);
  const [findOpponentOpponentWeekStart, setFindOpponentOpponentWeekStart] = useState(getStartOfWeek(new Date()));
  const [findOpponentOpponentGames, setFindOpponentOpponentGames] = useState([]);
  const [findOpponentIsLoadingOpponentGames, setFindOpponentIsLoadingOpponentGames] = useState(false);
  const [findOpponentOpponentOverallStats, setFindOpponentOpponentOverallStats] = useState([]);
  const [findOpponentOwnGamesForComparison, setFindOpponentOwnGamesForComparison] = useState([]);
  const [findOpponentOwnOverallStats, setFindOpponentOwnOverallStats] = useState([]);
  const [findOpponentHoveredDateForFullDay, setFindOpponentHoveredDateForFullDay] = useState(null);
  const [findOpponentIsTransitioning, setFindOpponentIsTransitioning] = useState(false);
  const [findOpponentIsSendingMatch, setFindOpponentIsSendingMatch] = useState(false);
  const [findOpponentCurrentPage, setFindOpponentCurrentPage] = useState(0);
  const [findOpponentTotalPages, setFindOpponentTotalPages] = useState(0);
  const [findOpponentTotalTeamsInSystem, setFindOpponentTotalTeamsInSystem] = useState(null);
  const [findOpponentDistanceSearch, setFindOpponentDistanceSearch] = useState(false);
  const [findOpponentMaxDistance, setFindOpponentMaxDistance] = useState(80);
  const [findOpponentEditDistance, setFindOpponentEditDistance] = useState(false);
  const [findOpponentDictionaries, setFindOpponentDictionaries] = useState({
    territories: [],
    seniorTiers: []
  });

  // Stany dla modalu "Zaproponuj termin" (dla gier bez ustalonej godziny)
  const [proposeTimeModal, setProposeTimeModal] = useState({ isOpen: false, game: null, schedule: null });
  const [proposeTimeDates, setProposeTimeDates] = useState([]);
  const [proposeTimeSelectedDay, setProposeTimeSelectedDay] = useState(null);
  const [proposeTimeSelectedTimeRange, setProposeTimeSelectedTimeRange] = useState(null);
  const [proposeTimeSelectedTimeSlot, setProposeTimeSelectedTimeSlot] = useState(null);
  const [isProposingTime, setIsProposingTime] = useState(false);
  const [proposeTimeCalendarBlocks, setProposeTimeCalendarBlocks] = useState([]);
  const [proposeTimeIsLoadingBlocks, setProposeTimeIsLoadingBlocks] = useState(false);
  const [proposeTimeLocation, setProposeTimeLocation] = useState('');
  const [proposeTimeMessage, setProposeTimeMessage] = useState('');

  // Stany dla nowego selektora daty/godziny w kroku 3
  const [findOpponentScheduleDates, setFindOpponentScheduleDates] = useState([]); // Wszystkie daty w wybranym schedule
  const [findOpponentCurrentDateIndex, setFindOpponentCurrentDateIndex] = useState(0); // Indeks aktualnie wyświetlanej daty
  const [findOpponentSelectedTimeRange, setFindOpponentSelectedTimeRange] = useState(null); // Wybrany przedział godzinowy (np. "08:00-10:00")
  const [findOpponentCalendarBlocks, setFindOpponentCalendarBlocks] = useState([]); // Blokady kalendarza z API
  const [findOpponentIsLoadingBlocks, setFindOpponentIsLoadingBlocks] = useState(false);
  const [findOpponentSelectedDay, setFindOpponentSelectedDay] = useState(null); // Wybrany dzień (string daty)
  const [findOpponentDateToBeDetermined, setFindOpponentDateToBeDetermined] = useState(false); // "Do ustalenia później"
  const [findOpponentShouldBlockCalendar, setFindOpponentShouldBlockCalendar] = useState(false); // "Czy blokować kalendarz na cały dzień"

  // Stan trybu przyspieszonego w wizardzie "Szukaj przeciwnika"
  const [findOpponentLastMinuteMode, setFindOpponentLastMinuteMode] = useState(false);
  const [findOpponentIgnoreDateOverlap, setFindOpponentIgnoreDateOverlap] = useState(false);

  // Stany dla modalu "Dodaj przeciwnika spoza systemu"
  const [manualOpponentModal, setManualOpponentModal] = useState({ isOpen: false, schedule: null });
  const [manualOpponentName, setManualOpponentName] = useState('');
  const [manualOpponentLocation, setManualOpponentLocation] = useState('');
  const [manualOpponentTier, setManualOpponentTier] = useState('');
  const [manualOpponentSelectedDay, setManualOpponentSelectedDay] = useState(null);
  const [manualOpponentSelectedTimeRange, setManualOpponentSelectedTimeRange] = useState(null);
  const [manualOpponentSelectedTimeSlot, setManualOpponentSelectedTimeSlot] = useState(null);
  const [manualOpponentDates, setManualOpponentDates] = useState([]);
  const [isSubmittingManualOpponent, setIsSubmittingManualOpponent] = useState(false);

  // Lokalizacja klubu z rejestracji (do pre-fill w wizardzie szukania przeciwnika)
  const [clubLatitude, setClubLatitude] = useState(null);
  const [clubLongitude, setClubLongitude] = useState(null);
  const [clubCity, setClubCity] = useState('');

  // Stany dla modala edycji obozu
  const [campEditModal, setCampEditModal] = useState({ isOpen: false, schedule: null });
  const [campEditIsAtCamp, setCampEditIsAtCamp] = useState(false);
  const [campEditAddress, setCampEditAddress] = useState('');
  const [campEditLatitude, setCampEditLatitude] = useState(null);
  const [campEditLongitude, setCampEditLongitude] = useState(null);
  const [campEditMaxDistanceKm, setCampEditMaxDistanceKm] = useState('');
  const [campMapPickerOpen, setCampMapPickerOpen] = useState(false);
  const [isSavingCamp, setIsSavingCamp] = useState(false);

  // Stany dla modalu "Dodaj wiele terminów"
  const [bulkTermModal, setBulkTermModal] = useState({ isOpen: false });
  const [bulkTermData, setBulkTermData] = useState({
    startDate: '',
    endDate: '',
  });
  const [bulkTermSchedules, setBulkTermSchedules] = useState([]);
  const [bulkTermOpponentTypes, setBulkTermOpponentTypes] = useState({});
  const [isSavingBulkTerm, setIsSavingBulkTerm] = useState(false);
  const [bulkTermError, setBulkTermError] = useState(null);
  const [bulkTermValidationErrors, setBulkTermValidationErrors] = useState([]);
  const [bulkTermOpponentModalOpen, setBulkTermOpponentModalOpen] = useState(null); // index sparingu lub null
  const [bulkTermSeasonDef, setBulkTermSeasonDef] = useState(null);

  // Pobierz drużyny dla sportsclub
  useEffect(() => {
    const fetchTeams = async () => {
      if (!isSportsClub || !sportsClubId) return;

      setTeamsLoading(true);
      try {
        const teamsData = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
        setTeams(teamsData || []);
        // Automatycznie wybierz pierwszą drużynę jeśli nie jest jeszcze wybrana
        if (teamsData && teamsData.length > 0 && teamsData[0]?.teamId && !reduxSelectedTeamId) {
          dispatch(setReduxSelectedTeamId(teamsData[0].teamId));
        }
      } catch (error) {
        toast.error('Nie udało się pobrać listy drużyn');
      } finally {
        setTeamsLoading(false);
      }
    };

    fetchTeams();
  }, [isSportsClub, sportsClubId]);

  useEffect(() => {
    if (activeTeamId) {
      fetchPreseasonSummary();
      fetchDictionaries();
    } else {
      setSummaryData(null);
      setIsLoading(false);
    }
  }, [activeTeamId]);

  // Pobierz lokalizację klubu z rejestracji (do pre-fill w szukaniu przeciwnika)
  useEffect(() => {
    const fetchClubLocation = async () => {
      try {
        let details;
        if (isSportsClub) {
          details = await SportsClubControllerService.getMyDetails();
        } else if (sportsClubId) {
          details = await SportsClubControllerService.getSportsClubDetailsById(sportsClubId);
        }
        if (details) {
          setClubLatitude(details.latitude || null);
          setClubLongitude(details.longitude || null);
          setClubCity(details.city || '');
        }
      } catch (e) {
        // nie krytyczne
      }
    };
    fetchClubLocation();
  }, [isSportsClub, sportsClubId]);

  const fetchPreseasonSummary = async () => {
    try {
      setIsLoading(true);
      const response = await PreseasonGameSummaryControllerService.getPreseasonGameSummary(activeTeamId);
      setSummaryData(response);
    } catch (error) {
      toast.error('Nie udało się pobrać danych o okresie przygotowawczym');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeamChange = (e) => {
    dispatch(setReduxSelectedTeamId(Number(e.target.value)));
    setSummaryData(null);
  };

  // Dropdown drużyn - dla SPORTS_CLUB (lokalny state) i COACH (Redux)
  const renderTeamModeLabel = () => {
    const mode = getActiveTeamMode();
    return (
      <div className={`team-mode-label ${mode === 'YEAR_ROUND' ? 'team-mode-year-round' : 'team-mode-seasonal'}`}>
        {mode === 'YEAR_ROUND' ? 'Tryb całoroczny' : 'Tryb sezonowy'}
      </div>
    );
  };

  const renderTeamDropdown = () => {
    if (isCoach && reduxTeams?.length > 0) {
      return (
        <div className="team-selector-section">
          <div className="form-group">
            <select
              id="teamSelect"
              className="form-select"
              value={reduxSelectedTeamId || coachTeamId || ''}
              onChange={(e) => dispatch(setReduxSelectedTeamId(Number(e.target.value)))}
            >
              {reduxTeams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.teamName}
                </option>
              ))}
            </select>
          </div>
          {/* renderTeamModeLabel() - hidden for seniors-only seasonal mode */}
        </div>
      );
    }

    if (!isSportsClub || teams.length <= 1) {
      // Nawet bez dropdowna pokaż tryb jeśli jest jedna drużyna
      if ((isSportsClub && teams.length === 1) || (isCoach && reduxTeams?.length === 1)) {
        return (
          <div className="team-selector-section">
            {/* renderTeamModeLabel() - hidden for seniors-only seasonal mode */}
          </div>
        );
      }
      return null;
    }

    return (
      <div className="team-selector-section">
        <div className="form-group">
          <select
            id="teamSelect"
            className="form-select"
            value={reduxSelectedTeamId || ''}
            onChange={handleTeamChange}
          >
            {teams.map(team => (
              <option key={team.teamId} value={team.teamId}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
        {/* renderTeamModeLabel() - hidden for seniors-only seasonal mode */}
      </div>
    );
  };

  const fetchDictionaries = async () => {
    try {
      const seniorData = await DictionaryControllerService.getSeniorTeamsSearchDictionaries();
      setSeniorTiers(seniorData.tiers || []);
      setFindOpponentDictionaries({
        territories: seniorData.territories || [],
        seniorTiers: seniorData.tiers || []
      });
      // Domyślnie ustaw roczniki 16+ (dla seniorów)
      setYearOfBirths(seniorData.yearOfBirths || []);
    } catch (error) {
      // error handled silently
    }

    // Dla juniorskich drużyn - pobierz youth tiers i nadpisz pełną listą roczników
    if (!activeTeamId) return;
    try {
      const youthData = await DictionaryControllerService.getYouthTeamsSearchDictionaries(activeTeamId);
      setYouthTiers(youthData.tiers || []);
      // Tylko dla juniorskich drużyn nadpisz roczniki pełną listą
      if (teamType === 'YOUTH' && youthData.yearOfBirths && youthData.yearOfBirths.length > 0) {
        setYearOfBirths(youthData.yearOfBirths);
      }
    } catch (error) {
      // error handled silently
    }
  };


  // Reset indeksu nawigatora przedziałów dat gdy zmieni się summaryData (chyba że zachowujemy pozycję)
  useEffect(() => {
    if (preserveDateRangeIndexRef.current) {
      preserveDateRangeIndexRef.current = false; // Zresetuj flagę po użyciu
    } else {
      setCurrentDateRangeIndex(0);
    }
  }, [summaryData]);

  // === Logika widoku kalendarza ===
  const dayNames = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nie'];
  const monthNames = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    const days = [];
    for (let i = 0; i < adjustedStart; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const isTodayDate = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const getDateEntry = (day) => {
    if (!day) return null;
    const dateStr = formatLocalDate(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    const entry = previewCalendarData.find(e => e.date === dateStr);
    return entry || null;
  };

  const fetchPreviewCalendar = async (month) => {
    if (!activeTeamId) return;
    setIsLoadingCalendar(true);
    try {
      const year = month.getFullYear();
      const m = month.getMonth();
      const startDate = formatLocalDate(year, m, 1);
      const lastDay = new Date(year, m + 1, 0).getDate();
      const endDate = formatLocalDate(year, m, lastDay);
      const data = await CalendarService.getPreviewCalendar(activeTeamId, startDate, endDate);
      setPreviewCalendarData(data || []);
    } catch (error) {
      toast.error('Nie udało się pobrać danych kalendarza');
      setPreviewCalendarData([]);
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  // Ustaw kalendarz na miesiąc pierwszego terminu
  useEffect(() => {
    if (summaryData?.games?.length > 0) {
      const firstDate = summaryData.games
        .map(g => g.approximateStartDate)
        .filter(Boolean)
        .sort()[0];
      if (firstDate) {
        const [year, month] = firstDate.split('-').map(Number);
        setCurrentMonth(new Date(year, month - 1, 1));
      }
    }
  }, [summaryData]);

  useEffect(() => {
    if ((viewMode === 'calendar' || mobileTab === 'kalendarz') && activeTeamId) {
      fetchPreviewCalendar(currentMonth);
    }
  }, [viewMode, mobileTab, currentMonth, activeTeamId]);

  const canDeleteTerm = (schedule) => {
    const noCreatedProposals = !schedule.amountOfCreatedProposals || schedule.amountOfCreatedProposals === 0;
    const noReceivedProposals = !schedule.amountOfReceivedProposals || schedule.amountOfReceivedProposals === 0;
    const noAgreedGames = !schedule.agreedGames || schedule.agreedGames.length === 0;

    return noCreatedProposals && noReceivedProposals && noAgreedGames;
  };

  const handleDeleteTermClick = (game) => {
    setDeleteConfirmModal({ isOpen: true, game });
  };

  const handleDeleteConfirm = async () => {
    const game = deleteConfirmModal.game;
    if (!game || !game.termId) {
      toast.error('Nie można usunąć terminu - brak identyfikatora');
      setDeleteConfirmModal({ isOpen: false, game: null });
      return;
    }

    if (!canDeleteTerm(game)) {
      toast.error('Nie można usunąć terminu z propozycjami lub potwierdzonym przeciwnikiem');
      return;
    }

    try {
      setIsDeleting(true);
      await TermControllerService.deleteTerm(game.termId);
      toast.success('Termin został usunięty');
      setDeleteConfirmModal({ isOpen: false, game: null });
      fetchPreseasonSummary();
    } catch (error) {
      const msg = error?.body?.message || 'Nie udało się usunąć terminu';
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ isOpen: false, game: null });
  };

  const handleAddGameInTermClick = (schedule) => {
    setAddGameInTermModal({ isOpen: true, schedule });
  };

  const handleAddGameInTermConfirm = async () => {
    const schedule = addGameInTermModal.schedule;
    if (!schedule?.termId) {
      toast.error('Nie można dodać meczu - brak identyfikatora terminu');
      setAddGameInTermModal({ isOpen: false, schedule: null });
      return;
    }

    try {
      setIsAddingGameInTerm(true);
      await TermControllerService.incrementAmountOfGames(schedule.termId);
      toast.success('Dodano mecz w terminie');
      setAddGameInTermModal({ isOpen: false, schedule: null });
      preserveDateRangeIndexRef.current = true;
      fetchPreseasonSummary();
    } catch (error) {
      const msg = error?.body?.message || 'Nie udało się dodać meczu w terminie';
      toast.error(msg);
    } finally {
      setIsAddingGameInTerm(false);
    }
  };

  const handleRemoveGameFromTermClick = (schedule) => {
    setRemoveGameFromTermModal({ isOpen: true, schedule });
  };

  const handleRemoveGameFromTermConfirm = async () => {
    const schedule = removeGameFromTermModal.schedule;
    if (!schedule?.termId) {
      toast.error('Nie można usunąć meczu - brak identyfikatora terminu');
      setRemoveGameFromTermModal({ isOpen: false, schedule: null });
      return;
    }

    try {
      setIsRemovingGameFromTerm(true);
      await TermControllerService.decrementAmountOfGames(schedule.termId);
      toast.success('Usunięto nieobsadzony mecz z terminu');
      setRemoveGameFromTermModal({ isOpen: false, schedule: null });
      preserveDateRangeIndexRef.current = true;
      fetchPreseasonSummary();
    } catch (error) {
      const msg = error?.body?.message || 'Nie udało się usunąć meczu z terminu';
      toast.error(msg);
    } finally {
      setIsRemovingGameFromTerm(false);
    }
  };

  // Otwieranie modala edycji obozu
  const handleCampEditOpen = (schedule) => {
    setCampEditIsAtCamp(schedule.isAtCamp || false);
    setCampEditAddress(schedule.campAddress || '');
    setCampEditLatitude(schedule.campLatitude || null);
    setCampEditLongitude(schedule.campLongitude || null);
    setCampEditMaxDistanceKm(schedule.campMaxDistanceKm || '');
    setCampEditModal({ isOpen: true, schedule });
  };

  const handleCampEditClose = () => {
    setCampEditModal({ isOpen: false, schedule: null });
  };

  const handleCampEditToggle = () => {
    const newVal = !campEditIsAtCamp;
    setCampEditIsAtCamp(newVal);
    if (!newVal) {
      setCampEditAddress('');
      setCampEditLatitude(null);
      setCampEditLongitude(null);
      setCampEditMaxDistanceKm('');
    }
  };

  const handleCampMapConfirm = (result) => {
    setCampMapPickerOpen(false);
    setCampEditLatitude(result.latitude);
    setCampEditLongitude(result.longitude);
    setCampEditAddress(result.searchTerm || result.displayName);
  };

  const handleCampEditSave = async () => {
    const scheduleId = campEditModal.schedule?.termId;
    if (!scheduleId) return;

    if (campEditIsAtCamp && (campEditLatitude == null || campEditLongitude == null)) {
      toast.error('Zaznacz lokalizację obozu na mapie');
      return;
    }

    setIsSavingCamp(true);
    try {
      await TermControllerService.updateCampInfo(scheduleId, {
        isAtCamp: campEditIsAtCamp,
        campLatitude: campEditIsAtCamp ? campEditLatitude : undefined,
        campLongitude: campEditIsAtCamp ? campEditLongitude : undefined,
        campAddress: campEditIsAtCamp ? campEditAddress : undefined,
        campMaxDistanceKm: campEditIsAtCamp && campEditMaxDistanceKm ? parseInt(campEditMaxDistanceKm) : undefined
      });
      toast.success('Informacje o obozie zostały zapisane');
      handleCampEditClose();
      fetchPreseasonSummary();
    } catch (error) {
      toast.error('Nie udało się zapisać informacji o obozie');
    } finally {
      setIsSavingCamp(false);
    }
  };

  const handleEditPeriodCancel = () => {
    setEditPeriodModal({ isOpen: false });
    setEditPeriodError(null);
  };

  const isEditPeriodFormValid = () => {
    if (!editPeriodData.startDate || !editPeriodData.endDate) return false;
    const start = new Date(editPeriodData.startDate);
    const end = new Date(editPeriodData.endDate);
    return start < end;
  };

  // Obsługa checkboxa województwa w edycji okresu
  const handleEditPeriodVoivodeshipToggle = (voivodeshipKey) => {
    setEditPeriodData(prev => {
      const current = prev.selectedVoivodeships || [];
      const newVoivodeships = current.includes(voivodeshipKey)
        ? current.filter(v => v !== voivodeshipKey)
        : [...current, voivodeshipKey];

      // Przy usunięciu województwa, usuń powiaty z tego województwa
      let newCounties = prev.selectedCounties || [];
      if (!newVoivodeships.includes(voivodeshipKey)) {
        const territory = findOpponentDictionaries.territories.find(t => t.key === voivodeshipKey);
        const countyKeys = (territory?.subValues || []).map(c => c.key);
        newCounties = newCounties.filter(c => !countyKeys.includes(c));
      }

      return { ...prev, selectedVoivodeships: newVoivodeships, selectedCounties: newCounties };
    });
  };

  // Obsługa checkboxa powiatu w edycji okresu
  const handleEditPeriodCountyToggle = (countyKey) => {
    setEditPeriodData(prev => {
      const current = prev.selectedCounties || [];
      const newCounties = current.includes(countyKey)
        ? current.filter(c => c !== countyKey)
        : [...current, countyKey];
      return { ...prev, selectedCounties: newCounties };
    });
  };

  // Pobierz powiaty dla wybranych województw w edycji okresu
  const getEditPeriodFilteredCounties = () => {
    const selectedVoivodeships = editPeriodData.selectedVoivodeships || [];
    if (selectedVoivodeships.length === 0) return [];
    return findOpponentDictionaries.territories
      .filter(t => selectedVoivodeships.includes(t.key))
      .flatMap(t => (t.subValues || []).map(c => ({ ...c, voivodeship: t.key, voivodeshipName: t.displayValue })));
  };

  // Funkcje dla modalu udostępniania linku zaproszenia (PreseasonGameInvitation)
  const refreshShareList = async () => {
    setShareListLoading(true);
    try {
      const list = await PreseasonGameInvitationControllerService.listOwn();
      const filtered = activeTeamId ? list.filter(inv => inv.teamId === activeTeamId) : list;
      setShareList(filtered);
    } catch (err) {
      console.error('Nie udało się pobrać listy linków', err);
    } finally {
      setShareListLoading(false);
    }
  };

  const handleOpenShareInvitationModal = async () => {
    setShareSelectedTermIds([]);
    setShareGenerated(null);
    setShareError('');
    setShareInvitationModal({ isOpen: true });
    refreshShareList();
  };

  const handleCloseShareInvitationModal = () => {
    setShareInvitationModal({ isOpen: false });
    setShareGenerated(null);
    setShareSelectedTermIds([]);
    setShareError('');
  };

  const handleToggleShareTerm = (termId) => {
    setShareSelectedTermIds(prev => prev.includes(termId)
      ? prev.filter(id => id !== termId)
      : [...prev, termId]);
  };

  const handleGenerateShareInvitation = async () => {
    if (!activeTeamId || shareSelectedTermIds.length === 0) {
      setShareError('Wybierz przynajmniej jeden termin');
      return;
    }
    setShareError('');
    setShareIsGenerating(true);
    try {
      const result = await PreseasonGameInvitationControllerService.createInvitation({
        teamId: activeTeamId,
        termIds: shareSelectedTermIds
      });
      setShareGenerated(result);
      setShareSelectedTermIds([]);
      refreshShareList();
    } catch (err) {
      console.error('Błąd generowania linku', err);
      setShareError(err?.body?.message || 'Nie udało się wygenerować linku. Spróbuj ponownie.');
    } finally {
      setShareIsGenerating(false);
    }
  };

  const handleDeactivateShareInvitation = async (token) => {
    if (!window.confirm('Dezaktywować ten link? Nowe drużyny nie będą mogły odpowiedzieć, ale istniejące propozycje zostaną zachowane.')) {
      return;
    }
    try {
      await PreseasonGameInvitationControllerService.deactivate(token);
      refreshShareList();
    } catch (err) {
      console.error('Błąd dezaktywacji', err);
      toast.error('Nie udało się dezaktywować linku');
    }
  };

  const handleDeleteShareInvitation = (invitation) => {
    setShareDeleteConfirm({ isOpen: true, invitation });
  };

  const handleCancelDeleteShareInvitation = () => {
    if (isDeletingShare) return;
    setShareDeleteConfirm({ isOpen: false, invitation: null });
  };

  const handleConfirmDeleteShareInvitation = async () => {
    const invitation = shareDeleteConfirm.invitation;
    if (!invitation) return;
    setIsDeletingShare(true);
    try {
      await PreseasonGameInvitationControllerService.deleteInvitation(invitation.token);
      setShareDeleteConfirm({ isOpen: false, invitation: null });
      refreshShareList();
      toast.success('Link został usunięty');
    } catch (err) {
      console.error('Błąd usuwania', err);
      toast.error('Nie udało się usunąć linku');
    } finally {
      setIsDeletingShare(false);
    }
  };

  const handleCopyShareLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link skopiowany do schowka');
    } catch (err) {
      console.error('Nie udało się skopiować', err);
      toast.error('Nie udało się skopiować — skopiuj ręcznie');
    }
  };

  const buildShareTextSingle = (term, url) => {
    const datePart = `${formatDate(term.startDate)} - ${formatDate(term.endDate)}`;
    return `Drużyna z Twojej okolicy szuka przeciwnika na termin ${datePart}. Zarejestruj się aby umówić sparing: ${url}`;
  };

  const buildShareTextBulk = (terms, url) => {
    const list = terms.map(t => `${formatDate(t.startDate)} - ${formatDate(t.endDate)}`).join(', ');
    return `Drużyna z Twojej okolicy szuka przeciwników na poniższe terminy: ${list}. Zarejestruj się aby umówić sparing: ${url}`;
  };

  // Funkcje dla modalu dodawania terminu
  const handleAddTermClick = async () => {
    const defaultOpponentType = teamType === 'YOUTH'
      ? { senior: false, junior: true }
      : { senior: true, junior: false };
    setAddTermData({ startDate: '', endDate: '', seniorTiers: [], youthTiers: [], yearOfBirths: [], opponentType: defaultOpponentType });
    setAddTermError(null);
    setAddTermSeasonDef(null);
    setAddTermModal({ isOpen: true });
    try {
      const seasonDef = await SeasonDefinitionControllerService.getCurrentOrNearest();
      setAddTermSeasonDef(seasonDef || null);
    } catch (error) {
      console.error('Nie udało się pobrać definicji sezonu', error);
    }
  };

  const handleAddTermCancel = () => {
    setAddTermModal({ isOpen: false });
    setAddTermData({ startDate: '', endDate: '', seniorTiers: [], youthTiers: [], yearOfBirths: [], opponentType: { senior: true, junior: false } });
    setAddTermError(null);
    setAddTermSeasonDef(null);
  };

  const handleAddTermChange = (field, value) => {
    setAddTermData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTermOpponentTypeChange = (type, checked) => {
    setAddTermData(prev => {
      const newOpponentType = { ...prev.opponentType, [type]: checked };
      const updates = { opponentType: newOpponentType };
      // Wyczyść odpowiednie dane po odznaczeniu
      if (!checked && type === 'senior') updates.seniorTiers = [];
      if (!checked && type === 'junior') { updates.youthTiers = []; updates.yearOfBirths = []; }
      return { ...prev, ...updates };
    });
  };

  const handleTierCheckboxChange = (tierKey, checked) => {
    setAddTermData(prev => {
      const currentTiers = prev.seniorTiers || [];
      const newTiers = checked
        ? [...currentTiers, tierKey]
        : currentTiers.filter(t => t !== tierKey);
      return { ...prev, seniorTiers: newTiers };
    });
  };

  const handleAddTermYouthTierChange = (tierKey, checked) => {
    setAddTermData(prev => {
      const currentTiers = prev.youthTiers || [];
      const newTiers = checked
        ? [...currentTiers, tierKey]
        : currentTiers.filter(t => t !== tierKey);
      return { ...prev, youthTiers: newTiers };
    });
  };

  const handleAddTermYearOfBirthChange = (yearKey, checked) => {
    setAddTermData(prev => {
      const currentYears = prev.yearOfBirths || [];
      const newYears = checked
        ? [...currentYears, yearKey]
        : currentYears.filter(y => y !== yearKey);
      return { ...prev, yearOfBirths: newYears };
    });
  };

  const handleAddTermConfirm = async () => {
    try {
      setIsSavingTerm(true);
      setAddTermError(null);

      const allTiers = [
        ...(addTermData.seniorTiers || []),
        ...(addTermData.youthTiers || [])
      ];

      // YEAR_ROUND teams without existing schedules - use year-round-term endpoint
      if (isYearRound && !summaryData?.seasonDefinitionId) {
        await PreseasonTimeControllerService.addYearRoundTerm({
          teamId: activeTeamId,
          startDate: addTermData.startDate,
          endDate: addTermData.endDate,
          amountOfGames: 1,
          tiers: allTiers.length > 0 ? allTiers : undefined,
          yearOfBirths: addTermData.yearOfBirths?.length > 0 ? addTermData.yearOfBirths : undefined
        });
      } else {
        if (!summaryData?.seasonDefinitionId) {
          toast.error('Nie można dodać terminu - brak wymaganych identyfikatorów');
          return;
        }

        await TermControllerService.createTerm({
          seasonDefinitionId: summaryData.seasonDefinitionId,
          teamId: activeTeamId,
          startDate: addTermData.startDate,
          endDate: addTermData.endDate,
          tiers: allTiers.length > 0 ? allTiers : undefined,
          yearOfBirths: addTermData.yearOfBirths?.length > 0 ? addTermData.yearOfBirths : undefined
        });
      }

      toast.success('Termin został dodany');
      setAddTermModal({ isOpen: false });
      setAddTermError(null);
      fetchPreseasonSummary();
    } catch (error) {
      if (error.status === 409) {
        toast.error(error?.body?.message || 'Podany zakres dat nakłada się z istniejącym harmonogramem.');
      } else {
        toast.error('Nie udało się dodać terminu');
      }
    } finally {
      setIsSavingTerm(false);
    }
  };

  const isAddTermFormValid = () => {
    if (!addTermData.startDate || !addTermData.endDate) return false;
    // Musi być zaznaczony przynajmniej jeden typ przeciwnika
    if (!addTermData.opponentType?.senior && !addTermData.opponentType?.junior) return false;
    // Jeśli seniorzy - musi być wybrany przynajmniej jeden tier
    if (addTermData.opponentType?.senior && (!addTermData.seniorTiers || addTermData.seniorTiers.length === 0)) return false;
    const start = new Date(addTermData.startDate);
    const end = new Date(addTermData.endDate);
    if (start > end) return false;
    // YEAR_ROUND teams don't have preseason date constraints
    if (!isYearRound && addTermSeasonDef) {
      if (addTermSeasonDef.minStartDate) {
        const preseasonStart = new Date(addTermSeasonDef.minStartDate);
        if (start < preseasonStart) return false;
      }
      if (addTermSeasonDef.maxEndDate) {
        const preseasonEnd = new Date(addTermSeasonDef.maxEndDate);
        if (end > preseasonEnd) return false;
      }
    }
    return true;
  };

  const getAddTermValidationErrors = () => {
    const errors = [];
    if (!addTermData.startDate) errors.push('Data od jest wymagana');
    if (!addTermData.endDate) errors.push('Data do jest wymagana');
    if (addTermData.startDate && addTermData.endDate) {
      const start = new Date(addTermData.startDate);
      const end = new Date(addTermData.endDate);
      if (end < start) {
        errors.push('Data do nie może być wcześniejsza niż data od');
      }
    }
    if (!isYearRound && addTermSeasonDef) {
      if (addTermData.startDate && addTermSeasonDef.minStartDate) {
        const start = new Date(addTermData.startDate);
        const preseasonStart = new Date(addTermSeasonDef.minStartDate);
        if (start < preseasonStart) {
          errors.push(`Data od nie może być wcześniejsza niż początek okresu przygotowawczego (${formatDate(addTermSeasonDef.minStartDate)})`);
        }
      }
      if (addTermData.endDate && addTermSeasonDef.maxEndDate) {
        const end = new Date(addTermData.endDate);
        const preseasonEnd = new Date(addTermSeasonDef.maxEndDate);
        if (end > preseasonEnd) {
          errors.push(`Data do nie może być późniejsza niż koniec okresu przygotowawczego (${formatDate(addTermSeasonDef.maxEndDate)})`);
        }
      }
      if (addTermData.startDate && addTermSeasonDef.maxEndDate) {
        const start = new Date(addTermData.startDate);
        const preseasonEnd = new Date(addTermSeasonDef.maxEndDate);
        if (start > preseasonEnd) {
          errors.push(`Data od nie może być późniejsza niż koniec okresu przygotowawczego (${formatDate(addTermSeasonDef.maxEndDate)})`);
        }
      }
      if (addTermData.endDate && addTermSeasonDef.minStartDate) {
        const end = new Date(addTermData.endDate);
        const preseasonStart = new Date(addTermSeasonDef.minStartDate);
        if (end < preseasonStart) {
          errors.push(`Data do nie może być wcześniejsza niż początek okresu przygotowawczego (${formatDate(addTermSeasonDef.minStartDate)})`);
        }
      }
    }
    if ((addTermData.startDate || addTermData.endDate) && !addTermData.opponentType?.senior && !addTermData.opponentType?.junior) {
      errors.push('Wybierz typ przeciwnika');
    }
    if (addTermData.opponentType?.senior && (!addTermData.seniorTiers || addTermData.seniorTiers.length === 0)) {
      errors.push('Wybierz co najmniej jeden poziom seniorski');
    }
    return errors;
  };

  // === Funkcje dla modalu "Dodaj wiele terminów" ===
  const handleBulkTermClick = async () => {
    const defaultOpponentType = teamType === 'YOUTH'
      ? { senior: false, junior: true }
      : { senior: true, junior: false };
    setBulkTermData({
      startDate: '',
      endDate: ''
    });
    setBulkTermSchedules([]);
    setBulkTermOpponentTypes({});
    setBulkTermError(null);
    setBulkTermValidationErrors([]);
    setBulkTermSeasonDef(null);
    setBulkTermModal({ isOpen: true });
    try {
      const seasonDef = await SeasonDefinitionControllerService.getCurrentOrNearest();
      setBulkTermSeasonDef(seasonDef || null);
    } catch (error) {
      console.error('Nie udało się pobrać definicji sezonu', error);
    }
  };

  const handleBulkTermCancel = () => {
    setBulkTermModal({ isOpen: false });
    setBulkTermSchedules([]);
    setBulkTermOpponentTypes({});
    setBulkTermError(null);
    setBulkTermValidationErrors([]);
  };

  const handleBulkTermFillWeekends = () => {
    if (!bulkTermData.startDate || !bulkTermData.endDate) {
      toast.error('Najpierw ustaw datę rozpoczęcia i zakończenia.');
      return;
    }
    const start = new Date(bulkTermData.startDate);
    const end = new Date(bulkTermData.endDate);
    if (start >= end) {
      toast.error('Data rozpoczęcia musi być wcześniejsza niż data zakończenia.');
      return;
    }
    const weekends = [];
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek === 5) {
        const friday = new Date(current);
        const sunday = new Date(current);
        sunday.setDate(sunday.getDate() + 2);
        const weekendEnd = sunday <= end ? sunday : new Date(end);
        const fmt = (d) => d.toISOString().split('T')[0];
        weekends.push({
          startDate: fmt(friday),
          endDate: fmt(weekendEnd),
          gamesCount: 1,
          opponentTiers: [],
          opponentYouthTiers: [],
          opponentYearOfBirths: []
        });
        current.setDate(current.getDate() + (7 - dayOfWeek));
        continue;
      }
      current.setDate(current.getDate() + 1);
    }
    if (weekends.length === 0) {
      toast.error('Brak weekendów w wybranym zakresie dat.');
      return;
    }
    setBulkTermSchedules(weekends);
    setBulkTermValidationErrors([]);
  };

  const handleBulkTermAddSchedule = () => {
    setBulkTermSchedules(prev => [...prev, {
      startDate: '',
      endDate: '',
      gamesCount: 1,
      opponentTiers: [],
      opponentYouthTiers: [],
      opponentYearOfBirths: []
    }]);
  };

  const handleBulkTermRemoveSchedule = (index) => {
    setBulkTermSchedules(prev => prev.filter((_, i) => i !== index));
    setBulkTermOpponentTypes(prev => {
      const remapped = { ...prev };
      delete remapped[index];
      const newMap = {};
      Object.keys(remapped).forEach(key => {
        const k = parseInt(key);
        if (k > index) newMap[k - 1] = remapped[key];
        else newMap[k] = remapped[key];
      });
      return newMap;
    });
  };

  const handleBulkTermScheduleChange = (index, field, value) => {
    let sanitizedValue = value;
    if (field === 'gamesCount') {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1) sanitizedValue = 1;
      else if (num > 2) sanitizedValue = 2;
      else sanitizedValue = num;
    }
    setBulkTermSchedules(prev => prev.map((s, i) =>
      i === index ? { ...s, [field]: sanitizedValue } : s
    ));
  };

  const handleBulkTermOpponentTypeChange = (index, type, checked) => {
    setBulkTermOpponentTypes(prev => {
      const current = prev[index] || (teamType === 'YOUTH' ? { senior: false, junior: true } : { senior: true, junior: false });
      const updated = { ...current, [type]: checked };
      return { ...prev, [index]: updated };
    });
    if (!checked && type === 'senior') {
      setBulkTermSchedules(prev => prev.map((s, i) =>
        i === index ? { ...s, opponentTiers: [] } : s
      ));
    }
    if (!checked && type === 'junior') {
      setBulkTermSchedules(prev => prev.map((s, i) =>
        i === index ? { ...s, opponentYouthTiers: [], opponentYearOfBirths: [] } : s
      ));
    }
  };

  const handleBulkTermTierChange = (index, tierKey, checked) => {
    setBulkTermSchedules(prev => prev.map((s, i) => {
      if (i !== index) return s;
      const tiers = checked
        ? [...(s.opponentTiers || []), tierKey]
        : (s.opponentTiers || []).filter(t => t !== tierKey);
      return { ...s, opponentTiers: tiers };
    }));
  };

  const getBulkTermValidationErrors = () => {
    const errors = [];
    if (bulkTermSchedules.length === 0) {
      errors.push('Dodaj przynajmniej jeden termin');
      return errors;
    }
    bulkTermSchedules.forEach((schedule, i) => {
      const label = `Termin ${i + 1}`;
      if (!schedule.startDate || !schedule.endDate) {
        errors.push(`${label}: Uzupełnij daty`);
      } else {
        const s = new Date(schedule.startDate);
        const e = new Date(schedule.endDate);
        if (s > e) errors.push(`${label}: Data od nie może być późniejsza niż data do`);
        if (!isYearRound && bulkTermSeasonDef) {
          const minDate = bulkTermSeasonDef.minStartDate;
          const maxDate = bulkTermSeasonDef.maxEndDate;
          if (minDate && schedule.startDate < minDate) {
            errors.push(`${label}: Data od nie może być wcześniejsza niż ${minDate}`);
          }
          if (maxDate && schedule.endDate > maxDate) {
            errors.push(`${label}: Data do nie może być późniejsza niż ${maxDate}`);
          }
          if (minDate && schedule.endDate < minDate) {
            errors.push(`${label}: Data do nie może być wcześniejsza niż ${minDate}`);
          }
          if (maxDate && schedule.startDate > maxDate) {
            errors.push(`${label}: Data od nie może być późniejsza niż ${maxDate}`);
          }
        }
      }
      const opType = bulkTermOpponentTypes[i] || (teamType === 'YOUTH' ? { senior: false, junior: true } : { senior: true, junior: false });
      if (!opType.senior && !opType.junior) {
        errors.push(`${label}: Wybierz typ przeciwnika`);
      }
      if (opType.senior && (!schedule.opponentTiers || schedule.opponentTiers.length === 0)) {
        errors.push(`${label}: Wybierz co najmniej jeden poziom seniorski`);
      }
    });
    return errors;
  };

  const handleBulkTermConfirm = async () => {
    const errors = getBulkTermValidationErrors();
    if (errors.length > 0) {
      setBulkTermValidationErrors(errors);
      return;
    }
    setIsSavingBulkTerm(true);
    setBulkTermError(null);
    setBulkTermValidationErrors([]);
    try {
      if (!summaryData?.seasonDefinitionId) {
        // Brak schedules — pobierz aktualny sezon i stwórz schedules
        const seasonDef = await SeasonDefinitionControllerService.getCurrentOrNearest();
        if (!seasonDef?.id) {
          toast.error('Nie znaleziono aktywnej definicji sezonu');
          return;
        }
        const schedules = bulkTermSchedules.map(schedule => {
          const allTiers = [...(schedule.opponentTiers || []), ...(schedule.opponentYouthTiers || [])];
          return {
            startDate: schedule.startDate,
            endDate: schedule.endDate,
            amountOfGames: schedule.gamesCount ? parseInt(schedule.gamesCount) : 1,
            tiers: allTiers.length > 0 ? allTiers : undefined,
            yearOfBirths: schedule.opponentYearOfBirths?.length > 0 ? schedule.opponentYearOfBirths : undefined,
            isAtCamp: false
          };
        });
        await PreseasonTimeControllerService.addTeamSchedules({
          seasonDefinitionId: seasonDef.id,
          teamId: activeTeamId,
          schedules: schedules
        });
      } else {
        // Istniejące schedules — batch create
        const dtos = bulkTermSchedules.map(schedule => {
          const allTiers = [...(schedule.opponentTiers || []), ...(schedule.opponentYouthTiers || [])];
          return {
            seasonDefinitionId: summaryData.seasonDefinitionId,
            teamId: activeTeamId,
            startDate: schedule.startDate,
            endDate: schedule.endDate,
            tiers: allTiers.length > 0 ? allTiers : undefined,
            yearOfBirths: schedule.opponentYearOfBirths?.length > 0 ? schedule.opponentYearOfBirths : undefined
          };
        });
        await TermControllerService.createBatch(dtos);
      }

      toast.success(`Dodano ${bulkTermSchedules.length} terminów`);
      setBulkTermModal({ isOpen: false });
      fetchPreseasonSummary();
    } catch (error) {
      if (error.status === 409) {
        setBulkTermError(error?.body?.message || 'Podany zakres dat nakłada się z istniejącym harmonogramem.');
      } else {
        setBulkTermError('Nie udało się dodać terminów');
      }
    } finally {
      setIsSavingBulkTerm(false);
    }
  };

  const handleBulkTermInsertSchedule = (afterIndex) => {
    const prev = bulkTermSchedules[afterIndex];
    const next = bulkTermSchedules[afterIndex + 1];
    const isWeekend = (s) => {
      if (!s?.startDate || !s?.endDate) return false;
      return [5, 6, 0].includes(new Date(s.startDate).getDay());
    };
    const newSchedule = {
      startDate: '',
      endDate: '',
      gamesCount: 1,
      opponentTiers: [],
      opponentYouthTiers: [],
      opponentYearOfBirths: []
    };
    if (isWeekend(prev) && isWeekend(next)) {
      const prevEnd = new Date(prev.endDate);
      const nextStart = new Date(next.startDate);
      const candidate = new Date(prevEnd);
      candidate.setDate(candidate.getDate() + 1);
      while (candidate < nextStart) {
        if (candidate.getDay() === 3) {
          const fmt = (d) => d.toISOString().split('T')[0];
          newSchedule.startDate = fmt(candidate);
          newSchedule.endDate = fmt(candidate);
          break;
        }
        candidate.setDate(candidate.getDate() + 1);
      }
    }
    setBulkTermSchedules(prevSchedules => [
      ...prevSchedules.slice(0, afterIndex + 1),
      newSchedule,
      ...prevSchedules.slice(afterIndex + 1)
    ]);
  };

  // Funkcje dla modalu edycji terminu (zakres dat)
  const handleEditTermClick = async (schedule) => {
    setEditTermData({
      startDate: schedule.approximateStartDate || '',
      endDate: schedule.approximateEndDate || ''
    });
    setEditTermError(null);
    setEditTermSeasonDef(null);
    setEditTermModal({ isOpen: true, schedule });
    try {
      const seasonDef = await SeasonDefinitionControllerService.getCurrentOrNearest();
      setEditTermSeasonDef(seasonDef || null);
    } catch (error) {
      console.error('Nie udało się pobrać definicji sezonu', error);
    }
  };

  const handleEditTermCancel = () => {
    setEditTermModal({ isOpen: false, schedule: null });
    setEditTermData({ startDate: '', endDate: '' });
    setEditTermError(null);
  };

  const handleEditTermConfirm = async () => {
    const schedule = editTermModal.schedule;
    if (!schedule?.termId) {
      toast.error('Nie można edytować terminu - brak identyfikatora');
      return;
    }
    try {
      setIsSavingEditTerm(true);
      setEditTermError(null);
      await TermControllerService.updateDateRange(
        schedule.termId,
        { startDate: editTermData.startDate, endDate: editTermData.endDate }
      );
      toast.success('Termin został zaktualizowany');
      setEditTermModal({ isOpen: false, schedule: null });
      setEditTermError(null);
      fetchPreseasonSummary();
    } catch (error) {
      toast.error(error?.body?.message || 'Nie udało się zaktualizować terminu');
    } finally {
      setIsSavingEditTerm(false);
    }
  };

  const isEditTermFormValid = () => {
    if (!editTermData.startDate || !editTermData.endDate) return false;
    const start = new Date(editTermData.startDate);
    const end = new Date(editTermData.endDate);
    if (start > end) return false;
    if (!isYearRound) {
      const minDate = editTermSeasonDef?.minStartDate || summaryData?.preseasonStartDate;
      const maxDate = editTermSeasonDef?.maxEndDate || summaryData?.preseasonEndDate;
      if (minDate && editTermData.startDate < minDate) return false;
      if (maxDate && editTermData.endDate > maxDate) return false;
    }
    return true;
  };

  // Funkcje dla modalu edycji wymaganego przeciwnika
  const handleEditOpponentClick = (game) => {
    // Wyodrębnij dane o przeciwnikach z game
    const seniorTiersFromGame = game.tiers?.filter(t => t.teamType === 'SENIOR').map(t => t.key || t.value) || [];
    const youthTiersFromGame = game.tiers?.filter(t => t.teamType === 'YOUTH').map(t => t.key || t.value) || [];
    const yearOfBirthsFromGame = game.yearOfBirths || [];

    const hasSenior = seniorTiersFromGame.length > 0;
    const hasJunior = youthTiersFromGame.length > 0 || yearOfBirthsFromGame.length > 0;

    // Domyślne ustawienia w zależności od typu drużyny
    let defaultOpponentType = { senior: false, junior: false };
    if (teamType === 'YOUTH') {
      defaultOpponentType = { senior: false, junior: true };
    } else if (teamType === 'SENIOR') {
      defaultOpponentType = { senior: true, junior: false };
    }

    // Jeśli są dane z gry, użyj ich
    if (hasSenior || hasJunior) {
      defaultOpponentType = { senior: hasSenior, junior: hasJunior };
    }

    setEditOpponentData({
      opponentType: defaultOpponentType,
      opponentTiers: seniorTiersFromGame,
      opponentYouthTiers: youthTiersFromGame,
      opponentYearOfBirths: yearOfBirthsFromGame
    });
    setEditOpponentError(null);
    setEditOpponentModal({ isOpen: true, game });
  };

  const handleEditOpponentCancel = () => {
    setEditOpponentModal({ isOpen: false, game: null });
    setEditOpponentData({
      opponentType: { senior: false, junior: false },
      opponentTiers: [],
      opponentYouthTiers: [],
      opponentYearOfBirths: []
    });
    setEditOpponentError(null);
  };

  const handleOpponentTypeChange = (type, checked) => {
    setEditOpponentData(prev => ({
      ...prev,
      opponentType: {
        ...prev.opponentType,
        [type]: checked
      }
    }));
  };

  const handleOpponentTierCheckboxChange = (tierKey, checked) => {
    setEditOpponentData(prev => {
      const currentTiers = prev.opponentTiers || [];
      const newTiers = checked
        ? [...currentTiers, tierKey]
        : currentTiers.filter(t => t !== tierKey);
      return { ...prev, opponentTiers: newTiers };
    });
  };

  const handleOpponentYouthTierCheckboxChange = (tierKey, checked) => {
    setEditOpponentData(prev => {
      const currentTiers = prev.opponentYouthTiers || [];
      const newTiers = checked
        ? [...currentTiers, tierKey]
        : currentTiers.filter(t => t !== tierKey);
      return { ...prev, opponentYouthTiers: newTiers };
    });
  };

  const handleOpponentYearOfBirthCheckboxChange = (yearKey, checked) => {
    setEditOpponentData(prev => {
      const currentYears = prev.opponentYearOfBirths || [];
      const newYears = checked
        ? [...currentYears, yearKey]
        : currentYears.filter(y => y !== yearKey);
      return { ...prev, opponentYearOfBirths: newYears };
    });
  };

  const handleEditOpponentConfirm = async () => {
    const game = editOpponentModal.game;
    if (!game || !game.termId) {
      toast.error('Nie można zaktualizować - brak identyfikatora');
      return;
    }

    try {
      setIsSavingOpponent(true);
      setEditOpponentError(null);

      // Połącz wszystkie wybrane tiers (seniorskie i juniorskie) do jednej tablicy
      const allTiers = [
        ...(editOpponentData.opponentTiers || []),
        ...(editOpponentData.opponentYouthTiers || [])
      ];

      const requestBody = {
        tiers: allTiers.length > 0 ? allTiers : undefined,
        yearsOfBirth: editOpponentData.opponentYearOfBirths?.length > 0 ? editOpponentData.opponentYearOfBirths : undefined
      };

      await TermControllerService.updateExpectedOpponents(
        game.termId,
        requestBody
      );

      toast.success('Wymagany przeciwnik został zaktualizowany');
      setEditOpponentModal({ isOpen: false, game: null });
      preserveDateRangeIndexRef.current = true; // Zachowaj pozycję nawigatora
      fetchPreseasonSummary();
    } catch (error) {
      if (error.status === 409) {
        setEditOpponentError('Konflikt danych - nie można zapisać zmian. Spróbuj ponownie.');
      } else {
        toast.error('Nie udało się zaktualizować wymaganego przeciwnika');
      }
    } finally {
      setIsSavingOpponent(false);
    }
  };

  const isEditOpponentFormValid = () => {
    // Musi być zaznaczony przynajmniej jeden typ przeciwnika
    if (!editOpponentData.opponentType.senior && !editOpponentData.opponentType.junior) {
      return false;
    }

    // Jeśli zaznaczono seniorów, musi być wybrany przynajmniej jeden tier seniorski
    if (editOpponentData.opponentType.senior && editOpponentData.opponentTiers.length === 0) {
      return false;
    }

    // Jeśli zaznaczono juniorów, musi być wybrany przynajmniej jeden tier juniorski lub rocznik
    if (editOpponentData.opponentType.junior) {
      if (editOpponentData.opponentYouthTiers.length === 0 && editOpponentData.opponentYearOfBirths.length === 0) {
        return false;
      }
    }

    return true;
  };

  // Funkcje dla modalu wiadomości
  const handleOpenMessagesModal = async (agreedGame) => {
    if (!agreedGame || !agreedGame.preseasonGameId) {
      toast.error('Nie można otworzyć wiadomości - brak identyfikatora gry');
      return;
    }

    setMessagesModal({ isOpen: true, game: agreedGame });
    setMessagesLoading(true);
    setMessages([]);
    setGameDetails(null);

    try {
      const details = await PreseasonGameControllerService.getPreseasonGameById(agreedGame.preseasonGameId);
      setGameDetails(details);
      setMessages(details.messages || []);
    } catch (error) {
      toast.error('Nie udało się pobrać wiadomości');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleCloseMessagesModal = () => {
    setMessagesModal({ isOpen: false, game: null });
    setMessages([]);
    setReplyMessage('');
    setGameDetails(null);
  };

  const handleSendMessage = async () => {
    if (!gameDetails || !replyMessage.trim()) return;

    try {
      setSendingReply(true);

      // Określ senderId i receiverId
      // W tym widoku jesteśmy właścicielem drużyny (activeTeamId), więc wysyłamy wiadomość do przeciwnika
      const isCreator = gameDetails.teamCreatorId === activeTeamId;
      const senderId = isCreator ? gameDetails.teamCreatorId : gameDetails.teamOpponentId;
      const receiverId = isCreator ? gameDetails.teamOpponentId : gameDetails.teamCreatorId;

      await PreseasonGameControllerService.addMessage({
        preseasonGameId: gameDetails.id,
        senderId: senderId,
        receiverId: receiverId,
        senderUserId: userId,
        message: replyMessage.trim()
      });

      toast.success('Wiadomość została wysłana');
      setReplyMessage('');

      // Odśwież wiadomości
      const updatedDetails = await PreseasonGameControllerService.getPreseasonGameById(gameDetails.id);
      setGameDetails(updatedDetails);
      setMessages(updatedDetails.messages || []);
    } catch (error) {
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSendingReply(false);
    }
  };

  // Funkcje dla modalu anulowania sparingu
  const handleOpenScoreModal = (agreedGame) => {
    setScoreModal({ isOpen: true, game: agreedGame });
    setScoreData({
      scoreMyTeam: agreedGame.scoreCreator != null ? String(agreedGame.scoreCreator) : '',
      scoreOpponentTeam: agreedGame.scoreOpponent != null ? String(agreedGame.scoreOpponent) : ''
    });
  };

  const handleSaveScore = async () => {
    if (scoreData.scoreMyTeam === '' || scoreData.scoreOpponentTeam === '') {
      toast.error('Podaj wynik dla obu drużyn');
      return;
    }
    setIsSavingScore(true);
    try {
      await PreseasonGameControllerService.updateScore(
        scoreModal.game.preseasonGameId,
        activeTeamId,
        { scoreMyTeam: parseInt(scoreData.scoreMyTeam), scoreOpponentTeam: parseInt(scoreData.scoreOpponentTeam) }
      );
      toast.success('Wynik został zapisany');
      setScoreModal({ isOpen: false, game: null });
      preserveDateRangeIndexRef.current = true;
      fetchPreseasonSummary();
    } catch (error) {
      toast.error(error?.body?.message || 'Nie udało się zapisać wyniku');
    } finally {
      setIsSavingScore(false);
    }
  };

  const handleCancelGameClick = (agreedGame) => {
    setCancelGameModal({ isOpen: true, game: agreedGame });
    setCancelReason('');
  };

  const handleCancelGameModalClose = () => {
    setCancelGameModal({ isOpen: false, game: null });
    setCancelReason('');
  };

  const handleCancelGameConfirm = async () => {
    const game = cancelGameModal.game;
    if (!game || !game.preseasonGameId || !cancelReason.trim()) return;

    try {
      setIsCancellingGame(true);
      await PreseasonGameControllerService.cancelPreseasonGame(game.preseasonGameId, {
        requesterTeamId: activeTeamId,
        reason: cancelReason.trim()
      });
      toast.success('Sparing został anulowany');
      setCancelGameModal({ isOpen: false, game: null });
      setCancelReason('');
      preserveDateRangeIndexRef.current = true; // Zachowaj pozycję nawigatora
      fetchPreseasonSummary();
    } catch (error) {
      toast.error('Nie udało się anulować sparingu');
    } finally {
      setIsCancellingGame(false);
    }
  };

  // Funkcja pomocnicza do generowania wszystkich dat między startDate a endDate
  const generateDatesBetween = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate <= end) {
      dates.push(formatLocalDate(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Funkcje dla modalu "Zaproponuj termin"
  const handleOpenProposeTimeModal = async (agreedGame, schedule) => {
    const dates = generateDatesBetween(schedule.approximateStartDate, schedule.approximateEndDate);
    setProposeTimeDates(dates);
    setProposeTimeSelectedDay(agreedGame.agreedDate || null);
    setProposeTimeSelectedTimeRange(null);
    setProposeTimeSelectedTimeSlot(null);
    setProposeTimeCalendarBlocks([]);
    setProposeTimeLocation(agreedGame.location || '');
    setProposeTimeModal({ isOpen: true, game: agreedGame, schedule });

    // Pobierz blokady kalendarza (pomiń dla ręcznych przeciwników — nie ma ich w systemie)
    if (agreedGame.isManualOpponent) {
      setProposeTimeCalendarBlocks([]);
      setProposeTimeIsLoadingBlocks(false);
    } else {
      setProposeTimeIsLoadingBlocks(true);
      try {
        const blocks = await CalendarBlockControllerService.getCompactCalendarBlocksBySchedule({
          startDate: schedule.approximateStartDate,
          endDate: schedule.approximateEndDate,
          creatorTeamId: activeTeamId,
          opponentTeamId: agreedGame.opponentTeamId,
          checkedPreseasonGameId: agreedGame.preseasonGameId
        });
        setProposeTimeCalendarBlocks(blocks || []);
      } catch (error) {
        setProposeTimeCalendarBlocks([]);
      } finally {
        setProposeTimeIsLoadingBlocks(false);
      }
    }
  };

  const handleCloseProposeTimeModal = () => {
    setProposeTimeModal({ isOpen: false, game: null, schedule: null });
    setProposeTimeDates([]);
    setProposeTimeSelectedDay(null);
    setProposeTimeSelectedTimeRange(null);
    setProposeTimeSelectedTimeSlot(null);
    setProposeTimeCalendarBlocks([]);
    setProposeTimeLocation('');
    setProposeTimeMessage('');
  };

  const handleProposeTimeRangeSelect = (range) => {
    setProposeTimeSelectedTimeRange(range);
    setProposeTimeSelectedTimeSlot(null);
  };

  const handleProposeTimeSlotSelect = (slot) => {
    setProposeTimeSelectedTimeSlot(slot);
  };

  const handleProposeTimeSubmit = async () => {
    if (!proposeTimeSelectedDay || !proposeTimeSelectedTimeSlot) return;

    const startHour = String(proposeTimeSelectedTimeSlot.hour).padStart(2, '0');
    const startMinute = String(proposeTimeSelectedTimeSlot.minute).padStart(2, '0');
    const endHour = String((proposeTimeSelectedTimeSlot.hour + 2) % 24).padStart(2, '0');
    const endMinute = String(proposeTimeSelectedTimeSlot.minute).padStart(2, '0');

    setIsProposingTime(true);
    try {
      if (proposeTimeModal.game?.isManualOpponent) {
        // Bezpośredni update dla meczów z ręcznym przeciwnikiem
        await PreseasonGameControllerService.updateManualPreseasonGame(
          proposeTimeModal.game.preseasonGameId,
          {
            date: proposeTimeSelectedDay,
            startTime: `${startHour}:${startMinute}`,
            endTime: `${endHour}:${endMinute}`,
            location: proposeTimeLocation || null
          }
        );
        toast.success('Zaktualizowano termin meczu');
      } else {
        await PreseasonGameDateProposalControllerService.addPreseasonGameDateProposal({
          preseasonGameId: proposeTimeModal.game.preseasonGameId,
          proposerTeamId: activeTeamId,
          date: proposeTimeSelectedDay,
          startTime: `${startHour}:${startMinute}`,
          endTime: `${endHour}:${endMinute}`,
          location: proposeTimeLocation || null,
          message: proposeTimeMessage.trim() || null
        });
        const isEdit = proposeTimeModal.game?.status === 'APPROVED';
        const opponentName = proposeTimeModal.game?.opponentTeamName;
        toast.success(isEdit
          ? `Wysłano propozycję zmiany terminu${opponentName ? ` do drużyny ${opponentName}` : ''}`
          : `Wysłano propozycję terminu${opponentName ? ` do drużyny ${opponentName}` : ''}`);
      }

      handleCloseProposeTimeModal();
      preserveDateRangeIndexRef.current = true;
      fetchPreseasonSummary();
    } catch (error) {
      toast.error(proposeTimeModal.game?.isManualOpponent
        ? 'Nie udało się zaktualizować terminu meczu'
        : 'Nie udało się wysłać propozycji terminu');
    } finally {
      setIsProposingTime(false);
    }
  };

  // Funkcje dla modalu blokowania godzin
  const handleBlockHoursClick = async (schedule) => {
    // Generuj daty dla danego schedule
    const dates = generateDatesBetween(schedule.approximateStartDate, schedule.approximateEndDate);
    // Inicjalizuj pusty obiekt z datami
    const initialData = {};
    dates.forEach(date => {
      initialData[date] = [];
    });

    // Pobierz istniejące ręczne blokady
    try {
      const existingBlocks = await CalendarBlockControllerService.getManualCalendarBlocks(
        activeTeamId,
        schedule.approximateStartDate,
        schedule.approximateEndDate
      );
      existingBlocks.forEach(block => {
        if (initialData[block.date] !== undefined) {
          initialData[block.date].push({
            id: block.id,
            startTime: block.startTime.substring(0, 5),
            endTime: block.endTime.substring(0, 5)
          });
        }
      });
    } catch (error) {
      console.error('Nie udało się pobrać istniejących blokad:', error);
    }

    setBlockHoursData(initialData);
    setBlockHoursError(null);
    setBlockHoursModal({ isOpen: true, schedule });
  };

  const handleBlockHoursCancel = () => {
    setBlockHoursModal({ isOpen: false, schedule: null });
    setBlockHoursData({});
    setBlockHoursError(null);
  };

  const handleAddBlockHour = (date) => {
    setBlockHoursData(prev => ({
      ...prev,
      [date]: [...(prev[date] || []), { startTime: '08:00', endTime: '22:00' }]
    }));
  };

  const adjustTime = (timeStr, hoursChange, minutesChange) => {
    if (!timeStr) return '08:00';
    const [hours, minutes] = timeStr.split(':').map(Number);
    let newHours = hours + hoursChange;
    let newMinutes = minutes + minutesChange;

    // Obsługa przepełnienia minut
    if (newMinutes >= 60) {
      newMinutes -= 60;
      newHours += 1;
    } else if (newMinutes < 0) {
      newMinutes += 60;
      newHours -= 1;
    }

    // Ograniczenie godzin do 0-23
    if (newHours > 23) newHours = 0;
    if (newHours < 0) newHours = 23;

    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  const handleRemoveBlockHour = async (date, index) => {
    const block = blockHoursData[date][index];
    if (block.id) {
      try {
        await CalendarBlockControllerService.deleteCalendarContigousBlock(block.id);
        toast.success('Blokada została usunięta');
      } catch (error) {
        toast.error('Nie udało się usunąć blokady');
        return;
      }
    }
    setBlockHoursData(prev => ({
      ...prev,
      [date]: prev[date].filter((_, i) => i !== index)
    }));
  };

  const handleBlockHourChange = (date, index, field, value) => {
    setBlockHoursData(prev => {
      const updatedBlock = { ...prev[date][index], [field]: value };
      // Walidacja: jeśli obie godziny ustawione i startTime >= endTime, skoryguj
      if (updatedBlock.startTime && updatedBlock.endTime && updatedBlock.startTime >= updatedBlock.endTime) {
        if (field === 'startTime') {
          // Użytkownik zmienił startTime — ustaw endTime na startTime + 1h
          const [h, m] = value.split(':').map(Number);
          const endH = Math.min(h + 1, 23);
          updatedBlock.endTime = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        } else {
          // Użytkownik zmienił endTime — ustaw startTime na endTime - 1h
          const [h, m] = value.split(':').map(Number);
          const startH = Math.max(h - 1, 0);
          updatedBlock.startTime = `${String(startH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        }
      }
      return {
        ...prev,
        [date]: prev[date].map((block, i) =>
          i === index ? updatedBlock : block
        )
      };
    });
  };

  const handleBlockHoursConfirm = async () => {
    // Walidacja: sprawdź czy wszystkie blokady mają poprawne godziny
    let hasInvalidBlock = false;
    Object.entries(blockHoursData).forEach(([date, dayBlocks]) => {
      dayBlocks.forEach(block => {
        if (block.startTime && block.endTime && block.startTime >= block.endTime) {
          hasInvalidBlock = true;
        }
      });
    });

    if (hasInvalidBlock) {
      toast.error('Godzina rozpoczęcia musi być wcześniejsza niż godzina zakończenia');
      return;
    }

    // Zbierz wszystkie blokady
    const blocks = [];
    Object.entries(blockHoursData).forEach(([date, dayBlocks]) => {
      dayBlocks.forEach(block => {
        if (block.startTime && block.endTime) {
          blocks.push({
            date: date,
            startTime: block.startTime,
            endTime: block.endTime,
            reason: 'PRESEASON_UNAVAILABLE'
          });
        }
      });
    });

    if (blocks.length === 0) {
      toast.warning('Nie dodano żadnych blokad');
      return;
    }

    try {
      setIsSavingBlockHours(true);
      setBlockHoursError(null);
      await CalendarBlockControllerService.createCalendarBlocks({
        teamId: activeTeamId,
        blocks: blocks
      });
      toast.success('Blokady godzin zostały zapisane');
      setBlockHoursModal({ isOpen: false, schedule: null });
      setBlockHoursData({});
    } catch (error) {
      if (error.status === 409) {
        const errorMessage = 'Wykryto konflikt danych. Odśwież stronę i spróbuj ponownie.';
        setBlockHoursError(errorMessage);
      } else {
        toast.error('Nie udało się zapisać blokad godzin');
      }
    } finally {
      setIsSavingBlockHours(false);
    }
  };

  const formatDateForBlockHours = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  };

  // =============================================
  // Funkcje dla modalu "Znajdź" (wizard szukania przeciwnika)
  // =============================================


  // Pobierz blokady kalendarza dla kroku 3
  const fetchCalendarBlocks = async () => {
    const schedule = findOpponentModal.schedule;
    if (!schedule) {
      return;
    }

    const creatorTeamId = findOpponentSelectedTeam ? parseInt(findOpponentSelectedTeam) : undefined;
    const opponentTeamId = findOpponentSelectedOpponent?.id;

    if (!creatorTeamId || !opponentTeamId) {
      return;
    }

    setFindOpponentIsLoadingBlocks(true);
    try {
      const blocks = await CalendarBlockControllerService.getCompactCalendarBlocksBySchedule({
        startDate: schedule.approximateStartDate,
        endDate: schedule.approximateEndDate,
        creatorTeamId: creatorTeamId,
        opponentTeamId: opponentTeamId
      });
      setFindOpponentCalendarBlocks(blocks || []);
    } catch (error) {
      // error handled silently
      setFindOpponentCalendarBlocks([]);
    } finally {
      setFindOpponentIsLoadingBlocks(false);
    }
  };

  const resetFindOpponentWizard = () => {
    setFindOpponentStep(1);
    setFindOpponentIsTransitioning(false);
    setFindOpponentSelectedTeam('');
    setFindOpponentSelectedOpponent(null);
    setFindOpponentSelectedTimeSlot(null);
    setFindOpponentMatchDate('');
    setFindOpponentMatchTime({ hour: 18, minute: 0 });
    setFindOpponentMatchEndTime({ hour: 20, minute: 0 });
    setFindOpponentMatchMessage('');
    setFindOpponentHasPitchAvailable(false);
    setFindOpponentLocation('');
    setFindOpponentPitchLatitude(null);
    setFindOpponentPitchLongitude(null);
    setFindOpponentSearchResults([]);
    setFindOpponentOpponentGames([]);
    setFindOpponentOwnGamesForComparison([]);
    setFindOpponentSearchCriteria({ county: '', voivodeship: '', seniorTier: '' });
    setFindOpponentSearchError('');
    // Reset nowych stanów selektora daty/godziny
    setFindOpponentScheduleDates([]);
    setFindOpponentCurrentDateIndex(0);
    setFindOpponentSelectedTimeRange(null);
    setFindOpponentCalendarBlocks([]);
    setFindOpponentSelectedDay(null);
    setFindOpponentDateToBeDetermined(false);
    setFindOpponentShouldBlockCalendar(false);
    setFindOpponentLastMinuteMode(false);
    setFindOpponentDistanceSearch(false);
    setFindOpponentMaxDistance(80);
    setFindOpponentIgnoreDateOverlap(false);
  };

  // Sprawdź czy dana godzina jest zablokowana dla aktualnie wybranej daty
  const isTimeSlotBlocked = (hour, minute) => {
    const currentDate = findOpponentScheduleDates[findOpponentCurrentDateIndex];
    if (!currentDate || !findOpponentCalendarBlocks.length) return false;

    // Sprawdź czy jest blokada dla tej daty i godziny
    return findOpponentCalendarBlocks.some(block => {
      if (block.date !== currentDate) return false;

      // Parsuj startTime i endTime z bloku (format "HH:mm:ss" lub "HH:mm")
      const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const parts = timeStr.split(':');
        return { hour: parseInt(parts[0]), minute: parseInt(parts[1]) };
      };

      const blockStart = parseTime(block.startTime);
      const blockEnd = parseTime(block.endTime);

      if (!blockStart || !blockEnd) return false;

      // Przekształć godziny na minuty od początku dnia dla łatwiejszego porównania
      const slotMinutes = hour * 60 + minute;
      const blockStartMinutes = blockStart.hour * 60 + blockStart.minute;
      const blockEndMinutes = blockEnd.hour * 60 + blockEnd.minute;

      // Sprawdź czy slot mieści się w blokadzie
      return slotMinutes >= blockStartMinutes && slotMinutes < blockEndMinutes;
    });
  };

  // Sprawdź czy cały przedział godzinowy jest zablokowany (find opponent)
  const isTimeRangeFullyBlocked = (startHour, endHour) => {
    const currentDate = findOpponentScheduleDates[findOpponentCurrentDateIndex];
    if (!currentDate || !findOpponentCalendarBlocks.length) return false;

    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (!isTimeSlotBlocked(h, m)) return false;
      }
    }
    return true;
  };

  // Sprawdź czy wszystkie godziny w danym dniu są zablokowane (8:00-24:00 co 15 min)
  const isDayFullyBlocked = (date) => {
    if (!date || !findOpponentCalendarBlocks.length) return false;

    // Pobierz blokady tylko dla tego dnia
    const dayBlocks = findOpponentCalendarBlocks.filter(block => block.date === date);
    if (dayBlocks.length === 0) return false;

    // Parsuj czasy blokad raz
    const parsedBlocks = dayBlocks.map(block => {
      const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const parts = timeStr.split(':');
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      };
      return { start: parseTime(block.startTime), end: parseTime(block.endTime) };
    }).filter(b => b.start !== null && b.end !== null);

    // Sprawdź każdy 15-minutowy slot od 8:00 do 24:00
    for (let hour = 8; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const slotMinutes = hour * 60 + minute;
        const isBlocked = parsedBlocks.some(b => slotMinutes >= b.start && slotMinutes < b.end);
        if (!isBlocked) return false;
      }
    }
    return true;
  };

  // Sprawdź czy dana godzina jest zablokowana w modalu "Zaproponuj termin"
  const isProposeTimeSlotBlocked = (date, hour, minute) => {
    if (!date || !proposeTimeCalendarBlocks.length) return false;

    return proposeTimeCalendarBlocks.some(block => {
      if (block.date !== date) return false;

      const parseTime = (timeVal) => {
        if (!timeVal) return null;
        if (typeof timeVal === 'string') {
          const parts = timeVal.split(':');
          return { hour: parseInt(parts[0]), minute: parseInt(parts[1]) };
        }
        return { hour: timeVal.hour, minute: timeVal.minute };
      };

      const blockStart = parseTime(block.startTime);
      const blockEnd = parseTime(block.endTime);
      if (!blockStart || !blockEnd) return false;

      const slotMinutes = hour * 60 + minute;
      const blockStartMinutes = blockStart.hour * 60 + blockStart.minute;
      const blockEndMinutes = blockEnd.hour * 60 + blockEnd.minute;

      return slotMinutes >= blockStartMinutes && slotMinutes < blockEndMinutes;
    });
  };

  // Sprawdź czy cały dzień jest zablokowany w modalu "Zaproponuj termin"
  const isProposeTimeDayFullyBlocked = (date) => {
    if (!date || !proposeTimeCalendarBlocks.length) return false;

    const dayBlocks = proposeTimeCalendarBlocks.filter(block => block.date === date);
    if (dayBlocks.length === 0) return false;

    const parsedBlocks = dayBlocks.map(block => {
      const parseTime = (timeVal) => {
        if (!timeVal) return null;
        if (typeof timeVal === 'string') {
          const parts = timeVal.split(':');
          return parseInt(parts[0]) * 60 + parseInt(parts[1]);
        }
        return timeVal.hour * 60 + timeVal.minute;
      };
      return { start: parseTime(block.startTime), end: parseTime(block.endTime) };
    }).filter(b => b.start !== null && b.end !== null);

    for (let h = 8; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const slotMinutes = h * 60 + m;
        const isBlocked = parsedBlocks.some(b => slotMinutes >= b.start && slotMinutes < b.end);
        if (!isBlocked) return false;
      }
    }
    return true;
  };

  // Sprawdź czy cały przedział godzinowy jest zablokowany w modalu "Zaproponuj termin"
  const isProposeTimeRangeFullyBlocked = (date, startHour, endHour) => {
    if (!date || !proposeTimeCalendarBlocks.length) return false;

    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (!isProposeTimeSlotBlocked(date, h, m)) return false;
      }
    }
    return true;
  };

  // Funkcja pomocnicza do generowania przedziałów 15-minutowych dla danego zakresu godzin
  const generateTimeSlots = (startHour, endHour) => {
    const slots = [];
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({
          hour,
          minute,
          label: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
        });
      }
    }
    return slots;
  };

  // Przedziały godzinowe (4 rzędy x 2 kolumny = 8 przedziałów, od 8:00 do 24:00)
  const timeRanges = [
    { start: 8, end: 10, label: '08:00 - 10:00' },
    { start: 10, end: 12, label: '10:00 - 12:00' },
    { start: 12, end: 14, label: '12:00 - 14:00' },
    { start: 14, end: 16, label: '14:00 - 16:00' },
    { start: 16, end: 18, label: '16:00 - 18:00' },
    { start: 18, end: 20, label: '18:00 - 20:00' },
    { start: 20, end: 22, label: '20:00 - 22:00' },
    { start: 22, end: 24, label: '22:00 - 00:00' }
  ];

  // Obsługa wyboru przedziału godzinowego
  const handleTimeRangeSelect = (range) => {
    setFindOpponentSelectedTimeRange(range);
    // Resetuj wybór dokładnej godziny przy zmianie przedziału
    setFindOpponentSelectedTimeSlot(null);
    setFindOpponentMatchDate('');
  };

  // Obsługa wyboru dokładnej godziny (kafelek 15-minutowy)
  const handleExactTimeSelect = (slot) => {
    const selectedDate = findOpponentScheduleDates[findOpponentCurrentDateIndex];
    setFindOpponentMatchDate(selectedDate);
    setFindOpponentMatchTime({ hour: slot.hour, minute: slot.minute });
    setFindOpponentMatchEndTime({ hour: (slot.hour + 2) % 24, minute: slot.minute }); // Domyślnie 2h
    setFindOpponentSelectedTimeSlot({
      date: selectedDate,
      hour: slot.hour,
      minute: slot.minute
    });
  };

  const handleOpenFindOpponentModal = (schedule) => {
    resetFindOpponentWizard();

    // Ustaw domyślną datę i godzinę
    const today = new Date();
    const dateStr = formatLocalDate(today.getFullYear(), today.getMonth(), today.getDate());
    setFindOpponentMatchDate(dateStr);
    setFindOpponentMatchTime({ hour: 18, minute: 0 });
    setFindOpponentMatchEndTime({ hour: 20, minute: 0 });

    // Ustaw wybraną drużynę z activeTeamId (dla coach lub sportsclub)
    if (activeTeamId) {
      setFindOpponentSelectedTeam(String(activeTeamId));
    }

    // Wygeneruj daty z przekazanego schedule (zamiast wyboru z dropdown)
    if (schedule.approximateStartDate && schedule.approximateEndDate) {
      const dates = generateDatesBetween(schedule.approximateStartDate, schedule.approximateEndDate);
      setFindOpponentScheduleDates(dates);
      setFindOpponentCurrentDateIndex(0);
    }

    // Ustaw odległość wyszukiwania — z obozu lub domyślna 80 km
    if (schedule.isAtCamp && schedule.campMaxDistanceKm) {
      setFindOpponentMaxDistance(Number(schedule.campMaxDistanceKm));
    } else {
      setFindOpponentMaxDistance(80);
    }
    setFindOpponentDistanceSearch(true);
    setFindOpponentEditDistance(false);

    // If team is at camp, pre-fill pitch with camp location
    if (schedule.isAtCamp) {
      setFindOpponentHasPitchAvailable(true);
      if (schedule.campLatitude && schedule.campLongitude) {
        setFindOpponentPitchLatitude(schedule.campLatitude);
        setFindOpponentPitchLongitude(schedule.campLongitude);
      }
      setFindOpponentLocation(schedule.campAddress || '');
    } else if (clubLatitude && clubLongitude) {
      // Pre-fill z adresem klubu z rejestracji (bez zaznaczania "mam boisko")
      setFindOpponentPitchLatitude(clubLatitude);
      setFindOpponentPitchLongitude(clubLongitude);
      setFindOpponentLocation(clubCity || '');
    }

    setFindOpponentModal({ isOpen: true, schedule });
  };

  const handleCloseFindOpponentModal = () => {
    setFindOpponentModal({ isOpen: false, schedule: null });
    resetFindOpponentWizard();
  };

  // Funkcje dla modalu "Dodaj przeciwnika spoza systemu"
  const handleOpenManualOpponentModal = (schedule) => {
    setManualOpponentName('');
    setManualOpponentLocation('');
    setManualOpponentTier('');
    setManualOpponentSelectedDay(null);
    setManualOpponentSelectedTimeRange(null);
    setManualOpponentSelectedTimeSlot(null);

    if (schedule.approximateStartDate && schedule.approximateEndDate) {
      const dates = generateDatesBetween(schedule.approximateStartDate, schedule.approximateEndDate);
      setManualOpponentDates(dates);
    } else {
      setManualOpponentDates([]);
    }

    setManualOpponentModal({ isOpen: true, schedule });
  };

  const handleCloseManualOpponentModal = () => {
    setManualOpponentModal({ isOpen: false, schedule: null });
    setManualOpponentName('');
    setManualOpponentLocation('');
    setManualOpponentTier('');
    setManualOpponentSelectedDay(null);
    setManualOpponentSelectedTimeRange(null);
    setManualOpponentSelectedTimeSlot(null);
    setManualOpponentDates([]);
  };

  const handleManualOpponentTimeRangeSelect = (range) => {
    setManualOpponentSelectedTimeRange(range);
    setManualOpponentSelectedTimeSlot(null);
  };

  const handleManualOpponentTimeSlotSelect = (slot) => {
    setManualOpponentSelectedTimeSlot(slot);
  };

  const handleManualOpponentSubmit = async () => {
    if (!manualOpponentName.trim()) {
      toast.error('Wpisz nazwę przeciwnika');
      return;
    }
    if (!manualOpponentSelectedDay) {
      toast.error('Wybierz dzień meczu');
      return;
    }
    if (!manualOpponentSelectedTimeSlot) {
      toast.error('Wybierz godzinę meczu');
      return;
    }

    const startHour = String(manualOpponentSelectedTimeSlot.hour).padStart(2, '0');
    const startMinute = String(manualOpponentSelectedTimeSlot.minute).padStart(2, '0');
    const endHour = String((manualOpponentSelectedTimeSlot.hour + 2) % 24).padStart(2, '0');
    const endMinute = String(manualOpponentSelectedTimeSlot.minute).padStart(2, '0');

    setIsSubmittingManualOpponent(true);
    try {
      await PreseasonGameControllerService.createManualPreseasonGame({
        teamCreatorId: activeTeamId,
        sportsClubCreatorId: sportsClubId || null,
        manualOpponentName: manualOpponentName.trim(),
        date: manualOpponentSelectedDay,
        startTime: `${startHour}:${startMinute}`,
        endTime: `${endHour}:${endMinute}`,
        location: manualOpponentLocation.trim() || null,
        creatorScheduleId: manualOpponentModal.schedule?.termId || null,
        manualOpponentTier: manualOpponentTier || null
      });
      toast.success(`Dodano mecz z "${manualOpponentName.trim()}"`);
      handleCloseManualOpponentModal();
      preserveDateRangeIndexRef.current = true;
      fetchPreseasonSummary();
    } catch (error) {
      if (error?.body?.message) {
        toast.error(error.body.message);
      } else {
        toast.error('Nie udało się dodać meczu');
      }
    } finally {
      setIsSubmittingManualOpponent(false);
    }
  };

  // Map picker confirm/clear handlers — Find Opponent
  const handleFindOpponentMapConfirm = (result) => {
    setFindOpponentLocation(result.searchTerm || result.displayName);
    setFindOpponentPitchLatitude(result.latitude);
    setFindOpponentPitchLongitude(result.longitude);
    setFindOpponentMapPickerOpen(false);
  };
  const handleFindOpponentClearLocation = () => {
    setFindOpponentLocation('');
    setFindOpponentPitchLatitude(null);
    setFindOpponentPitchLongitude(null);
  };


  const handleFindOpponentSearchChange = (e) => {
    const { name, value } = e.target;

    if (name === 'voivodeship') {
      setFindOpponentSearchCriteria(prev => ({
        ...prev,
        voivodeship: value,
        county: ''
      }));
    } else {
      setFindOpponentSearchCriteria(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const getFindOpponentCountiesForVoivodeship = () => {
    if (!findOpponentSearchCriteria.voivodeship) {
      return [];
    }
    const territory = findOpponentDictionaries.territories.find(t => t.key === findOpponentSearchCriteria.voivodeship);
    return territory?.subValues || [];
  };

  const performFindOpponentTeamSearch = async (page = 0) => {
    setFindOpponentIsLoadingSearch(true);
    setFindOpponentSearchError('');

    // Użyj ID schedule przekazanego z kafelka
    const scheduleId = findOpponentModal.schedule?.termId;
    if (!scheduleId) {
      setFindOpponentSearchError('Brak identyfikatora terminu');
      setFindOpponentIsLoadingSearch(false);
      return;
    }

    try {
      const useDistanceSearch = findOpponentDistanceSearch && findOpponentPitchLatitude != null;
      const criteria = {
        county: findOpponentSearchCriteria.county || undefined,
        voivodeship: findOpponentSearchCriteria.voivodeship || undefined,
        tier: findOpponentSearchCriteria.seniorTier || undefined,
        searchMode: useDistanceSearch ? 'DISTANCE' : undefined,
        maxDistanceKm: useDistanceSearch ? findOpponentMaxDistance : undefined,
        pitchLatitude: useDistanceSearch ? findOpponentPitchLatitude : undefined,
        pitchLongitude: useDistanceSearch ? findOpponentPitchLongitude : undefined,
        scheduleId: scheduleId,
        ignoreDateOverlap: findOpponentIgnoreDateOverlap || undefined
      };

      const response = await TeamControllerService.searchTeams(
        criteria,
        scheduleId,
        page,
        10
      );

      setFindOpponentSearchResults(response.content || []);
      setFindOpponentCurrentPage(response.number ?? 0);
      setFindOpponentTotalPages(response.totalPages || 0);

      // Fetch total team count in system on first search
      if (page === 0) {
        TeamControllerService.getTotalTeamCount()
          .then(count => setFindOpponentTotalTeamsInSystem(count))
          .catch(() => {});
      }

      if (!response.content || response.content.length === 0) {
        setFindOpponentSearchError('Nie znaleziono drużyn spełniających kryteria');
      }
    } catch (error) {
      setFindOpponentSearchError('Wystąpił błąd podczas wyszukiwania');
    } finally {
      setFindOpponentIsLoadingSearch(false);
    }
  };

  const handleFindOpponentSearch = async (e) => {
    e.preventDefault();
    setFindOpponentCurrentPage(0);
    await performFindOpponentTeamSearch(0);
  };

  const handleFindOpponentResetSearch = () => {
    setFindOpponentSearchCriteria({
      county: '',
      voivodeship: '',
      seniorTier: ''
    });
    // Przywróć odległość do wartości globalnej (obóz lub definicja)
    const schedule = findOpponentModal.schedule;
    if (schedule?.isAtCamp && schedule?.campMaxDistanceKm) {
      setFindOpponentMaxDistance(Number(schedule.campMaxDistanceKm));
    } else {
      setFindOpponentMaxDistance(80);
    }
    setFindOpponentSearchResults([]);
    setFindOpponentSearchError('');
  };

  const handleFindOpponentPageChange = (newPage) => {
    setFindOpponentCurrentPage(newPage);
    performFindOpponentTeamSearch(newPage);
  };

  const handleSelectFindOpponent = (team) => {
    setFindOpponentSelectedOpponent(team);
  };

  const fetchFindOpponentCalendar = async (opponentSportsClubId, opponentTeamId, weekStart) => {
    setFindOpponentIsLoadingOpponentGames(true);
    try {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const dateFrom = formatLocalDate(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate()
      );
      const dateTo = formatLocalDate(
        weekEnd.getFullYear(),
        weekEnd.getMonth(),
        weekEnd.getDate()
      );

      const [opponentCalendarResponse, overallStats] = await Promise.all([
        CalendarService.getDetailedCalendar(
          opponentSportsClubId,
          opponentTeamId,
          dateFrom,
          dateTo
        ),
        PreseasonGameControllerService.getPendingProposalsOverallStatistics(
          opponentTeamId,
          dateFrom,
          dateTo
        )
      ]);
      setFindOpponentOpponentGames(opponentCalendarResponse?.entries || []);
      setFindOpponentOpponentOverallStats(overallStats || []);
    } catch (error) {
      setFindOpponentOpponentGames([]);
      setFindOpponentOpponentOverallStats([]);
    } finally {
      setFindOpponentIsLoadingOpponentGames(false);
    }
  };

  const fetchFindOpponentOwnCalendarForComparison = async (weekStart) => {
    if (!findOpponentSelectedTeam || !(isCoach ? sportsClubId : userId)) {
      setFindOpponentOwnGamesForComparison([]);
      setFindOpponentOwnOverallStats([]);
      return;
    }

    try {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const dateFrom = formatLocalDate(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate()
      );
      const dateTo = formatLocalDate(
        weekEnd.getFullYear(),
        weekEnd.getMonth(),
        weekEnd.getDate()
      );

      const ownTeamId = parseInt(findOpponentSelectedTeam);

      const [ownCalendarResponse, stats] = await Promise.all([
        CalendarService.getDetailedCalendar(
          isCoach ? sportsClubId : userId,
          ownTeamId,
          dateFrom,
          dateTo
        ),
        PreseasonGameControllerService.getPendingProposalsOverallStatistics(
          ownTeamId,
          dateFrom,
          dateTo
        )
      ]);
      setFindOpponentOwnGamesForComparison(ownCalendarResponse?.entries || []);
      setFindOpponentOwnOverallStats(stats || []);
    } catch (error) {
      setFindOpponentOwnGamesForComparison([]);
      setFindOpponentOwnOverallStats([]);
    }
  };

  const goToNextFindOpponentStep = () => {
    if (findOpponentStep === 1 && !findOpponentSelectedTeam) {
      toast.error('Wybierz swoją drużynę');
      return;
    }
    if (findOpponentStep === 1 && findOpponentHasPitchAvailable && !findOpponentPitchLatitude) {
      toast.error('Wybierz lokalizację boiska na mapie');
      return;
    }
    if (findOpponentStep === 2 && !findOpponentSelectedOpponent) {
      return;
    }
    if (findOpponentStep === 3) {
      if (!findOpponentHasPitchAvailable) {
        if (!findOpponentDateToBeDetermined && !findOpponentSelectedDay) return;
        if (!findOpponentDateToBeDetermined) {
          setFindOpponentSelectedTimeSlot({ date: findOpponentSelectedDay });
          setFindOpponentMatchDate(findOpponentSelectedDay);
        }
      } else {
        if (!findOpponentSelectedDay || !findOpponentSelectedTimeRange || !findOpponentSelectedTimeSlot) return;
      }
    }

    let nextStep = Math.min(findOpponentStep + 1, 4);

    // Tryb przyspieszony: pomiń krok 2 (wybór przeciwnika)
    if (findOpponentLastMinuteMode && findOpponentStep === 1) {
      nextStep = 3;
    }

    // Przy przejściu do kroku 3 - resetuj wybrany przedział czasowy i dzień, pobierz blokady
    if (nextStep === 3) {
      setFindOpponentSelectedTimeRange(null);
      setFindOpponentCurrentDateIndex(0);
      setFindOpponentSelectedDay(null);
      setFindOpponentSelectedTimeSlot(null);
      setFindOpponentDateToBeDetermined(false);
      setFindOpponentShouldBlockCalendar(false);

      // Dla non-overlapping przeciwnika: użyj dat z terminu przeciwnika zamiast swoich
      if (findOpponentIgnoreDateOverlap && findOpponentSelectedOpponent?.isNonOverlapping
          && findOpponentSelectedOpponent?.opponentScheduleStartDate && findOpponentSelectedOpponent?.opponentScheduleEndDate) {
        const dates = generateDatesBetween(
          findOpponentSelectedOpponent.opponentScheduleStartDate,
          findOpponentSelectedOpponent.opponentScheduleEndDate
        );
        setFindOpponentScheduleDates(dates);
      }

      fetchCalendarBlocks();
    }

    setFindOpponentIsTransitioning(true);
    setFindOpponentStep(nextStep);

    setTimeout(() => {
      setFindOpponentIsTransitioning(false);
    }, 100);
  };

  const goToPreviousFindOpponentStep = () => {
    setFindOpponentStep(prev => {
      let prevStep = Math.max(prev - 1, 1);
      // Tryb przyspieszony: pomiń krok 2 wstecz
      if (findOpponentLastMinuteMode && prevStep === 2) {
        prevStep = 1;
      }
      return prevStep;
    });
  };

  const handleSendFindOpponentProposal = async (e) => {
    e.preventDefault();

    if (findOpponentIsTransitioning) {
      return;
    }

    if (findOpponentStep !== 4) {
      return;
    }

    if (!findOpponentSelectedTeam) {
      toast.error('Wybierz swoją drużynę');
      return;
    }

    if (!findOpponentLastMinuteMode && !findOpponentSelectedOpponent) {
      toast.error('Wybierz przeciwną drużynę');
      return;
    }

    // Walidacja - jeśli mamy boisko, wymagamy time slot; w przeciwnym razie wymagamy tylko wybranego dnia
    if (findOpponentHasPitchAvailable) {
      if (!findOpponentSelectedTimeSlot || !findOpponentMatchDate) {
        toast.error('Wybierz datę i godzinę z kalendarza');
        return;
      }
    } else {
      if (!findOpponentDateToBeDetermined && !findOpponentSelectedDay) {
        toast.error('Wybierz dzień');
        return;
      }
    }

    setFindOpponentIsSendingMatch(true);

    // Użyj wybranego dnia jako daty meczu, jeśli nie mamy boiska (null jeśli "Do ustalenia później")
    const matchDate = findOpponentHasPitchAvailable
      ? findOpponentMatchDate
      : (findOpponentDateToBeDetermined ? null : findOpponentSelectedDay);

    try {
      if (findOpponentLastMinuteMode) {
        // Tryb przyspieszony — searchLastMinutePreseasonGame
        const requestBody = {
          teamId: parseInt(findOpponentSelectedTeam),
          scheduleId: findOpponentModal.schedule?.termId,
          message: findOpponentMatchMessage || undefined
        };
        if (matchDate) {
          requestBody.date = matchDate;
        }
        if (findOpponentHasPitchAvailable && findOpponentSelectedTimeSlot) {
          requestBody.startTime = `${String(findOpponentMatchTime.hour).padStart(2, '0')}:${String(findOpponentMatchTime.minute).padStart(2, '0')}`;
          requestBody.endTime = `${String(findOpponentMatchEndTime.hour).padStart(2, '0')}:${String(findOpponentMatchEndTime.minute).padStart(2, '0')}`;
        }
        await PreseasonGameControllerService.searchLastMinutePreseasonGame(requestBody);
        toast.success('Wysłano powiadomienia do pasujących drużyn!');
      } else {
        // Tryb normalny — createPreseasonGame
        await PreseasonGameControllerService.createPreseasonGame({
          sportsClubCreatorId: isCoach ? (sportsClubId || userId) : userId,
          teamCreatorId: parseInt(findOpponentSelectedTeam),
          sportsClubOpponentId: findOpponentSelectedOpponent.sportsClubId,
          teamOpponentId: findOpponentSelectedOpponent.id,
          date: matchDate,
          startTime: findOpponentHasPitchAvailable ? `${String(findOpponentMatchTime.hour).padStart(2, '0')}:${String(findOpponentMatchTime.minute).padStart(2, '0')}` : null,
          endTime: findOpponentHasPitchAvailable ? `${String(findOpponentMatchEndTime.hour).padStart(2, '0')}:${String(findOpponentMatchEndTime.minute).padStart(2, '0')}` : null,
          hasPitchAvailable: findOpponentHasPitchAvailable,
          location: findOpponentHasPitchAvailable ? findOpponentLocation : null,
          pitchLatitude: findOpponentHasPitchAvailable ? findOpponentPitchLatitude : null,
          pitchLongitude: findOpponentHasPitchAvailable ? findOpponentPitchLongitude : null,
          shouldBlockCalendarWhenNoPitchAvailable: findOpponentHasPitchAvailable ? true : (isYearRound ? false : ((findOpponentModal.schedule?.amountOfGames || 1) > 1 ? findOpponentShouldBlockCalendar : true)),
          creatorScheduleId: findOpponentSelectedOpponent.isNonOverlapping ? undefined : (findOpponentModal.schedule?.termId || undefined),
          opponentScheduleId: findOpponentSelectedOpponent.scheduleId || undefined,
          message: findOpponentMatchMessage || undefined,
          createScheduleFromOpponent: findOpponentSelectedOpponent.isNonOverlapping || undefined
        });
        toast.success(`Propozycja sparingu z drużyną ${findOpponentSelectedOpponent.sportsClubName} została wysłana`);
      }

      handleCloseFindOpponentModal();
      preserveDateRangeIndexRef.current = true;
      fetchPreseasonSummary();
    } catch (error) {
      const message = error?.body?.message || error?.message || 'Nie udało się wysłać zapytania';
      toast.error(message);
    } finally {
      setFindOpponentIsSendingMatch(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  // Wydzielona funkcja renderująca modale bulk (używana w obu gałęziach return)
  const renderBulkTermModalContent = () => (
    <div className="modal-overlay" onClick={handleBulkTermCancel}>
      <div className="modal-content modal-content-bulk" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Dodaj wiele terminów</h2>
          <button className="modal-close-btn" onClick={handleBulkTermCancel}>&times;</button>
        </div>
        <div className="modal-body bulk-modal-body">
          <div className="preseason-definition-card">
            <div className="preseason-two-column-layout">
              <div className="preseason-basic-info">
                <h4 className="column-header">Podstawowe informacje</h4>
                <div className="form-group">
                  <label>Data rozpoczęcia przygotowań<span className="required-asterisk">*</span></label>
                  <input type="date" className="form-input" value={bulkTermData.startDate}
                    min={!isYearRound && bulkTermSeasonDef?.minStartDate ? bulkTermSeasonDef.minStartDate : undefined}
                    max={!isYearRound && bulkTermSeasonDef?.maxEndDate ? (bulkTermData.endDate || bulkTermSeasonDef.maxEndDate) : (bulkTermData.endDate || undefined)}
                    onChange={(e) => setBulkTermData(prev => ({ ...prev, startDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Data zakończenia przygotowań<span className="required-asterisk">*</span></label>
                  <input type="date" className="form-input" value={bulkTermData.endDate}
                    min={bulkTermData.startDate || (!isYearRound && bulkTermSeasonDef?.minStartDate ? bulkTermSeasonDef.minStartDate : undefined)}
                    max={!isYearRound && bulkTermSeasonDef?.maxEndDate ? bulkTermSeasonDef.maxEndDate : undefined}
                    onChange={(e) => setBulkTermData(prev => ({ ...prev, endDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Liczba sparingów do rozegrania<span className="required-asterisk">*</span></label>
                  <input type="number" className="form-input" min="0" value={bulkTermSchedules.length}
                    onChange={(e) => {
                      const count = parseInt(e.target.value) || 0;
                      setBulkTermSchedules(prev => {
                        const newSchedules = [];
                        for (let i = 0; i < count; i++) {
                          newSchedules.push(prev[i] || { startDate: '', endDate: '', gamesCount: 1, opponentTiers: [], opponentYouthTiers: [], opponentYearOfBirths: [] });
                        }
                        return newSchedules;
                      });
                    }}
                    placeholder="Wprowadź liczbę" />
                  <button type="button" className="btn btn-fill-weekends" onClick={handleBulkTermFillWeekends}
                    disabled={!bulkTermData.startDate || !bulkTermData.endDate}>
                    Uzupełnij weekendami
                  </button>
                </div>
              </div>
              <div className="preseason-sparings-info">
                <h4 className="column-header">Definicje sparingów</h4>
                {bulkTermSchedules.length > 0 ? (
                  <div className="sparings-container">
                    {bulkTermSchedules.map((schedule, index) => (
                      <React.Fragment key={index}>
                        <div className="sparing-item-row">
                          <div className="sparing-item-header">
                            <h5 className="sparing-item-title">Sparing {index + 1}</h5>
                            <div className="sparing-header-actions">
                              <button type="button" className="sparing-remove-btn"
                                onClick={() => handleBulkTermRemoveSchedule(index)} title="Usuń sparing">&times;</button>
                            </div>
                          </div>
                          <div className="sparing-item-fields">
                            <div className="form-group sparing-field">
                              <label>Data od<span className="required-asterisk">*</span></label>
                              <input type="date" className="form-input" value={schedule.startDate}
                                min={!isYearRound && bulkTermSeasonDef?.minStartDate ? bulkTermSeasonDef.minStartDate : undefined}
                                max={!isYearRound && bulkTermSeasonDef?.maxEndDate ? (schedule.endDate || bulkTermSeasonDef.maxEndDate) : (schedule.endDate || undefined)}
                                onChange={(e) => handleBulkTermScheduleChange(index, 'startDate', e.target.value)} />
                            </div>
                            <div className="form-group sparing-field">
                              <label>Data do<span className="required-asterisk">*</span></label>
                              <input type="date" className="form-input" value={schedule.endDate}
                                min={schedule.startDate || (!isYearRound && bulkTermSeasonDef?.minStartDate ? bulkTermSeasonDef.minStartDate : undefined)}
                                max={!isYearRound && bulkTermSeasonDef?.maxEndDate ? bulkTermSeasonDef.maxEndDate : undefined}
                                onChange={(e) => handleBulkTermScheduleChange(index, 'endDate', e.target.value)} />
                            </div>
                            <div className="form-group sparing-field">
                              <label>Ilość sparingów<span className="required-asterisk">*</span></label>
                              <input type="number" className="form-input" min="1" max="2" value={schedule.gamesCount || 1}
                                onChange={(e) => handleBulkTermScheduleChange(index, 'gamesCount', e.target.value)} />
                            </div>
                            <div className="form-group sparing-field sparing-button-field">
                              <label>&nbsp;</label>
                              <button type="button" className="btn btn-secondary btn-define-opponent"
                                onClick={() => setBulkTermOpponentModalOpen(index)}>
                                Określ poziom rozgrywkowy przeciwnika
                              </button>
                            </div>
                          </div>
                          {((schedule.opponentTiers && schedule.opponentTiers.length > 0) ||
                            (schedule.opponentYearOfBirths && schedule.opponentYearOfBirths.length > 0) ||
                            (schedule.opponentYouthTiers && schedule.opponentYouthTiers.length > 0)) && (
                            <div className="selection-badges">
                              {(schedule.opponentTiers || []).map(tierKey => {
                                const tier = seniorTiers.find(t => t.key === tierKey);
                                return tier ? <span key={tierKey} className="selection-badge badge-opponent">{tier.displayValue}</span> : null;
                              })}
                            </div>
                          )}
                        </div>
                        {index < bulkTermSchedules.length - 1 && (
                          <div className="sparing-insert-btn-wrapper">
                            <button type="button" className="sparing-insert-btn"
                              onClick={() => handleBulkTermInsertSchedule(index)} title="Wstaw sparing">+</button>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="no-sparings-message">
                    <p>Wprowadź liczbę sparingów aby dodać ich definicje</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {bulkTermValidationErrors.length > 0 && (
            <div className="save-validation-errors">
              {bulkTermValidationErrors.map((err, i) => <p key={i} className="save-validation-error">{err}</p>)}
            </div>
          )}
          {bulkTermError && <div className="error-message-409">{bulkTermError}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={handleBulkTermCancel} disabled={isSavingBulkTerm}>Anuluj</button>
          <button className="btn-save" onClick={handleBulkTermConfirm}
            disabled={isSavingBulkTerm || bulkTermSchedules.length === 0}>
            {isSavingBulkTerm ? 'Zapisywanie...' : `Zapisz (${bulkTermSchedules.length} terminów)`}
          </button>
        </div>
      </div>
      {bulkTermOpponentModalOpen !== null && (() => {
        const idx = bulkTermOpponentModalOpen;
        const schedule = bulkTermSchedules[idx];
        if (!schedule) return null;
        const opType = bulkTermOpponentTypes[idx] || (teamType === 'YOUTH' ? { senior: false, junior: true } : { senior: true, junior: false });
        return (
          <div className="modal-overlay bulk-opponent-overlay" onClick={() => setBulkTermOpponentModalOpen(null)}>
            <div className="modal-content bulk-opponent-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Przeciwnik — Sparing {idx + 1}</h2>
                <button className="modal-close-btn" onClick={() => setBulkTermOpponentModalOpen(null)}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="opponent-type-checkboxes">
                  <label className="tier-checkbox-label" style={{
                    cursor: teamType === 'YOUTH' && isUnder16 ? 'not-allowed' : 'pointer',
                    opacity: teamType === 'YOUTH' && isUnder16 ? 0.6 : 1
                  }}>
                    <input type="checkbox" checked={opType.senior}
                      onChange={(e) => handleBulkTermOpponentTypeChange(idx, 'senior', e.target.checked)}
                      disabled={teamType === 'YOUTH' && isUnder16} />
                    <span className="tier-checkbox-text">Seniorzy</span>
                  </label>
                </div>
                {opType.senior && (
                  <>
                    <label className="section-label" style={{ marginTop: '0.5rem', display: 'block' }}>Poziom rozgrywkowy</label>
                    <div className="tier-checkboxes tier-checkboxes-compact">
                      {seniorTiers.filter(tier => !tier.teamType || tier.teamType === 'SENIOR').map((tier) => (
                        <label key={tier.key} className="tier-checkbox-label tier-checkbox-label-compact">
                          <input type="checkbox" checked={(schedule.opponentTiers || []).includes(tier.key)}
                            onChange={(e) => handleBulkTermTierChange(idx, tier.key, e.target.checked)} />
                          <span className="tier-checkbox-text">{tier.displayValue}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-save" onClick={() => setBulkTermOpponentModalOpen(null)}>Gotowe</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );

  const renderBulkTermModal = () => (
    <>
      {bulkTermModal.isOpen && renderBulkTermModalContent()}
    </>
  );

  // Dla sportsclub - pokazuj loading drużyn
  if (isSportsClub && teamsLoading) {
    return (
      <div className="preseason-summary-container">
        <div className="loading-spinner">Ładowanie drużyn...</div>
      </div>
    );
  }

  // Dla sportsclub bez drużyn
  if (isSportsClub && teams.length === 0 && !teamsLoading) {
    return (
      <div className="preseason-summary-container">
        <div className="no-data-message">Brak drużyn przypisanych do klubu</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="preseason-summary-container">
        {renderTeamDropdown()}
        <div className="loading-spinner">Ładowanie...</div>
      </div>
    );
  }

  if (!summaryData) {
    // Pierwszy login lub brak definicji — pokaż dropdown, widoki i przyciski dodawania
    return (
      <div className="preseason-summary-container">
        <h2 className="preseason-page-title">Twoje plany</h2>
        <div className="top-bar-row">
          {renderTeamDropdown()}
          <div className="view-mode-buttons">
            <button
              className={`btn ${viewMode === 'operational' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('operational')}
            >
              Widok operacyjny
            </button>
            <button
              className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('calendar')}
            >
              Widok kalendarza
            </button>
          </div>
        </div>

        <div className="preseason-actions-bar">
          <button className="btn-add-schedule btn-add-bulk" onClick={handleBulkTermClick}>
            Dodaj wiele terminów
          </button>
          <button className="btn-add-schedule" onClick={handleAddTermClick}>
            Dodaj termin
          </button>
        </div>

        <div className="no-data-message">
          <p>Dodaj przynajmniej jeden termin, aby stać się widocznym w wyszukiwarce i móc wyszukiwać przeciwników.</p>
        </div>

        {/* Modale — muszą być renderowane również w tym widoku */}
        {renderBulkTermModal()}
      </div>
    );
  }

  const { preseasonStartDate, preseasonEndDate, games = [], coachName } = summaryData;
  const isPreseasonNotDefined = isSportsClub && !isYearRound && !preseasonStartDate && !preseasonEndDate;
  const isYearRoundNotDefined = isYearRound && !preseasonStartDate && !preseasonEndDate;

  // Grupowanie gier po przedziałach dat (approximateStartDate + approximateEndDate)
  const groupGamesByDateRange = (games) => {
    const groups = {};
    games.forEach(game => {
      const key = `${game.approximateStartDate || ''}_${game.approximateEndDate || ''}`;
      if (!groups[key]) {
        groups[key] = {
          startDate: game.approximateStartDate,
          endDate: game.approximateEndDate,
          games: []
        };
      }
      groups[key].games.push(game);
    });
    // Sortuj grupy po dacie rozpoczęcia
    return Object.values(groups).sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(a.startDate) - new Date(b.startDate);
    });
  };

  const dateRangeGroups = groupGamesByDateRange(games);
  const currentGroup = dateRangeGroups[currentDateRangeIndex] || null;
  const currentGames = currentGroup?.games || [];

  const handlePreviousDateRange = () => {
    setCurrentDateRangeIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextDateRange = () => {
    setCurrentDateRangeIndex(prev => Math.min(dateRangeGroups.length - 1, prev + 1));
  };

  return (
    <div className="preseason-summary-container">
      <h2 className="preseason-page-title">Twoje plany</h2>
      <div className="top-bar-row">
        {renderTeamDropdown()}

        <div className="view-mode-buttons">
          <button
            className={`btn ${viewMode === 'operational' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('operational')}
          >
            Widok operacyjny
          </button>
          <button
            className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('calendar')}
          >
            Widok kalendarza
          </button>
        </div>
      </div>

      {/* Mobile team dropdown - above tabs, below navbar */}
      {((isCoach && reduxTeams?.length > 1) || (isSportsClub && teams.length > 1)) && (
        <div className="mobile-team-dropdown-wrapper">
          <label className="mobile-team-dropdown-label">Drużyna:</label>
          <select
            className="mobile-team-dropdown"
            value={reduxSelectedTeamId || ''}
            onChange={(e) => {
              dispatch(setReduxSelectedTeamId(Number(e.target.value)));
              setSummaryData(null);
            }}
          >
            {isCoach
              ? reduxTeams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)
              : teams.map(t => <option key={t.teamId} value={t.teamId}>{t.name}</option>)
            }
          </select>
        </div>
      )}

      {/* Mobile tabs */}
      {(
        <div className="mobile-tabs">
          <button
            className={`mobile-tab ${mobileTab === 'sparingi' ? 'mobile-tab--active' : ''}`}
            onClick={() => setMobileTab('sparingi')}
          >
            Sparingi
          </button>
          <button
            className={`mobile-tab ${mobileTab === 'kalendarz' ? 'mobile-tab--active' : ''}`}
            onClick={() => setMobileTab('kalendarz')}
          >
            Kalendarz
          </button>
          {!isYearRound && (
            <button
              className={`mobile-tab ${mobileTab === 'ustawienia' ? 'mobile-tab--active' : ''}`}
              onClick={() => setMobileTab('ustawienia')}
            >
              Zarządzaj okresem
            </button>
          )}
        </div>
      )}

      {(isPreseasonNotDefined || isYearRoundNotDefined) ? (
        <>
          <div className="preseason-actions-bar">
            <button className="btn-add-schedule btn-add-bulk" onClick={handleBulkTermClick}>
              Dodaj wiele terminów
            </button>
            <button className="btn-add-schedule" onClick={handleAddTermClick}>
              Dodaj termin
            </button>
          </div>
          <div className="no-data-message">
            <p>Dodaj przynajmniej jeden termin, aby stać się widocznym w wyszukiwarce i móc wyszukiwać przeciwników.</p>
            <p>Jeśli planujesz więcej sparingów (cały okres przygotowawczy), skorzystaj z opcji "Dodaj wiele terminów".</p>
          </div>
        </>
      ) : (
      <>
      {/* Calendar view - desktop: controlled by viewMode, mobile: controlled by mobileTab */}
      <div className={`preview-calendar-section ${viewMode !== 'calendar' ? 'desktop-hidden' : ''} ${mobileTab !== 'kalendarz' ? 'mobile-hidden' : 'mobile-visible'}`}>
          <div className="calendar-nav">
            <button className="calendar-nav-btn calendar-nav-btn-prev" onClick={goToPreviousMonth}>
              &larr; Poprzedni
            </button>
            <h3 className="calendar-title">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <button className="calendar-nav-btn calendar-nav-btn-next" onClick={goToNextMonth}>
              Następny &rarr;
            </button>
          </div>

          {isLoadingCalendar ? (
            <div className="loading-spinner">Ładowanie kalendarza...</div>
          ) : (
            <div className="calendar-wrapper">
              <div className="preview-calendar-grid">
                {dayNames.map((day, idx) => (
                  <div key={idx} className="preview-calendar-day-header">
                    {day}
                  </div>
                ))}
                {generateCalendarDays().map((day, idx) => {
                  const entry = getDateEntry(day);
                  const status = entry ? entry.status : null;

                  return (
                    <div
                      key={idx}
                      className={`preview-calendar-day ${!day ? 'empty' : ''}`}
                    >
                      {day && (
                        <>
                          <span className="day-number">{day}</span>
                          {status === 'APPROVED_GAMES_EXIST' && entry.games && entry.games.map((game, gi) => (
                            <div className="preview-dot-container" key={gi}>
                              {game.logoUrl && (
                                <img
                                  src={game.logoUrl}
                                  alt="Logo przeciwnika"
                                  className="preview-calendar-logo"
                                />
                              )}
                              <span className="preview-calendar-time">
                                {game.startTime ? game.startTime.substring(0, 5) : 'Do ustalenia'}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

      {/* Operational view - info-actions-row: desktop: always in operational, mobile: ustawienia tab */}
      <div className={`info-actions-row ${viewMode !== 'operational' ? 'desktop-hidden' : ''} ${mobileTab !== 'ustawienia' ? 'mobile-hidden' : 'mobile-visible'}`}>
        <div className="info-group">
          {!isYearRound && (
            <>
              <h1 className="preseason-summary-title">
                Okres przygotowawczy {formatDate(preseasonStartDate)} - {formatDate(preseasonEndDate)}
              </h1>
              <p className="preseason-games-count">Ilość zaplanowanych gier: {games.length}</p>
            </>
          )}
        </div>

        <div className="preseason-actions-bar">
          <button className="btn-add-schedule" onClick={handleAddTermClick}>
            Dodaj termin
          </button>
          <button className="btn-add-schedule btn-add-bulk" onClick={handleBulkTermClick}>
            Dodaj wiele terminów
          </button>
          {currentGames.length > 0 && (
            <button className="btn-edit-term" onClick={() => handleEditTermClick(currentGames[0])}>
              Edytuj termin
            </button>
          )}
          {currentGames.length > 0 && (
            <button className="btn-delete-term" onClick={() => handleDeleteTermClick(currentGames[0])}>
              Usuń termin
            </button>
          )}
          <button className="btn-add-schedule" onClick={handleOpenShareInvitationModal}>
            Generuj zaproszenia
          </button>
        </div>
      </div>

      {/* Sparingi content - desktop: operational view, mobile: sparingi tab */}
      <div className={`mobile-tab-content ${viewMode !== 'operational' ? 'desktop-hidden' : ''} ${mobileTab !== 'sparingi' ? 'mobile-hidden' : 'mobile-visible'}`}>
      {games.length === 0 ? (
        <div className="no-games-message">
          {isYearRound ? 'Brak zaplanowanych sparingów' : 'Brak zaplanowanych sparingów w okresie przygotowawczym'}
        </div>
      ) : (
        <>
          <div className="term-content-wrapper">
            {/* Sidebar z lewej */}
            <div className="sidebar-column">
              <div className="sidebar-row">
                <div className="proposals-sidebar">
                  <div className="proposals-sidebar-header">Zapytania na termin</div>
                  <div className="proposals-sidebar-content">
                    <div className="proposal-sidebar-item">
                      <div className="proposal-sidebar-info">
                        <span className="proposal-sidebar-label">Otrzymane:</span>
                        <span className="proposal-sidebar-value">
                          {currentGames.reduce((sum, g) => sum + (g.amountOfReceivedProposals || 0), 0)}
                        </span>
                      </div>
                        <div className="proposal-sidebar-action">
                          <button
                            className="show-proposals-sidebar-btn"
                            onClick={() => {
                              const scheduleId = currentGames[0]?.termId;
                              navigate('/sports-club-received-matches', {
                                state: {
                                  termId: scheduleId,
                                  teamId: activeTeamId
                                }
                              });
                            }}
                          >
                            Pokaż
                          </button>
                        </div>
                    </div>
                    <div className="proposal-sidebar-item">
                      <div className="proposal-sidebar-info">
                        <span className="proposal-sidebar-label">Wysłane:</span>
                        <span className="proposal-sidebar-value">
                          {currentGames.reduce((sum, g) => sum + (g.amountOfCreatedProposals || 0), 0)}
                        </span>
                      </div>
                        <div className="proposal-sidebar-action">
                          <button
                            className="show-proposals-sidebar-btn"
                            onClick={() => {
                              const scheduleId = currentGames[0]?.termId;
                              navigate('/sports-club-matches', {
                                state: {
                                  termId: scheduleId,
                                  teamId: activeTeamId
                                }
                              });
                            }}
                          >
                            Pokaż
                          </button>
                        </div>
                    </div>
                  </div>
                </div>
                {currentGames.length > 0 && (
                  <div className="term-actions-container">
                    <button
                      className="add-game-in-term-sidebar-btn"
                      onClick={() => handleOpenManualOpponentModal(currentGames[0])}
                    >
                      Dodaj przeciwnika spoza aplikacji
                    </button>
                    <button
                      className="add-game-in-term-sidebar-btn"
                      onClick={() => handleAddGameInTermClick(currentGames[0])}
                    >
                      Dodaj mecz w terminie
                    </button>
                    <button
                      className="block-hours-sidebar-btn"
                      onClick={() => handleBlockHoursClick(currentGames[0])}
                    >
                      Zablokuj godziny
                    </button>
                    <button
                      className="remove-game-from-term-sidebar-btn"
                      onClick={() => handleRemoveGameFromTermClick(currentGames[0])}
                    >
                      Usuń nieobsadzony mecz z terminu
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Główna zawartość po prawej */}
            <div className="term-main-content">
              {/* Mobile: przyciski "Dodaj termin" i "Dodaj wiele terminów" */}
              <div className="mobile-year-round-add-term">
                <button className="btn-add-schedule" onClick={handleAddTermClick}>
                  Dodaj termin
                </button>
                <button className="btn-add-schedule btn-add-bulk" onClick={handleBulkTermClick}>
                  Dodaj wiele terminów
                </button>
              </div>

              {/* Nawigator przedziałów dat */}
              {dateRangeGroups.length > 0 && (
                <div className="date-range-navigator">
                  <button
                    className="nav-btn nav-btn-prev"
                    onClick={handlePreviousDateRange}
                    disabled={currentDateRangeIndex === 0}
                  >
                    &larr; Poprzedni
                  </button>
                  <div className="date-range-display">
                    <span className="date-range-label">Termin {currentDateRangeIndex + 1} z {dateRangeGroups.length}</span>
                    <span className="date-range-dates">
                      {formatDate(currentGroup?.startDate)} - {formatDate(currentGroup?.endDate)}
                    </span>
                    <span className="date-range-games-count">
                      ({currentGames.reduce((sum, g) => sum + (g.amountOfGames || 1), 0)} {currentGames.reduce((sum, g) => sum + (g.amountOfGames || 1), 0) === 1 ? 'mecz' : 'mecze'})
                    </span>
                  </div>
                  <button
                    className="nav-btn nav-btn-next"
                    onClick={handleNextDateRange}
                    disabled={currentDateRangeIndex === dateRangeGroups.length - 1}
                  >
                    Następny &rarr;
                  </button>
                </div>
              )}

              {/* Mobile: przycisk "Zarządzaj terminem" z dropdownem */}
              {currentGames.length > 0 && (
                <div className="mobile-manage-term-wrapper">
                  <button
                    className="mobile-manage-term-btn"
                    onClick={() => setMobileTermMenuOpen(!mobileTermMenuOpen)}
                  >
                    Zarządzaj terminem
                  </button>
                  {mobileTermMenuOpen && (
                    <>
                      <div className="mobile-term-menu-backdrop" onClick={() => setMobileTermMenuOpen(false)} />
                      <div className="mobile-term-menu-dropdown">
                        <button
                          className="mobile-term-menu-item"
                          onClick={() => { handleOpenManualOpponentModal(currentGames[0]); setMobileTermMenuOpen(false); }}
                        >
                          Dodaj przeciwnika spoza aplikacji
                        </button>
                        <button
                          className="mobile-term-menu-item"
                          onClick={() => { handleAddGameInTermClick(currentGames[0]); setMobileTermMenuOpen(false); }}
                        >
                          Dodaj mecz w terminie
                        </button>
                        <button
                          className="mobile-term-menu-item"
                          onClick={() => { handleBlockHoursClick(currentGames[0]); setMobileTermMenuOpen(false); }}
                        >
                          Zablokuj godziny
                        </button>
                        <button
                          className="mobile-term-menu-item"
                          onClick={() => { handleEditTermClick(currentGames[0]); setMobileTermMenuOpen(false); }}
                        >
                          Edytuj termin
                        </button>
                        <button
                          className="mobile-term-menu-item mobile-term-menu-item--danger"
                          onClick={() => { handleRemoveGameFromTermClick(currentGames[0]); setMobileTermMenuOpen(false); }}
                        >
                          Usuń nieobsadzony mecz z terminu
                        </button>
                        <button
                          className="mobile-term-menu-item mobile-term-menu-item--danger"
                          onClick={() => { handleDeleteTermClick(currentGames[0]); setMobileTermMenuOpen(false); }}
                        >
                          Usuń termin
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Kontener na kafelki gier */}
              <div className="games-tiles-container">
                {currentGames.map((schedule, scheduleIndex) => {
                const allAgreedGames = schedule.agreedGames || [];
                const agreedGames = allAgreedGames.filter(g =>
                  !(g.status === 'PENDING_DATE_TIME_PROPOSAL' && !g.proposedDate && !g.proposedStartTime && !g.proposedEndTime)
                );
                const maxGames = schedule.amountOfGames || 1;
                const emptySlots = Math.max(0, maxGames - agreedGames.length);

                return (
                  <div key={schedule.termId || scheduleIndex} className="schedule-group">
                    {/* Wyświetl ustalone gry */}
                    {agreedGames.map((agreedGame, gameIndex) => (
                      <div key={agreedGame.preseasonGameId || `agreed-${gameIndex}`} className="game-card game-card-confirmed">
                        <div className="game-card-header">
                          <h3>Mecz {gameIndex + 1}</h3>
                        </div>
                        <div className="game-card-body">
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0 2px' }}>
                            {agreedGame.logoUrl ? (
                              <img
                                src={agreedGame.logoUrl}
                                alt={`Logo ${agreedGame.opponentTeamName}`}
                                style={{
                                  width: '72px',
                                  height: '72px',
                                  objectFit: 'contain'
                                }}
                              />
                            ) : (
                              <div className="game-card-logo-placeholder" />
                            )}
                          </div>
                          <h4 className="game-opponent-name">
                            {agreedGame.opponentTeamName}
                            {agreedGame.isManualOpponent && (
                              <span className="manual-opponent-badge"> (spoza aplikacji)</span>
                            )}
                          </h4>
                          {(agreedGame.opponentTier || agreedGame.manualOpponentTier) && (
                            <div className="game-info-row">
                              <span className="game-label">Poziom rozgrywkowy:</span>
                              <span className="game-value">{agreedGame.opponentTier || {
                                'EKSTRAKLASA': 'Ekstraklasa', 'FIRST_LEAGUE': 'I Liga', 'SECOND_LEAGUE': 'II Liga',
                                'THIRD_LEAGUE': 'III Liga', 'FOURTH_LEAGUE': 'IV Liga', 'FIFTH_LEAGUE': 'V Liga',
                                'REGIONAL_CLASS': 'Klasa Okręgowa', 'CLASS_A': 'Klasa A', 'CLASS_B': 'Klasa B', 'CLASS_C': 'Klasa C'
                              }[agreedGame.manualOpponentTier] || agreedGame.manualOpponentTier}</span>
                            </div>
                          )}
                          {/* Scenariusz B: agreed + proposed = propozycja zmiany terminu */}
                          {agreedGame.agreedDate && agreedGame.proposedDate && agreedGame.proposedStartTime && agreedGame.proposedEndTime ? (
                            <>
                              <div className="game-info-row">
                                <span className="game-label">Obecny termin:</span>
                                <span className="game-value">
                                  {formatDate(agreedGame.agreedDate)}, {formatTime(agreedGame.agreedStartTime)} - {formatTime(agreedGame.agreedEndTime)}
                                </span>
                              </div>
                              <div className="game-info-row">
                                <span className="game-label">
                                  {agreedGame.isOwnProposal ? 'Twoja propozycja zmiany:' : 'Otrzymana propozycja zmiany:'}
                                </span>
                                <span className="game-value">
                                  {formatDate(agreedGame.proposedDate)}, {formatTime(agreedGame.proposedStartTime)} - {formatTime(agreedGame.proposedEndTime)}
                                  </span>
                              </div>
                            </>
                          ) : /* Scenariusz C: agreedStartTime/EndTime bez agreedDate + proposed = pierwsza propozycja terminu */
                          agreedGame.agreedStartTime && agreedGame.agreedEndTime && !agreedGame.agreedDate && agreedGame.proposedDate && agreedGame.proposedStartTime && agreedGame.proposedEndTime ? (
                            <div className="game-info-row">
                              <span className="game-label">
                                {agreedGame.isOwnProposal ? 'Twoja propozycja terminu:' : 'Otrzymana propozycja zmiany:'}
                              </span>
                              <span className="game-value">
                                {formatDate(agreedGame.proposedDate)}, {formatTime(agreedGame.proposedStartTime)} - {formatTime(agreedGame.proposedEndTime)}
                              </span>
                            </div>
                          ) : /* Scenariusz D: brak agreed, ale proposal wypełnione = wysłana/otrzymana propozycja terminu */
                          !agreedGame.agreedDate && !agreedGame.agreedStartTime && !agreedGame.agreedEndTime && agreedGame.proposedDate && agreedGame.proposedStartTime && agreedGame.proposedEndTime ? (
                            <div className="game-info-row">
                              <span className="game-label">
                                {agreedGame.isOwnProposal ? 'Twoja propozycja terminu:' : 'Otrzymana propozycja zmiany:'}
                              </span>
                              <span className="game-value">
                                {formatDate(agreedGame.proposedDate)}, {formatTime(agreedGame.proposedStartTime)} - {formatTime(agreedGame.proposedEndTime)}
                              </span>
                            </div>
                          ) : /* Scenariusz A: agreed bez proposed = wyswietl normalnie */
                          agreedGame.agreedDate ? (
                            <>
                              <div className="game-info-row">
                                <span className="game-label">Data meczu:</span>
                                <span className="game-value">{formatDate(agreedGame.agreedDate)}</span>
                              </div>
                              <div className="game-info-row">
                                <span className="game-label">Godzina:</span>
                                {agreedGame.agreedStartTime && agreedGame.agreedEndTime ? (
                                  <span className="game-value">
                                    {formatTime(agreedGame.agreedStartTime)} - {formatTime(agreedGame.agreedEndTime)}
                                  </span>
                                ) : (
                                  <span className="game-value game-value-pending">nie ustalono</span>
                                )}
                              </div>
                            </>
                          ) : agreedGame.agreedStartTime && agreedGame.agreedEndTime ? (
                            <div className="game-info-row">
                              <span className="game-label">Godzina:</span>
                              <span className="game-value">
                                {formatTime(agreedGame.agreedStartTime)} - {formatTime(agreedGame.agreedEndTime)}
                              </span>
                            </div>
                          ) : agreedGame.status === 'APPROVED_WITH_TIME_TO_CONFIRM' ? (
                            <>
                              <div className="game-info-row">
                                <span className="game-label">Data meczu:</span>
                                <span className="game-value game-value-pending">nie ustalono</span>
                              </div>
                              <div className="game-info-row">
                                <span className="game-label">Godzina:</span>
                                <span className="game-value game-value-pending">nie ustalono</span>
                              </div>
                            </>
                          ) : null}
                          {agreedGame.location ? (
                            <div className="game-info-row">
                              <span className="game-label">Boisko:</span>
                              <span className="game-value">{agreedGame.location}</span>
                            </div>
                          ) : agreedGame.status === 'APPROVED_WITH_TIME_TO_CONFIRM' ? (
                            <div className="game-info-row">
                              <span className="game-label">Boisko:</span>
                              <span className="game-value game-value-pending">nie ustalono</span>
                            </div>
                          ) : null}
                          {agreedGame.scoreCreator != null && agreedGame.scoreOpponent != null && (() => {
                            const myScore = agreedGame.scoreCreator;
                            const theirScore = agreedGame.scoreOpponent;
                            const resultLabel = myScore > theirScore ? 'Zwycięstwo' : myScore < theirScore ? 'Porażka' : 'Remis';
                            const resultClass = myScore > theirScore ? 'score-win' : myScore < theirScore ? 'score-loss' : 'score-draw';
                            return (
                              <div className="game-info-row game-score-row">
                                <span className="game-label">Wynik:</span>
                                <span className="game-score-right">
                                  <span className={`score-result-badge ${resultClass}`}>{resultLabel}</span>
                                  <span className="game-value game-value-score">{myScore} : {theirScore}</span>
                                </span>
                              </div>
                            );
                          })()}
                          <div className="game-card-bottom">
                            {agreedGame.status === 'APPROVED' && (
                              <button
                                className="btn-add-score"
                                onClick={() => handleOpenScoreModal(agreedGame)}
                              >
                                {agreedGame.scoreCreator != null ? 'Edytuj wynik' : 'Dodaj wynik'}
                              </button>
                            )}
                            {agreedGame.status === 'APPROVED' && !agreedGame.isManualOpponent && (
                              <button
                                className="btn-propose-time"
                                onClick={() => handleOpenProposeTimeModal(agreedGame, schedule)}
                              >
                                Zaproponuj zmianę terminu
                              </button>
                            )}
                            {agreedGame.status === 'APPROVED_WITH_TIME_TO_CONFIRM' && !agreedGame.isManualOpponent && (
                              <button
                                className="btn-propose-time"
                                onClick={() => handleOpenProposeTimeModal(agreedGame, schedule)}
                              >
                                Zaproponuj termin
                              </button>
                            )}
                            <div className="game-card-actions">
                              {!agreedGame.isManualOpponent && (
                                <button
                                  className="btn-messages"
                                  onClick={() => handleOpenMessagesModal(agreedGame)}
                                >
                                  Wiadomości
                                </button>
                              )}
                              <button
                                className="btn-cancel-game"
                                onClick={() => handleCancelGameClick(agreedGame)}
                              >
                                Odwołaj mecz
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Wyświetl puste sloty */}
                    {Array.from({ length: emptySlots }).map((_, slotIndex) => (
                      <div key={`empty-${scheduleIndex}-${slotIndex}`} className="game-card game-card-empty">
                        <div className="game-card-header game-card-header-empty">
                          <h3>Mecz {agreedGames.length + slotIndex + 1}</h3>
                        </div>
                        <div className="game-card-body">
                          {/* Placeholder na logo - puste dla empty card */}
                          <div className="game-card-logo-placeholder" />
                          {/* Nagłówek */}
                          <h4 className="game-opponent-name">Brak przeciwnika</h4>
                          {/* Szczegóły - wymagania dot. przeciwnika */}
                          {((schedule.tiers && schedule.tiers.length > 0) || (schedule.yearOfBirths && schedule.yearOfBirths.length > 0)) && (
                            <div className="required-opponent-section">
                              <div className="required-opponent-header">
                                <span className="game-label">Wymagany przeciwnik:</span>
                                <button
                                  className="btn-edit-opponent"
                                  onClick={() => handleEditOpponentClick(schedule)}
                                >
                                  Edytuj
                                </button>
                              </div>

                              <div className="opponent-categories-row">
                                {schedule.tiers && schedule.tiers.filter(t => t.teamType === 'SENIOR').length > 0 && (
                                  <div className="opponent-category">
                                    <span className="category-title">Seniorzy</span>
                                    <div className="category-detail">
                                      <span className="detail-label">Poziom rozgrywkowy:</span>
                                      <span className="detail-value detail-value-tiers">
                                        {schedule.tiers
                                          .filter(t => t.teamType === 'SENIOR')
                                          .map(t => t.displayValue || t.value)
                                          .join(', ')}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* Junior tier display in schedule summary - hidden for seniors-only mode */}
                              </div>
                            </div>
                          )}
                          {(!schedule.tiers || schedule.tiers.length === 0) && (!schedule.yearOfBirths || schedule.yearOfBirths.length === 0) && (
                            <div className="required-opponent-section">
                              <div className="required-opponent-header">
                                <span className="game-label">Wymagany przeciwnik:</span>
                                <button
                                  className="btn-edit-opponent"
                                  onClick={() => handleEditOpponentClick(schedule)}
                                >
                                  Edytuj
                                </button>
                              </div>
                              <p className="no-opponent-defined">Nie zdefiniowano wymagań</p>
                            </div>
                          )}
                          {/* Sekcja przycisków */}
                          <div className="game-card-bottom">
                            <div className="no-opponent-buttons">
                              <button
                                className="btn-find-opponent"
                                onClick={() => handleOpenFindOpponentModal(schedule)}
                              >
                                Szukaj przeciwnika
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
      </>
      )}

      {/* Modal potwierdzenia usunięcia */}
      {deleteConfirmModal.isOpen && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Potwierdzenie usunięcia</h2>
              <button className="modal-close-btn" onClick={handleDeleteCancel}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Czy na pewno chcesz usunąć termin {formatDate(deleteConfirmModal.game?.approximateStartDate)} - {formatDate(deleteConfirmModal.game?.approximateEndDate)}?</p>
              <p style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888' }}>Operacja nie jest możliwa jeśli istnieją propozycje lub potwierdzeni przeciwnicy.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Nie
              </button>
              <button
                className="btn-delete-confirm"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Usuwanie...' : 'Tak'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia dodania meczu w terminie */}
      {addGameInTermModal.isOpen && (
        <div className="modal-overlay" onClick={() => setAddGameInTermModal({ isOpen: false, schedule: null })}>
          <div className="modal-content modal-add-game-in-term" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dodaj mecz w terminie</h2>
              <button className="modal-close-btn" onClick={() => setAddGameInTermModal({ isOpen: false, schedule: null })}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Czy na pewno chcesz dodać kolejny mecz w terminie {formatDate(addGameInTermModal.schedule?.approximateStartDate)} - {formatDate(addGameInTermModal.schedule?.approximateEndDate)}?</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setAddGameInTermModal({ isOpen: false, schedule: null })}
                disabled={isAddingGameInTerm}
              >
                Nie
              </button>
              <button
                className="btn-save"
                onClick={handleAddGameInTermConfirm}
                disabled={isAddingGameInTerm}
              >
                {isAddingGameInTerm ? 'Dodawanie...' : 'Tak'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia meczu z terminu */}
      {removeGameFromTermModal.isOpen && (
        <div className="modal-overlay" onClick={() => setRemoveGameFromTermModal({ isOpen: false, schedule: null })}>
          <div className="modal-content modal-add-game-in-term" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Usuń nieobsadzony mecz z terminu</h2>
              <button className="modal-close-btn" onClick={() => setRemoveGameFromTermModal({ isOpen: false, schedule: null })}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Czy na pewno chcesz usunąć pojedynczy nieobsadzony mecz z terminu {formatDate(removeGameFromTermModal.schedule?.approximateStartDate)} - {formatDate(removeGameFromTermModal.schedule?.approximateEndDate)}?</p>
              <p style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888' }}>Operacja nie jest możliwa jeśli istnieją aktywne zapytania na ten termin.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setRemoveGameFromTermModal({ isOpen: false, schedule: null })}
                disabled={isRemovingGameFromTerm}
              >
                Nie
              </button>
              <button
                className="btn-delete-confirm"
                onClick={handleRemoveGameFromTermConfirm}
                disabled={isRemovingGameFromTerm}
              >
                {isRemovingGameFromTerm ? 'Usuwanie...' : 'Tak'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal dodawania terminu */}
      {addTermModal.isOpen && (
        <div className="modal-overlay" onClick={handleAddTermCancel}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dodaj termin</h2>
              <button className="modal-close-btn" onClick={handleAddTermCancel}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="add-term-start">Data od</label>
                <input
                  type="date"
                  id="add-term-start"
                  className="form-input"
                  value={addTermData.startDate}
                  min={isYearRound ? undefined : (addTermSeasonDef?.minStartDate || undefined)}
                  max={isYearRound ? (addTermData.endDate || undefined) : (addTermData.endDate || addTermSeasonDef?.maxEndDate || undefined)}
                  onChange={(e) => handleAddTermChange('startDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="add-term-end">Data do</label>
                <input
                  type="date"
                  id="add-term-end"
                  className="form-input"
                  value={addTermData.endDate}
                  min={addTermData.startDate || undefined}
                  max={isYearRound ? undefined : (addTermSeasonDef?.maxEndDate || undefined)}
                  onChange={(e) => handleAddTermChange('endDate', e.target.value)}
                />
              </div>
              {/* Sekcja "Z kim chcesz zagrać" */}
              <div className="form-group opponent-type-section">
                <label className="section-label-bold">Z kim chcesz zagrać?</label>
                <div className="opponent-type-checkboxes">
                  <label
                    className="tier-checkbox-label"
                    style={{
                      cursor: teamType === 'YOUTH' && isUnder16 ? 'not-allowed' : 'pointer',
                      opacity: teamType === 'YOUTH' && isUnder16 ? 0.6 : 1
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={addTermData.opponentType?.senior ?? false}
                      onChange={(e) => handleAddTermOpponentTypeChange('senior', e.target.checked)}
                      disabled={teamType === 'YOUTH' && isUnder16}
                    />
                    <span className="tier-checkbox-text">Seniorzy</span>
                  </label>
                  {/* Juniorzy checkbox - hidden for seniors-only mode */}
                </div>
              </div>

              {/* Kontener dla sekcji Seniorów i Juniorów obok siebie */}
              <div className="opponent-sections-container">
                {/* Sekcja Seniorów */}
                {addTermData.opponentType?.senior && (
                  <div className="seniors-filter-section">
                    <label className="section-label">Seniorzy</label>
                    <div className="section-filters-content">
                      <div className="form-group">
                        <label>Poziom rozgrywkowy przeciwnika</label>
                        <div className="tier-checkboxes tier-checkboxes-compact">
                          {seniorTiers
                            .filter(tier => !tier.teamType || tier.teamType === 'SENIOR')
                            .map((tier) => (
                              <label key={tier.key} className="tier-checkbox-label tier-checkbox-label-compact">
                                <input
                                  type="checkbox"
                                  checked={(addTermData.seniorTiers || []).includes(tier.key)}
                                  onChange={(e) => handleTierCheckboxChange(tier.key, e.target.checked)}
                                />
                                <span className="tier-checkbox-text">{tier.displayValue}</span>
                              </label>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sekcja Juniorów - hidden for seniors-only mode */}
              </div>
              {getAddTermValidationErrors().length > 0 && (addTermData.startDate || addTermData.endDate) && (
                <div className="add-term-validation-errors">
                  {getAddTermValidationErrors().map((err, i) => (
                    <p key={i} className="add-term-error-text">{err}</p>
                  ))}
                </div>
              )}
              {addTermError && (
                <div className="error-message-409">
                  {addTermError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleAddTermCancel}
                disabled={isSavingTerm}
              >
                Anuluj
              </button>
              <button
                className="btn-save"
                onClick={handleAddTermConfirm}
                disabled={isSavingTerm || !isAddTermFormValid()}
              >
                {isSavingTerm ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {renderBulkTermModal()}


      {/* Modal edycji terminu (zakres dat) */}
      {editTermModal.isOpen && (
        <div className="modal-overlay" onClick={handleEditTermCancel}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edytuj termin</h2>
              <button className="modal-close-btn" onClick={handleEditTermCancel}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="edit-term-start">Data od</label>
                <input
                  type="date"
                  id="edit-term-start"
                  className="form-input"
                  value={editTermData.startDate}
                  min={isYearRound ? undefined : (editTermSeasonDef?.minStartDate || summaryData?.preseasonStartDate || undefined)}
                  max={isYearRound ? (editTermData.endDate || undefined) : (editTermData.endDate || editTermSeasonDef?.maxEndDate || summaryData?.preseasonEndDate || undefined)}
                  onChange={(e) => setEditTermData(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-term-end">Data do</label>
                <input
                  type="date"
                  id="edit-term-end"
                  className="form-input"
                  value={editTermData.endDate}
                  min={editTermData.startDate || (isYearRound ? undefined : (editTermSeasonDef?.minStartDate || summaryData?.preseasonStartDate || undefined))}
                  max={isYearRound ? undefined : (editTermSeasonDef?.maxEndDate || summaryData?.preseasonEndDate || undefined)}
                  onChange={(e) => setEditTermData(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <p style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888' }}>Operacja nie jest możliwa jeśli istnieją propozycje lub potwierdzeni przeciwnicy.</p>
              {editTermError && (
                <div className="error-message-409">
                  {editTermError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleEditTermCancel}
                disabled={isSavingEditTerm}
              >
                Anuluj
              </button>
              <button
                className="btn-save"
                onClick={handleEditTermConfirm}
                disabled={isSavingEditTerm || !isEditTermFormValid() || !canDeleteTerm(editTermModal.schedule)}
              >
                {isSavingEditTerm ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal edycji wymaganego przeciwnika */}
      {editOpponentModal.isOpen && (
        <div className="modal-overlay" onClick={handleEditOpponentCancel}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edytuj wymaganego przeciwnika</h2>
              <button className="modal-close-btn" onClick={handleEditOpponentCancel}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Sekcja "Z kim chcesz zagrać" */}
              <div className="form-group opponent-type-section">
                <label className="section-label-bold">Z kim chcesz zagrać?</label>
                <div className="opponent-type-checkboxes">
                  <label
                    className="tier-checkbox-label"
                    style={{
                      cursor: teamType === 'YOUTH' && isUnder16 ? 'not-allowed' : 'pointer',
                      opacity: teamType === 'YOUTH' && isUnder16 ? 0.6 : 1
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={editOpponentData.opponentType.senior}
                      onChange={(e) => handleOpponentTypeChange('senior', e.target.checked)}
                      disabled={teamType === 'YOUTH' && isUnder16}
                    />
                    <span className="tier-checkbox-text">Seniorzy</span>
                  </label>
                  {/* Juniorzy checkbox - hidden for seniors-only mode */}
                </div>
              </div>

              {/* Kontener dla sekcji Seniorów i Juniorów obok siebie */}
              <div className="opponent-sections-container">
                {/* Sekcja Seniorów */}
                {editOpponentData.opponentType.senior && (
                  <div className="seniors-filter-section">
                    <label className="section-label">Seniorzy</label>
                    <div className="section-filters-content">
                      <div className="form-group">
                        <label>Poziom rozgrywkowy przeciwnika</label>
                        <div className="tier-checkboxes tier-checkboxes-compact">
                          {seniorTiers
                            .filter(tier => !tier.teamType || tier.teamType === 'SENIOR')
                            .map((tier) => (
                              <label key={tier.key} className="tier-checkbox-label tier-checkbox-label-compact">
                                <input
                                  type="checkbox"
                                  checked={(editOpponentData.opponentTiers || []).includes(tier.key)}
                                  onChange={(e) => handleOpponentTierCheckboxChange(tier.key, e.target.checked)}
                                />
                                <span className="tier-checkbox-text">{tier.displayValue}</span>
                              </label>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sekcja Juniorów - hidden for seniors-only mode */}
              </div>

              {/* Komunikat błędu 409 */}
              {editOpponentError && (
                <div className="error-message-409">
                  {editOpponentError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleEditOpponentCancel}
                disabled={isSavingOpponent}
              >
                Zamknij
              </button>
              <button
                className="btn-save"
                onClick={handleEditOpponentConfirm}
                disabled={isSavingOpponent || !isEditOpponentFormValid()}
              >
                {isSavingOpponent ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal wiadomości */}
      {messagesModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseMessagesModal}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Wiadomości</h2>
              <button className="modal-close-btn" onClick={handleCloseMessagesModal}>&times;</button>
            </div>
            <div className="modal-body">
              {messagesLoading ? (
                <div className="loading-spinner">Ładowanie wiadomości...</div>
              ) : (
                <>
                  {/* Nagłówek z logami drużyn */}
                  {gameDetails && (
                    <>
                      <div className="details-teams-row">
                        <div className="details-team-column">
                          {gameDetails.teamCreatorId === activeTeamId ? (
                            <>
                              {gameDetails.creatorLogoUrl && (
                                <div className="details-team-logo">
                                  <img src={gameDetails.creatorLogoUrl} alt="Logo" />
                                </div>
                              )}
                              <p className="details-team-name">{gameDetails.teamCreatorName || 'Brak danych'}</p>
                              {gameDetails.teamCreatorTier && (
                                <p className="details-team-tier">{gameDetails.teamCreatorTier}</p>
                              )}
                            </>
                          ) : (
                            <>
                              {gameDetails.opponentLogoUrl && (
                                <div className="details-team-logo">
                                  <img src={gameDetails.opponentLogoUrl} alt="Logo" />
                                </div>
                              )}
                              <p className="details-team-name">{gameDetails.teamOpponentName || 'Brak danych'}</p>
                              {gameDetails.teamOpponentTier && (
                                <p className="details-team-tier">{gameDetails.teamOpponentTier}</p>
                              )}
                            </>
                          )}
                        </div>

                        <div className="details-team-column">
                          {gameDetails.teamCreatorId === activeTeamId ? (
                            <>
                              {gameDetails.opponentLogoUrl && (
                                <div className="details-team-logo">
                                  <img src={gameDetails.opponentLogoUrl} alt="Logo" />
                                </div>
                              )}
                              <p className="details-team-name">{gameDetails.teamOpponentName || 'Brak danych'}</p>
                              {gameDetails.teamOpponentTier && (
                                <p className="details-team-tier">{gameDetails.teamOpponentTier}</p>
                              )}
                            </>
                          ) : (
                            <>
                              {gameDetails.creatorLogoUrl && (
                                <div className="details-team-logo">
                                  <img src={gameDetails.creatorLogoUrl} alt="Logo" />
                                </div>
                              )}
                              <p className="details-team-name">{gameDetails.teamCreatorName || 'Brak danych'}</p>
                              {gameDetails.teamCreatorTier && (
                                <p className="details-team-tier">{gameDetails.teamCreatorTier}</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <h5 className="messages-details-title">Szczegóły</h5>
                      <hr className="messages-divider" />
                      <div className="messages-game-details">
                        {gameDetails.opponentScheduleStartDate && gameDetails.opponentScheduleEndDate && (
                          <div className="game-detail-row">
                            <span className="game-detail-label">Propozycja na termin:</span>
                            <span className="game-detail-value">
                              {new Date(gameDetails.opponentScheduleStartDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              {' - '}
                              {new Date(gameDetails.opponentScheduleEndDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          </div>
                        )}
                        <div className="game-detail-row">
                          <span className="game-detail-label">Data meczu:</span>
                          <span className="game-detail-value">{formatDate(gameDetails.date)}</span>
                        </div>
                        <div className="game-detail-row">
                          <span className="game-detail-label">Godzina:</span>
                          <span className="game-detail-value">
                            {formatTime(gameDetails.startTime)} - {formatTime(gameDetails.endTime)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Lista wiadomości */}
                  <div className="messages-list-container">
                    <h4>Historia wiadomości</h4>
                    {messages.length > 0 ? (
                      <div className="messages-list">
                        {messages.map((message) => {
                          const isOwnMessage = message.senderId === activeTeamId;
                          const senderName = isOwnMessage
                            ? (gameDetails?.teamCreatorId === activeTeamId ? gameDetails?.sportsClubCreatorName : gameDetails?.sportsClubOpponentName)
                            : (gameDetails?.teamCreatorId === activeTeamId ? gameDetails?.sportsClubOpponentName : gameDetails?.sportsClubCreatorName);
                          return (
                            <div
                              key={message.id}
                              className={`message-item ${isOwnMessage ? 'message-own' : 'message-other'}`}
                            >
                              <div className="message-content">
                                <div className="message-sender">{senderName}</div>
                                <div className="message-text">{message.message}</div>
                                {message.createdAt && (
                                  <div className="message-timestamp">
                                    {new Date(message.createdAt + (message.createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="no-messages">Brak wiadomości</div>
                    )}
                  </div>

                  {/* Formularz odpowiedzi */}
                  <div className="reply-section">
                    <h4>Wyślij wiadomość</h4>
                    <div className="reply-form">
                      <textarea
                        className="reply-textarea"
                        placeholder="Wpisz swoją wiadomość..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={3}
                        maxLength={800}
                      />
                      <button
                        className="reply-send-button"
                        onClick={handleSendMessage}
                        disabled={sendingReply || !replyMessage.trim()}
                      >
                        {sendingReply ? 'Wysyłanie...' : 'Wyślij'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCloseMessagesModal}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal anulowania sparingu */}
      {cancelGameModal.isOpen && (
        <div className="modal-overlay" onClick={handleCancelGameModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Anulowanie sparingu</h2>
              <button className="modal-close-btn" onClick={handleCancelGameModalClose}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Czy na pewno chcesz anulować sparing z <strong>{cancelGameModal.game?.opponentTeamName}</strong>?</p>
              <div className="form-group">
                <label htmlFor="cancel-game-reason">Powód anulowania <span style={{ color: 'red' }}>*</span></label>
                <textarea
                  id="cancel-game-reason"
                  className="form-textarea"
                  placeholder="Podaj powód anulowania sparingu..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  maxLength={800}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCancelGameModalClose}
                disabled={isCancellingGame}
              >
                Anuluj
              </button>
              <button
                className="btn-delete-confirm"
                onClick={handleCancelGameConfirm}
                disabled={isCancellingGame || !cancelReason.trim()}
              >
                {isCancellingGame ? 'Anulowanie...' : 'Potwierdź usunięcie'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Dodaj/Edytuj wynik */}
      {scoreModal.isOpen && (
        <div className="modal-overlay" onClick={() => setScoreModal({ isOpen: false, game: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '380px' }}>
            <div className="modal-header">
              <h2>{scoreModal.game?.scoreCreator != null ? 'Edytuj wynik' : 'Dodaj wynik'}</h2>
              <button className="modal-close-btn" onClick={() => setScoreModal({ isOpen: false, game: null })}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', margin: '16px 0' }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#333', display: 'block', marginBottom: '6px' }}>
                    {isCoach
                      ? (reduxTeams.find(t => t.id === activeTeamId)?.teamName || 'Twoja drużyna')
                      : (teams.find(t => t.teamId === activeTeamId)?.name || 'Twoja drużyna')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={scoreData.scoreMyTeam}
                    onChange={(e) => setScoreData(prev => ({ ...prev, scoreMyTeam: e.target.value }))}
                    style={{ width: '70px', textAlign: 'center', fontSize: '1.4rem', fontWeight: 700, padding: '8px', borderRadius: '8px', border: '2px solid #c8e6c9' }}
                  />
                </div>
                <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#666', paddingTop: '22px' }}>:</span>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#333', display: 'block', marginBottom: '6px' }}>
                    {scoreModal.game?.isManualOpponent
                      ? (scoreModal.game?.manualOpponentName || 'Przeciwnik')
                      : (scoreModal.game?.opponentTeamName || 'Przeciwnik')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={scoreData.scoreOpponentTeam}
                    onChange={(e) => setScoreData(prev => ({ ...prev, scoreOpponentTeam: e.target.value }))}
                    style={{ width: '70px', textAlign: 'center', fontSize: '1.4rem', fontWeight: 700, padding: '8px', borderRadius: '8px', border: '2px solid #c8e6c9' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setScoreModal({ isOpen: false, game: null })} disabled={isSavingScore}>
                Anuluj
              </button>
              <button className="btn-confirm" onClick={handleSaveScore} disabled={isSavingScore || scoreData.scoreMyTeam === '' || scoreData.scoreOpponentTeam === ''}>
                {isSavingScore ? 'Zapisywanie...' : 'Zapisz wynik'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Znajdź - Wizard szukania przeciwnika */}
      {findOpponentModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseFindOpponentModal}>
          <div className="modal-content quick-action-modal wizard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Znajdź przeciwnika - Krok {findOpponentStep} z 4
              </h2>
              <button type="button" className="modal-close" onClick={handleCloseFindOpponentModal}>×</button>
            </div>

            {/* Progress bar */}
            <div className="wizard-progress">
              <div className={`wizard-step ${findOpponentStep >= 1 ? 'active' : ''} ${findOpponentStep > 1 ? 'completed' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Twoja drużyna</div>
              </div>
              <div className="wizard-line"></div>
              {!findOpponentLastMinuteMode && (
                <>
                  <div className={`wizard-step ${findOpponentStep >= 2 ? 'active' : ''} ${findOpponentStep > 2 ? 'completed' : ''}`}>
                    <div className="step-number">2</div>
                    <div className="step-label">Przeciwna drużyna</div>
                  </div>
                  <div className="wizard-line"></div>
                </>
              )}
              <div className={`wizard-step ${findOpponentStep >= 3 ? 'active' : ''} ${findOpponentStep > 3 ? 'completed' : ''}`}>
                <div className="step-number">{findOpponentLastMinuteMode ? 2 : 3}</div>
                <div className="step-label">Data i godzina</div>
              </div>
              <div className="wizard-line"></div>
              <div className={`wizard-step ${findOpponentStep >= 4 ? 'active' : ''}`}>
                <div className="step-number">{findOpponentLastMinuteMode ? 3 : 4}</div>
                <div className="step-label">Podsumowanie</div>
              </div>
            </div>

            <form
              onSubmit={handleSendFindOpponentProposal}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && findOpponentStep !== 4) {
                  e.preventDefault();
                }
              }}
            >
              <div className="modal-body">
                {/* KROK 1: Potwierdź dane */}
                {findOpponentStep === 1 && (
                  <div className="wizard-step-content">
                    <h3>Potwierdź dane</h3>

                    {/* Wyświetlenie wybranej drużyny - kontekst z navbara */}
                    <div className="form-group">
                      <label>Twoja drużyna</label>
                      <div className="selected-team-info">
                        <strong>
                          {isCoach
                            ? (reduxTeams.find(t => t.id === activeTeamId)?.teamName || `Drużyna #${activeTeamId}`)
                            : (teams.find(t => t.teamId === activeTeamId)?.name || `Drużyna #${activeTeamId}`)
                          }
                        </strong>
                        {(() => {
                          const teamTier = isCoach
                            ? reduxTeams.find(t => t.id === activeTeamId)?.tier
                            : teams.find(t => t.teamId === activeTeamId)?.tier;
                          if (!teamTier) return null;
                          const allTiers = [...seniorTiers, ...youthTiers];
                          const tierDisplay = allTiers.find(t => t.key === teamTier)?.displayValue || teamTier;
                          return <div className="selected-team-tier">{tierDisplay}</div>;
                        })()}
                      </div>
                    </div>

                    {/* Wyświetlenie wybranego terminu (z schedule) */}
                    <div className="form-group">
                      <label>Wybrany termin</label>
                      <div className="selected-schedule-info">
                        <strong>
                          {findOpponentModal.schedule?.approximateStartDate && findOpponentModal.schedule?.approximateEndDate ? (
                            <>
                              {new Date(findOpponentModal.schedule.approximateStartDate).toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' })}
                              {' - '}
                              {new Date(findOpponentModal.schedule.approximateEndDate).toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' })}
                            </>
                          ) : 'Nie określono'}
                        </strong>
                        <span className="schedule-days-count">
                          ({findOpponentScheduleDates.length} {findOpponentScheduleDates.length === 1 ? 'dzień' : 'dni'})
                        </span>
                      </div>
                    </div>

                    {findOpponentModal.schedule?.isAtCamp && (
                      <div className="camp-info-banner">
                         Drużyna jest na obozie w tym terminie
                      </div>
                    )}

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={findOpponentHasPitchAvailable}
                          onChange={(e) => setFindOpponentHasPitchAvailable(e.target.checked)}
                        />
                        Mamy dostępne boisko
                      </label>
                    </div>

                    {findOpponentHasPitchAvailable && (
                      <div className="form-group" style={{ textAlign: 'center' }}>
                        <label>Lokalizacja boiska *</label>
                        {findOpponentPitchLatitude ? (
                          <div className="map-location-selected">
                            <span className="map-location-address">{findOpponentLocation}</span>
                            <div className="map-location-actions">
                              <button type="button" className="map-location-btn-change" onClick={() => setFindOpponentMapPickerOpen(true)}>Zmień</button>
                              <button type="button" className="map-location-btn-remove" onClick={handleFindOpponentClearLocation}>Usuń</button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" className="map-location-btn-pick" onClick={() => setFindOpponentMapPickerOpen(true)}>
                            Wybierz lokalizację na mapie
                          </button>
                        )}
                      </div>
                    )}

                    {(() => {
                      const scheduleStartDate = findOpponentModal.schedule?.approximateStartDate;
                      const isExpressModeAvailable = expressModeEnabled && scheduleStartDate && (() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const start = new Date(scheduleStartDate + 'T00:00:00');
                        const diffDays = Math.ceil((start - today) / (1000 * 60 * 60 * 24));
                        return diffDays <= expressModeThreshold;
                      })();
                      const isDisabled = !isExpressModeAvailable;
                      return (
                        <div className="form-group">
                          <label className={`checkbox-label${isDisabled ? ' checkbox-label-disabled' : ''}`}>
                            <input
                              type="checkbox"
                              checked={findOpponentLastMinuteMode}
                              onChange={(e) => setFindOpponentLastMinuteMode(e.target.checked)}
                              disabled={isDisabled}
                            />
                            Tryb ekspresowy
                            <span
                              className="last-minute-tooltip-trigger"
                              title={`Powiadomienia o poszukiwaniu sparingu zostaną wysłane do wszystkich drużyn pasujących do filtrów przeciwnika zdefiniowanych dla tego terminu. Pomija krok wyboru konkretnego przeciwnika. Tryb możliwy do aktywowania w ciągu ${expressModeThreshold} dni przed rozpoczęciem terminu.`}
                            >
                              ?
                            </span>
                          </label>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* KROK 2: Wybierz przeciwną drużynę */}
                {findOpponentStep === 2 && (
                  <div className="wizard-step-content">
                    <h3>Wybierz przeciwną drużynę</h3>

                    {/* Filtry wyszukiwania */}
                    <div className="search-filters">
                      <div className="search-filters-row">
                        <div className="form-group">
                          <label htmlFor="find-voivodeship">Województwo</label>
                          <select
                            id="find-voivodeship"
                            name="voivodeship"
                            value={findOpponentSearchCriteria.voivodeship}
                            onChange={handleFindOpponentSearchChange}
                            className="form-select"
                          >
                            <option value="">-- Wszystkie --</option>
                            {findOpponentDictionaries.territories.map((territory) => (
                              <option key={territory.key} value={territory.key}>
                                {territory.displayValue}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="find-county">Powiat</label>
                          <select
                            id="find-county"
                            name="county"
                            value={findOpponentSearchCriteria.county}
                            onChange={handleFindOpponentSearchChange}
                            className="form-select"
                            disabled={!findOpponentSearchCriteria.voivodeship}
                          >
                            <option value="">-- Wszystkie --</option>
                            {getFindOpponentCountiesForVoivodeship().map((county) => (
                              <option key={county.key} value={county.key}>
                                {county.displayValue}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="find-tier">Poziom</label>
                          <select
                            id="find-tier"
                            name="seniorTier"
                            value={findOpponentSearchCriteria.seniorTier}
                            onChange={handleFindOpponentSearchChange}
                            className="form-select"
                          >
                            <option value="">-- Wszystkie --</option>
                            {(() => {
                              const scheduleTierValues = (findOpponentModal.schedule?.tiers || []).map(t => t.value);
                              const tiersToShow = scheduleTierValues.length > 0
                                ? findOpponentDictionaries.seniorTiers.filter(t => scheduleTierValues.includes(t.key))
                                : findOpponentDictionaries.seniorTiers;
                              return tiersToShow.map((tier) => (
                                <option key={tier.key} value={tier.key}>
                                  {tier.displayValue}
                                </option>
                              ));
                            })()}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="find-distance">Maks. odległość (km)</label>
                          <input
                            id="find-distance"
                            type="number"
                            min="1"
                            value={findOpponentMaxDistance}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') return;
                              const num = parseInt(val, 10);
                              if (!isNaN(num) && num >= 1) {
                                setFindOpponentMaxDistance(num);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            className="form-input"
                          />
                        </div>
                      </div>


                      {isYearRound && (
                        <div className="year-round-overlap-section">
                          <label className="tier-checkbox-label">
                            <input
                              type="checkbox"
                              checked={findOpponentIgnoreDateOverlap}
                              onChange={(e) => setFindOpponentIgnoreDateOverlap(e.target.checked)}
                            />
                            <span className="tier-checkbox-text">Pokaż wyniki niepokrywające się z moimi terminami</span>
                          </label>
                          {findOpponentIgnoreDateOverlap && (
                            <p className="year-round-overlap-info">
                              Wysłanie propozycji przeciwnikowi spoza Twoich terminów poskutkuje automatycznym stworzeniem nowego terminu dopasowanego do dat przeciwnika.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="search-actions">
                        <button type="button" onClick={handleFindOpponentResetSearch} className="btn btn-secondary">
                          Wyczyść
                        </button>
                        <button type="button" onClick={handleFindOpponentSearch} className="btn btn-primary" disabled={findOpponentIsLoadingSearch}>
                          {findOpponentIsLoadingSearch ? 'Wyszukiwanie...' : 'Szukaj'}
                        </button>
                      </div>
                    </div>

                    {/* Wyniki wyszukiwania */}
                    {findOpponentSearchError && <div className="error-message">{findOpponentSearchError}</div>}

                    {findOpponentSearchResults.length > 0 && (
                      <div className="search-results-full-width">
                        <h4>Wyniki wyszukiwania</h4>
                        {findOpponentTotalTeamsInSystem != null && (
                          <p style={{ fontSize: '0.75rem', color: '#888', margin: '-0.3rem 0 0.5rem', textAlign: 'center' }}>
                            Łączna liczba drużyn w systemie: <strong>{findOpponentTotalTeamsInSystem}</strong>.
                          </p>
                        )}
                        <div className="results-grid">
                          {findOpponentSearchResults.map((team, index) => (
                            <div key={index} className={`result-card ${findOpponentSelectedOpponent?.id === team.id ? 'selected' : ''}`}>
                              {team.logoUrl ? (
                                <img
                                  src={team.logoUrl}
                                  alt={`Logo ${team.name}`}
                                  style={{
                                    width: '64px',
                                    height: '64px',
                                    objectFit: 'contain',
                                    alignSelf: 'center',
                                    borderRadius: '4px'
                                  }}
                                />
                              ) : (
                                <div style={{ flex: 1 }} />
                              )}
                              <h5>{team.name}</h5>
                              <div className="team-info">
                                <p><strong>Klub:</strong> {team.sportsClubName}</p>
                                <p><strong>Woj.:</strong> {team.voivodeship}, <strong>Pow.:</strong> {team.county}</p>
                                {team.tier && <p><strong>Poziom rozgrywkowy:</strong> {team.tier}</p>}
                                {team.distanceKm != null && <p><strong>Odległość:</strong> {team.distanceKm} km</p>}
                                {findOpponentIgnoreDateOverlap && team.opponentScheduleStartDate && team.opponentScheduleEndDate && (
                                  <p><strong>Termin przeciwnika:</strong> {formatDate(team.opponentScheduleStartDate)} - {formatDate(team.opponentScheduleEndDate)}</p>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-primary btn-select"
                                onClick={() => handleSelectFindOpponent(team)}
                              >
                                {findOpponentSelectedOpponent?.id === team.id ? 'Wybrano ✓' : 'Wybierz'}
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Paginacja */}
                        {findOpponentTotalPages > 1 && (
                          <div className="pagination">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleFindOpponentPageChange(findOpponentCurrentPage - 1)}
                              disabled={findOpponentCurrentPage === 0}
                            >
                              ← Poprzednia
                            </button>
                            <span className="page-info">
                              Strona {findOpponentCurrentPage + 1} z {findOpponentTotalPages}
                            </span>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleFindOpponentPageChange(findOpponentCurrentPage + 1)}
                              disabled={findOpponentCurrentPage >= findOpponentTotalPages - 1}
                            >
                              Następna →
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* KROK 3: Wybierz datę i godzinę - nowy interfejs */}
                {findOpponentStep === 3 && (
                  <div className="wizard-step-content">
                    <h3>{findOpponentHasPitchAvailable ? 'Wybierz datę i godzinę' : 'Wybierz dzień'}</h3>
                    {!findOpponentLastMinuteMode && (
                      <div className="selected-team-info">
                        <strong>{findOpponentSelectedOpponent?.name}</strong>
                        {(() => {
                          const tier = findOpponentSelectedOpponent?.tier;
                          if (!tier) return null;
                          const allTiers = [...seniorTiers, ...youthTiers];
                          const tierDisplay = allTiers.find(t => t.key === tier)?.displayValue || tier;
                          return <div className="selected-team-tier">{tierDisplay}</div>;
                        })()}
                      </div>
                    )}
                    {!findOpponentHasPitchAvailable && (
                      <p className="step-description"><em>Godzina będzie ustalona później z przeciwnikiem</em></p>
                    )}

                    {!findOpponentHasPitchAvailable && findOpponentScheduleDates.length > 1 && (
                      <div className="determine-later-checkbox" style={{ margin: '0', textAlign: 'center' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '0.72rem' }}>
                          <input
                            type="checkbox"
                            checked={findOpponentDateToBeDetermined}
                            onChange={(e) => {
                              setFindOpponentDateToBeDetermined(e.target.checked);
                              if (e.target.checked) {
                                setFindOpponentSelectedDay(null);
                                setFindOpponentShouldBlockCalendar(false);
                              }
                            }}
                          />
                          Dokładny dzień do ustalenia później
                        </label>
                      </div>
                    )}

                    {!findOpponentHasPitchAvailable && (findOpponentModal.schedule?.amountOfGames || 1) > 1 && (
                      <div className="determine-later-checkbox" style={{ margin: '0', textAlign: 'center' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: findOpponentDateToBeDetermined ? 'default' : 'pointer', fontSize: '0.72rem', opacity: findOpponentDateToBeDetermined ? 0.4 : 1 }}>
                          <input
                            type="checkbox"
                            checked={findOpponentShouldBlockCalendar}
                            disabled={findOpponentDateToBeDetermined}
                            onChange={(e) => setFindOpponentShouldBlockCalendar(e.target.checked)}
                          />
                          Chcę zagrać tylko jeden sparing wybranego dnia
                        </label>
                      </div>
                    )}

                    {findOpponentScheduleDates.length === 0 ? (
                      <div className="loading">Brak dostępnych dat w wybranym terminie</div>
                    ) : (
                      <div className="date-time-picker-container">
                        {/* Sekcja wyboru dnia - kafelki */}
                        <div className="day-selection-section">
                          <h4>Wybierz dzień</h4>
                          <div className="days-grid">
                            {findOpponentScheduleDates.map((date, index) => {
                              const dateObj = new Date(date);
                              const isSelected = findOpponentSelectedDay === date;
                              const dayBlocked = isDayFullyBlocked(date);
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  className={`day-tile ${isSelected ? 'selected' : ''} ${findOpponentDateToBeDetermined || dayBlocked ? 'disabled' : ''}`}
                                  disabled={findOpponentDateToBeDetermined || dayBlocked}
                                  title={dayBlocked ? 'Wszystkie godziny w tym dniu są zajęte' : ''}
                                  onClick={() => {
                                    setFindOpponentSelectedDay(date);
                                    setFindOpponentCurrentDateIndex(index);
                                    // Reset time selection when day changes
                                    setFindOpponentSelectedTimeRange(null);
                                    setFindOpponentSelectedTimeSlot(null);
                                    // Fetch blocks only if we have a pitch
                                    if (findOpponentHasPitchAvailable) {
                                      fetchCalendarBlocks();
                                    }
                                  }}
                                >
                                  <span className="day-tile-weekday">
                                    {dateObj.toLocaleDateString('pl-PL', { weekday: 'long' })}
                                  </span>
                                  <span className="day-tile-date">
                                    {dateObj.toLocaleDateString('pl-PL', { day: '2-digit', month: 'long' })}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Główna sekcja: przedziały godzinowe + dokładne godziny - tylko gdy mamy boisko i wybrany dzień */}
                        {findOpponentHasPitchAvailable && (
                          <>
                            {!findOpponentSelectedDay ? (
                              <div className="time-selection-placeholder">
                                <p>Wybierz dzień aby zobaczyć dostępne godziny</p>
                              </div>
                            ) : (
                              <div className="time-selection-grid">
                                {/* Lewa strona: przedziały godzinowe (4 rzędy x 2 kolumny) */}
                                <div className="time-ranges-panel">
                                  <h4>Wybierz przedział godzinowy</h4>
                                  <div className="time-ranges-grid">
                                    {timeRanges.map((range, index) => {
                                      const rangeBlocked = isTimeRangeFullyBlocked(range.start, range.end);
                                      return (
                                        <button
                                          key={index}
                                          type="button"
                                          className={`time-range-btn ${findOpponentSelectedTimeRange?.label === range.label ? 'active' : ''} ${rangeBlocked ? 'blocked' : ''}`}
                                          onClick={() => !rangeBlocked && handleTimeRangeSelect(range)}
                                          disabled={rangeBlocked}
                                          title={rangeBlocked ? 'Ten przedział jest w pełni zajęty' : ''}
                                        >
                                          {range.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Prawa strona: dokładne godziny co 15 min */}
                                <div className="exact-times-panel">
                                  <h4>Wybierz dokładną godzinę</h4>
                                  {!findOpponentSelectedTimeRange ? (
                                    <div className="exact-times-placeholder">
                                      Wybierz przedział godzinowy po lewej stronie
                                    </div>
                                  ) : findOpponentIsLoadingBlocks ? (
                                    <div className="exact-times-placeholder">
                                      Ładowanie dostępnych godzin...
                                    </div>
                                  ) : (
                                    <div className="exact-times-grid">
                                      {generateTimeSlots(findOpponentSelectedTimeRange.start, findOpponentSelectedTimeRange.end).map((slot, index) => {
                                        const isBlocked = isTimeSlotBlocked(slot.hour, slot.minute);
                                        return (
                                          <button
                                            key={index}
                                            type="button"
                                            className={`exact-time-btn ${
                                              findOpponentSelectedTimeSlot?.hour === slot.hour &&
                                              findOpponentSelectedTimeSlot?.minute === slot.minute ? 'selected' : ''
                                            } ${isBlocked ? 'blocked' : ''}`}
                                            onClick={() => handleExactTimeSelect(slot)}
                                            disabled={isBlocked}
                                            title={isBlocked ? 'Ta godzina jest zajęta' : ''}
                                          >
                                            {slot.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Informacja o wybranej dacie i godzinie */}
                        {findOpponentHasPitchAvailable && findOpponentSelectedTimeSlot && (
                          <div className="selected-time-info">
                            <strong>Wybrano:</strong>{' '}
                            {new Date(findOpponentSelectedTimeSlot.date).toLocaleDateString('pl-PL', {
                              weekday: 'long',
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}{' '}
                            o godz. {String(findOpponentSelectedTimeSlot.hour).padStart(2, '0')}:{String(findOpponentSelectedTimeSlot.minute).padStart(2, '0')}
                          </div>
                        )}

                        {/* Informacja o wybranym dniu - gdy nie mamy boiska */}
                        {!findOpponentHasPitchAvailable && findOpponentSelectedDay && !findOpponentDateToBeDetermined && (
                          <div className="selected-time-info">
                            <strong>Wybrano dzień:</strong>{' '}
                            {new Date(findOpponentSelectedDay).toLocaleDateString('pl-PL', {
                              weekday: 'long',
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* KROK 4: Podsumowanie */}
                {findOpponentStep === 4 && (
                  <div className="wizard-step-content">
                    <h3>Podsumowanie</h3>
                    <p className="step-description step-description-summary">
                      {findOpponentLastMinuteMode
                        ? 'Sprawdź szczegóły przed wysłaniem powiadomień do pasujących drużyn'
                        : 'Sprawdź szczegóły przed wysłaniem zapytania o sparing'}
                    </p>

                    <div className="summary-section">
                      {findOpponentLastMinuteMode && (
                        <div className="summary-item">
                          <div className="summary-label">Tryb:</div>
                          <div className="summary-value">Przyspieszony</div>
                        </div>
                      )}

                      <div className="summary-item">
                        <div className="summary-label">Twoja drużyna:</div>
                        <div className="summary-value">
                          {isCoach
                            ? (reduxTeams.find(t => t.id === parseInt(findOpponentSelectedTeam))?.teamName || 'Nie wybrano')
                            : (teams.find(t => t.teamId === parseInt(findOpponentSelectedTeam))?.name || 'Nie wybrano')
                          }
                          {(() => {
                            const tier = isCoach
                              ? reduxTeams.find(t => t.id === parseInt(findOpponentSelectedTeam))?.tier
                              : teams.find(t => t.teamId === parseInt(findOpponentSelectedTeam))?.tier;
                            if (!tier) return null;
                            const allTiers = [...seniorTiers, ...youthTiers];
                            const display = allTiers.find(t => t.key === tier)?.displayValue || tier;
                            return <span className="summary-tier">({display})</span>;
                          })()}
                        </div>
                      </div>

                      {!findOpponentLastMinuteMode && (
                        <div className="summary-item">
                          <div className="summary-label">Przeciwna drużyna:</div>
                          <div className="summary-value">
                            {findOpponentSelectedOpponent ? `${findOpponentSelectedOpponent.name}` : 'Nie wybrano'}
                            {(() => {
                              const tier = findOpponentSelectedOpponent?.tier;
                              if (!tier) return null;
                              const allTiers = [...seniorTiers, ...youthTiers];
                              const display = allTiers.find(t => t.key === tier)?.displayValue || tier;
                              return <span className="summary-tier">({display})</span>;
                            })()}
                          </div>
                        </div>
                      )}

                      <div className="summary-item">
                        <div className="summary-label">Data:</div>
                        <div className="summary-value">
                          {findOpponentHasPitchAvailable
                            ? (findOpponentSelectedTimeSlot ? new Date(findOpponentSelectedTimeSlot.date).toLocaleDateString('pl-PL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Nie wybrano')
                            : (findOpponentDateToBeDetermined
                              ? 'Do ustalenia później'
                              : (findOpponentSelectedDay ? new Date(findOpponentSelectedDay).toLocaleDateString('pl-PL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Nie wybrano'))
                          }
                        </div>
                      </div>

                      {findOpponentHasPitchAvailable ? (
                        <div className="summary-item">
                          <div className="summary-label">Godzina:</div>
                          <div className="summary-value">
                            {findOpponentSelectedTimeSlot ? `${String(findOpponentMatchTime.hour).padStart(2, '0')}:${String(findOpponentMatchTime.minute || 0).padStart(2, '0')} - ${String(findOpponentMatchEndTime.hour).padStart(2, '0')}:${String(findOpponentMatchEndTime.minute || 0).padStart(2, '0')}` : 'Nie wybrano'}
                          </div>
                        </div>
                      ) : (
                        <div className="summary-item">
                          <div className="summary-label">Godzina:</div>
                          <div className="summary-value summary-value-muted">
                            {findOpponentDateToBeDetermined ? 'Do ustalenia później' : 'Do ustalenia z przeciwnikiem'}
                          </div>
                        </div>
                      )}

                      <div className="summary-item">
                        <div className="summary-label">Boisko:</div>
                        <div className="summary-value">
                          {findOpponentHasPitchAvailable
                            ? (findOpponentLocation ? findOpponentLocation : 'Dostępne')
                            : 'Niedostępne'}
                        </div>
                      </div>
                    </div>

                    {/* Pole wiadomości */}
                    <div className="message-field-section">
                      <label htmlFor="find-match-message">Wiadomość (opcjonalnie)</label>
                      <textarea
                        id="find-match-message"
                        className="form-textarea"
                        rows="4"
                        placeholder="Dodaj wiadomość do zapytania o sparing..."
                        value={findOpponentMatchMessage}
                        onChange={(e) => setFindOpponentMatchMessage(e.target.value)}
                        maxLength={800}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Nawigacja kroków */}
              {findOpponentStep < 4 ? (
              <div className="modal-footer wizard-navigation">
                {findOpponentStep > 1 && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={goToPreviousFindOpponentStep}
                  >
                    ← Wstecz
                  </button>
                )}

                <div className="flex-spacer"></div>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={goToNextFindOpponentStep}
                    disabled={
                      (findOpponentStep === 1 && findOpponentHasPitchAvailable && !findOpponentPitchLatitude) ||
                      (findOpponentStep === 2 && !findOpponentSelectedOpponent) ||
                      (findOpponentStep === 3 && (
                        findOpponentHasPitchAvailable
                          ? (!findOpponentSelectedDay || !findOpponentSelectedTimeRange || !findOpponentSelectedTimeSlot)
                          : (!findOpponentDateToBeDetermined && !findOpponentSelectedDay)
                      ))
                    }
                  >
                    Dalej →
                  </button>
              </div>
              ) : (
              <div className="modal-footer wizard-navigation-final">
                <div className="wizard-final-back">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={goToPreviousFindOpponentStep}
                  >
                    ← Wstecz
                  </button>
                </div>
                <div className="wizard-final-submit">
                  <button
                    type="submit"
                    className="btn btn-primary btn-submit"
                    disabled={findOpponentIsSendingMatch || findOpponentIsTransitioning}
                  >
                    {findOpponentIsSendingMatch ? 'Wysyłanie...' : 'Zapisz'}
                  </button>
                </div>
              </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Modal blokowania godzin */}
      {blockHoursModal.isOpen && (
        <div className="modal-overlay" onClick={handleBlockHoursCancel}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Zablokuj godziny</h2>
              <button className="modal-close-btn" onClick={handleBlockHoursCancel}>&times;</button>
            </div>
            <div className="modal-body">
              {blockHoursError && (
                <div className="validation-error" style={{ color: 'red', marginBottom: '16px' }}>
                  {blockHoursError}
                </div>
              )}
              <p className="block-hours-info">
                Dodaj blokady godzin dla terminu {formatDate(blockHoursModal.schedule?.approximateStartDate)} - {formatDate(blockHoursModal.schedule?.approximateEndDate)}. Przeciwnicy nie będą mogli składać propozycji z dokładną godziną na określone przedziały.
              </p>
              <div className="block-hours-days">
                {Object.entries(blockHoursData).map(([date, blocks]) => (
                  <div key={date} className="block-hours-day-section">
                    <div className="block-hours-day-header">
                      <span className="block-hours-day-title">{formatDateForBlockHours(date)}</span>
                      <button
                        type="button"
                        className="btn-add-block-hour"
                        onClick={() => handleAddBlockHour(date)}
                      >
                        + Dodaj
                      </button>
                    </div>
                    <div className="block-hours-entries">
                      {blocks.length === 0 && (
                        <span className="block-hours-empty">Brak blokad dla tego dnia</span>
                      )}
                      {blocks.map((block, index) => (
                        <div key={index} className="block-hour-entry">
                          <span className="block-hour-label">od</span>
                          <div className="time-input-with-arrows">
                            <button
                              type="button"
                              className="time-arrow-btn"
                              onClick={() => handleBlockHourChange(date, index, 'startTime', adjustTime(block.startTime, -1, 0))}
                              title="-1 godzina"
                            >
                              &#9664;
                            </button>
                            <input
                              type="time"
                              className="form-input"
                              value={block.startTime}
                              onChange={(e) => handleBlockHourChange(date, index, 'startTime', e.target.value)}
                            />
                            <button
                              type="button"
                              className="time-arrow-btn"
                              onClick={() => handleBlockHourChange(date, index, 'startTime', adjustTime(block.startTime, 0, 15))}
                              title="+15 minut"
                            >
                              &#9654;
                            </button>
                          </div>
                          <span className="block-hour-label">do</span>
                          <div className="time-input-with-arrows">
                            <button
                              type="button"
                              className="time-arrow-btn"
                              onClick={() => handleBlockHourChange(date, index, 'endTime', adjustTime(block.endTime, -1, 0))}
                              title="-1 godzina"
                            >
                              &#9664;
                            </button>
                            <input
                              type="time"
                              className="form-input"
                              value={block.endTime}
                              onChange={(e) => handleBlockHourChange(date, index, 'endTime', e.target.value)}
                            />
                            <button
                              type="button"
                              className="time-arrow-btn"
                              onClick={() => handleBlockHourChange(date, index, 'endTime', adjustTime(block.endTime, 0, 15))}
                              title="+15 minut"
                            >
                              &#9654;
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn-remove-block-hour"
                            onClick={() => handleRemoveBlockHour(date, index)}
                          >
                            Usuń
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleBlockHoursCancel}
                disabled={isSavingBlockHours}
              >
                Anuluj
              </button>
              <button
                className="btn-save"
                onClick={handleBlockHoursConfirm}
                disabled={isSavingBlockHours}
              >
                {isSavingBlockHours ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal "Zaproponuj termin" */}
      {/* Modal: Dodaj przeciwnika spoza systemu */}
      {manualOpponentModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseManualOpponentModal}>
          <div className="modal-content modal-propose-time" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dodaj przeciwnika spoza aplikacji</h2>
              <button className="modal-close-btn" onClick={handleCloseManualOpponentModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.72rem', color: '#555', marginBottom: '12px', lineHeight: '1.4', maxWidth: '420px', margin: '0 auto 12px', textAlign: 'justify', background: '#f1f8e9', border: '1px solid #c5e1a5', borderRadius: '6px', padding: '8px 12px' }}>
                Skorzystaj z tego formularza, aby umówić sparing z drużyną, która nie posiada jeszcze konta w aplikacji. Wystarczy podać nazwę przeciwnika, wybrać termin i opcjonalnie wskazać lokalizację boiska. Dodanie przeciwnika skutkuje zdjęciem terminu z wyszukiwarki (jeśli na dany termin potwierdzono już wszystkich przeciwników).
              </p>
              {/* Nazwa przeciwnika */}
              <div className="form-group" style={{ maxWidth: '320px', margin: '0 auto' }}>
                <label htmlFor="manual-opponent-name">Nazwa przeciwnika <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  id="manual-opponent-name"
                  className="form-input"
                  placeholder="Wpisz nazwę drużyny..."
                  value={manualOpponentName}
                  onChange={(e) => setManualOpponentName(e.target.value)}
                  maxLength={255}
                />
              </div>

              {/* Poziom rozgrywkowy */}
              <div className="form-group" style={{ maxWidth: '320px', margin: '12px auto 0' }}>
                <label htmlFor="manual-opponent-tier">Poziom rozgrywkowy <span style={{ color: 'red' }}>*</span></label>
                <select
                  id="manual-opponent-tier"
                  className="form-select"
                  value={manualOpponentTier}
                  onChange={(e) => setManualOpponentTier(e.target.value)}
                >
                  <option value="">-- Wybierz --</option>
                  <option value="EKSTRAKLASA">Ekstraklasa</option>
                  <option value="FIRST_LEAGUE">I Liga</option>
                  <option value="SECOND_LEAGUE">II Liga</option>
                  <option value="THIRD_LEAGUE">III Liga</option>
                  <option value="FOURTH_LEAGUE">IV Liga</option>
                  <option value="FIFTH_LEAGUE">V Liga</option>
                  <option value="REGIONAL_CLASS">Klasa Okręgowa</option>
                  <option value="CLASS_A">Klasa A</option>
                  <option value="CLASS_B">Klasa B</option>
                  <option value="CLASS_C">Klasa C</option>
                </select>
              </div>

              {/* Wybór dnia */}
              <div className="day-selection-section" style={{ marginTop: '16px' }}>
                <h4>Wybierz dzień <span style={{ color: 'red' }}>*</span></h4>
                <div className="days-grid">
                  {manualOpponentDates.map((date, index) => {
                    const dateObj = new Date(date);
                    const isSelected = manualOpponentSelectedDay === date;
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`day-tile ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          setManualOpponentSelectedDay(date);
                          setManualOpponentSelectedTimeRange(null);
                          setManualOpponentSelectedTimeSlot(null);
                        }}
                      >
                        <span className="day-tile-weekday">
                          {dateObj.toLocaleDateString('pl-PL', { weekday: 'long' })}
                        </span>
                        <span className="day-tile-date">
                          {dateObj.toLocaleDateString('pl-PL', { day: '2-digit', month: 'long' })}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Wybór godziny */}
              {manualOpponentSelectedDay && (
                <div className="time-selection-grid" style={{ marginTop: '16px' }}>
                  <div className="time-ranges-panel">
                    <h4>Przedział godzinowy *</h4>
                    <div className="time-ranges-grid">
                      {timeRanges.map((range, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`time-range-btn ${manualOpponentSelectedTimeRange?.label === range.label ? 'active' : ''}`}
                          onClick={() => handleManualOpponentTimeRangeSelect(range)}
                        >
                          {range.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="exact-times-panel">
                    <h4>Dokładna godzina *</h4>
                    {!manualOpponentSelectedTimeRange ? (
                      <div className="exact-times-placeholder">
                        Wybierz przedział godzinowy po lewej stronie
                      </div>
                    ) : (
                      <div className="exact-times-grid">
                        {generateTimeSlots(manualOpponentSelectedTimeRange.start, manualOpponentSelectedTimeRange.end).map((slot, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`exact-time-btn ${
                              manualOpponentSelectedTimeSlot?.hour === slot.hour &&
                              manualOpponentSelectedTimeSlot?.minute === slot.minute ? 'selected' : ''
                            }`}
                            onClick={() => handleManualOpponentTimeSlotSelect(slot)}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lokalizacja */}
              <div className="form-group" style={{ marginTop: '16px', textAlign: 'center', maxWidth: '420px', margin: '16px auto 0' }}>
                <label>Miejscowość boiska (opcjonalne)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Wpisz lokalizację boiska"
                  value={manualOpponentLocation}
                  onChange={(e) => setManualOpponentLocation(e.target.value)}
                />
              </div>

              {/* Podsumowanie wyboru */}
              {manualOpponentSelectedDay && manualOpponentSelectedTimeSlot && (
                <div className="selected-time-info">
                  <strong>Podsumowanie:</strong>{' '}
                  Mecz z <strong>{manualOpponentName || '...'}</strong> dnia{' '}
                  {new Date(manualOpponentSelectedDay).toLocaleDateString('pl-PL', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}{' '}
                  o godz. {String(manualOpponentSelectedTimeSlot.hour).padStart(2, '0')}:{String(manualOpponentSelectedTimeSlot.minute).padStart(2, '0')}
                  {manualOpponentLocation && `, ${manualOpponentLocation}`}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCloseManualOpponentModal}
                disabled={isSubmittingManualOpponent}
              >
                Anuluj
              </button>
              <button
                className="btn-save"
                onClick={handleManualOpponentSubmit}
                disabled={isSubmittingManualOpponent || !manualOpponentName.trim() || !manualOpponentTier || !manualOpponentSelectedDay || !manualOpponentSelectedTimeSlot}
              >
                {isSubmittingManualOpponent ? 'Dodawanie...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {proposeTimeModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseProposeTimeModal}>
          <div className="modal-content modal-propose-time" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{proposeTimeModal.game?.isManualOpponent || proposeTimeModal.game?.status === 'APPROVED' ? 'Edytuj termin' : 'Zaproponuj termin'}</h2>
              <button className="modal-close-btn" onClick={handleCloseProposeTimeModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Mecz z: <strong>{proposeTimeModal.game?.opponentTeamName}</strong>
              </p>

              {/* Wybór dnia */}
              <div className="day-selection-section">
                <h4>Wybierz dzień</h4>
                <div className="days-grid">
                  {proposeTimeDates.map((date, index) => {
                    const dateObj = new Date(date);
                    const isSelected = proposeTimeSelectedDay === date;
                    const dayBlocked = isProposeTimeDayFullyBlocked(date);
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`day-tile ${isSelected ? 'selected' : ''} ${dayBlocked ? 'blocked' : ''}`}
                        onClick={() => {
                          if (dayBlocked) return;
                          setProposeTimeSelectedDay(date);
                          setProposeTimeSelectedTimeRange(null);
                          setProposeTimeSelectedTimeSlot(null);
                        }}
                        disabled={dayBlocked}
                        title={dayBlocked ? 'Ten dzień jest w pełni zajęty' : ''}
                      >
                        <span className="day-tile-weekday">
                          {dateObj.toLocaleDateString('pl-PL', { weekday: 'long' })}
                        </span>
                        <span className="day-tile-date">
                          {dateObj.toLocaleDateString('pl-PL', { day: '2-digit', month: 'long' })}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Wybór godziny */}
              {proposeTimeSelectedDay && (
                <div className="time-selection-grid">
                  <div className="time-ranges-panel">
                    <h4>Wybierz przedział godzinowy</h4>
                    <div className="time-ranges-grid">
                      {timeRanges.map((range, index) => {
                        const rangeBlocked = isProposeTimeRangeFullyBlocked(proposeTimeSelectedDay, range.start, range.end);
                        return (
                          <button
                            key={index}
                            type="button"
                            className={`time-range-btn ${proposeTimeSelectedTimeRange?.label === range.label ? 'active' : ''} ${rangeBlocked ? 'blocked' : ''}`}
                            onClick={() => !rangeBlocked && handleProposeTimeRangeSelect(range)}
                            disabled={rangeBlocked}
                            title={rangeBlocked ? 'Ten przedział jest w pełni zajęty' : ''}
                          >
                            {range.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="exact-times-panel">
                    <h4>Wybierz dokładną godzinę</h4>
                    {!proposeTimeSelectedTimeRange ? (
                      <div className="exact-times-placeholder">
                        Wybierz przedział godzinowy po lewej stronie
                      </div>
                    ) : proposeTimeIsLoadingBlocks ? (
                      <div className="exact-times-placeholder">
                        Ładowanie dostępnych godzin...
                      </div>
                    ) : (
                      <div className="exact-times-grid">
                        {generateTimeSlots(proposeTimeSelectedTimeRange.start, proposeTimeSelectedTimeRange.end).map((slot, index) => {
                          const isBlocked = isProposeTimeSlotBlocked(proposeTimeSelectedDay, slot.hour, slot.minute);
                          return (
                            <button
                              key={index}
                              type="button"
                              className={`exact-time-btn ${
                                proposeTimeSelectedTimeSlot?.hour === slot.hour &&
                                proposeTimeSelectedTimeSlot?.minute === slot.minute ? 'selected' : ''
                              } ${isBlocked ? 'blocked' : ''}`}
                              onClick={() => !isBlocked && handleProposeTimeSlotSelect(slot)}
                              disabled={isBlocked}
                              title={isBlocked ? 'Ta godzina jest zajęta' : ''}
                            >
                              {slot.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Boisko */}
              <div className="form-group" style={{ marginTop: '16px', textAlign: 'center', maxWidth: '420px', margin: '16px auto 0' }}>
                <label>Miejscowość boiska</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Wpisz lokalizację boiska"
                  value={proposeTimeLocation}
                  onChange={(e) => setProposeTimeLocation(e.target.value)}
                />
              </div>

              {/* Podsumowanie wyboru */}
              {proposeTimeSelectedDay && proposeTimeSelectedTimeSlot && (
                <div className="selected-time-info">
                  <strong>Wybrano:</strong>{' '}
                  {new Date(proposeTimeSelectedDay).toLocaleDateString('pl-PL', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}{' '}
                  o godz. {String(proposeTimeSelectedTimeSlot.hour).padStart(2, '0')}:{String(proposeTimeSelectedTimeSlot.minute).padStart(2, '0')}
                </div>
              )}
            </div>
            {!proposeTimeModal.game?.isManualOpponent && (
              <div className="propose-time-message-section">
                <label className="propose-time-message-label">Wiadomość (opcjonalnie)</label>
                <textarea
                  className="propose-time-message-textarea"
                  placeholder="Dodaj wiadomość do propozycji..."
                  value={proposeTimeMessage}
                  onChange={(e) => setProposeTimeMessage(e.target.value)}
                  rows={3}
                  maxLength={800}
                />
              </div>
            )}
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCloseProposeTimeModal}
                disabled={isProposingTime}
              >
                Anuluj
              </button>
              <button
                className="btn-save"
                onClick={handleProposeTimeSubmit}
                disabled={isProposingTime || !proposeTimeSelectedDay || !proposeTimeSelectedTimeSlot}
              >
                {isProposingTime
                  ? (proposeTimeModal.game?.isManualOpponent ? 'Zapisywanie...' : 'Wysyłanie...')
                  : (proposeTimeModal.game?.isManualOpponent ? 'Zapisz' : 'Wyślij propozycję')}
              </button>
            </div>
          </div>
        </div>
      )}

      <MapPickerModal
        isOpen={findOpponentMapPickerOpen}
        onConfirm={handleFindOpponentMapConfirm}
        onCancel={() => setFindOpponentMapPickerOpen(false)}
        initialLat={findOpponentPitchLatitude}
        initialLon={findOpponentPitchLongitude}
        initialAddress={findOpponentLocation}
      />
      {/* Modal edycji obozu */}
      {campEditModal.isOpen && (
        <div className="map-picker-overlay" onClick={handleCampEditClose}>
          <div className="camp-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="camp-edit-modal-header">
              <h2>Edytuj informacje o obozie</h2>
              <button className="map-picker-close-btn" onClick={handleCampEditClose}>&times;</button>
            </div>
            <div className="camp-edit-modal-body">
              <label className="checkbox-label camp-sidebar-checkbox">
                <input
                  type="checkbox"
                  checked={campEditIsAtCamp}
                  onChange={handleCampEditToggle}
                />
                <span>Drużyna jest w tym terminie na obozie</span>
              </label>

              {campEditIsAtCamp && (
                <div className="camp-edit-modal-details">
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn btn-camp-map"
                      onClick={() => setCampMapPickerOpen(true)}
                    >
                      {campEditAddress ? 'Zmień lokalizację na mapie' : 'Zaznacz na mapie'}
                    </button>
                    {campEditAddress ? (
                      <div className="camp-address-summary">
                        <span className="camp-address-text">{campEditAddress}</span>
                      </div>
                    ) : (
                      <p className="validation-error" style={{ color: '#d32f2f', fontSize: '0.85rem', margin: '4px 0 0' }}>
                        Zaznacz lokalizację obozu na mapie
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Maksymalny dojazd z obozu na mecz z przeciwnikiem (km)</label>
                    <input
                      type="number"
                      className="form-input"
                      min="0"
                      value={campEditMaxDistanceKm}
                      onChange={(e) => setCampEditMaxDistanceKm(e.target.value)}
                      placeholder="np. 50"
                      style={{ width: '120px' }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="camp-edit-modal-footer">
              <button className="btn btn-secondary" onClick={handleCampEditClose}>
                Anuluj
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCampEditSave}
                disabled={isSavingCamp || (campEditIsAtCamp && campEditLatitude == null)}
              >
                {isSavingCamp ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MapPickerModal
        isOpen={campMapPickerOpen}
        onConfirm={handleCampMapConfirm}
        onCancel={() => setCampMapPickerOpen(false)}
        initialLat={campEditLatitude}
        initialLon={campEditLongitude}
        initialAddress={campEditAddress}
      />

      {shareDeleteConfirm.isOpen && (
        <div className="modal-overlay" onClick={handleCancelDeleteShareInvitation} style={{ zIndex: 1100 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Potwierdzenie usunięcia</h2>
              <button className="modal-close-btn" onClick={handleCancelDeleteShareInvitation}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Czy na pewno chcesz usunąć link z terminami:</p>
              <ul style={{ margin: '6px 0 10px 18px', padding: 0 }}>
                {shareDeleteConfirm.invitation?.terms.map(t => (
                  <li key={t.invitationTermId} style={{ fontSize: '0.85rem' }}>
                    {formatDate(t.startDate)} – {formatDate(t.endDate)}
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: 8, fontSize: '0.8rem', color: '#888' }}>
                Link przestanie działać. Wcześniej złożone propozycje sparingów pozostaną w systemie, ale stracą powiązanie z tym linkiem (auto-odrzucanie pozostałych propozycji z tego linku po akceptacji nie zadziała dla tych konkretnych gier).
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCancelDeleteShareInvitation}
                disabled={isDeletingShare}
              >
                Nie
              </button>
              <button
                className="btn-delete-confirm"
                onClick={handleConfirmDeleteShareInvitation}
                disabled={isDeletingShare}
              >
                {isDeletingShare ? 'Usuwanie...' : 'Tak, usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {shareInvitationModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseShareInvitationModal}>
          <div className="modal-content modal-content-fullscreen" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generuj zaproszenia</h2>
              <button className="modal-close" onClick={handleCloseShareInvitationModal}>×</button>
            </div>
            <div className="modal-body">
              {!shareGenerated && (
                <>
                  <p style={{ marginBottom: 12 }}>
                    Wybierz terminy, które chcesz umieścić w jednym linku. Po wygenerowaniu skopiuj link i wklej do posta.
                  </p>
                  {(!summaryData?.games || summaryData.games.length === 0) ? (
                    <p style={{ color: '#7f8c8d' }}>Najpierw dodaj termin, aby móc wygenerować link.</p>
                  ) : (
                    <div style={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12 }}>
                      {summaryData.games.map((g) => (
                        <label key={g.termId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={shareSelectedTermIds.includes(g.termId)}
                            onChange={() => handleToggleShareTerm(g.termId)}
                          />
                          <span>{formatDate(g.approximateStartDate)} – {formatDate(g.approximateEndDate)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {shareError && <div className="error-message" style={{ marginTop: 8 }}>{shareError}</div>}
                </>
              )}

              {shareGenerated && (
                <div>
                  <p style={{ marginBottom: 8, fontWeight: 600 }}>Link wygenerowany</p>
                  <div style={{ marginBottom: 10, padding: '8px 10px', background: '#eef5ff', border: '1px solid #cfe1ff', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, color: '#3a6ea8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                      Terminy w tym linku ({shareGenerated.terms.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {shareGenerated.terms.map(t => (
                        <span
                          key={t.invitationTermId}
                          style={{ background: '#fff', border: '1px solid #b9d4ff', borderRadius: 12, padding: '2px 10px', fontSize: 13 }}
                        >
                          {formatDate(t.startDate)} – {formatDate(t.endDate)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <input
                      readOnly
                      value={shareGenerated.shareUrl}
                      style={{ flex: 1, padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
                      onFocus={(e) => e.target.select()}
                    />
                    <button className="btn btn-primary" onClick={() => handleCopyShareLink(shareGenerated.shareUrl)}>
                      Kopiuj link
                    </button>
                  </div>
                  <p style={{ marginTop: 12, fontWeight: 600 }}>Tekst do wklejenia (jeden termin):</p>
                  {shareGenerated.terms.map((t) => (
                    <textarea
                      key={t.invitationTermId}
                      readOnly
                      rows={4}
                      style={{ width: '100%', marginBottom: 6, padding: 8, fontSize: 13, lineHeight: 1.4, resize: 'vertical' }}
                      value={buildShareTextSingle(t, shareGenerated.shareUrl)}
                      onFocus={(e) => e.target.select()}
                    />
                  ))}
                  {shareGenerated.terms.length > 1 && (
                    <>
                      <p style={{ marginTop: 12, fontWeight: 600 }}>Tekst zbiorczy:</p>
                      <textarea
                        readOnly
                        rows={6}
                        style={{ width: '100%', padding: 8, fontSize: 13, lineHeight: 1.4, resize: 'vertical' }}
                        value={buildShareTextBulk(shareGenerated.terms, shareGenerated.shareUrl)}
                        onFocus={(e) => e.target.select()}
                      />
                    </>
                  )}
                </div>
              )}

              <hr style={{ margin: '20px 0' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Twoje aktywne linki</h3>
                <button className="btn btn-secondary" onClick={refreshShareList} disabled={shareListLoading}>
                  Odśwież
                </button>
              </div>
              {shareListLoading ? (
                <p style={{ color: '#7f8c8d' }}>Ładowanie...</p>
              ) : shareList.length === 0 ? (
                <p style={{ color: '#7f8c8d' }}>Brak wygenerowanych linków dla tej drużyny.</p>
              ) : (
                <div style={{ maxHeight: '35vh', overflowY: 'auto' }}>
                  {shareList.map((inv) => (
                    <div key={inv.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1, fontSize: 13 }}>
                          <div>
                            <strong>{inv.terms.length} {inv.terms.length === 1 ? 'termin' : 'terminów'}</strong>
                            {inv.active ? (
                              <span style={{ marginLeft: 8, color: '#2e7d32' }} title="Link działa — nowe drużyny mogą odpowiadać">aktywny</span>
                            ) : (
                              <span
                                style={{ marginLeft: 8, color: '#999' }}
                                title="Link nie przyjmuje już nowych odpowiedzi, ale wcześniej złożone propozycje sparingów wciąż obowiązują"
                              >
                                dezaktywowany
                              </span>
                            )}
                          </div>
                          <div style={{ color: '#555' }}>
                            {inv.terms.map(t => `${formatDate(t.startDate)}-${formatDate(t.endDate)}`).join('; ')}
                          </div>
                          <div style={{ color: '#777', marginTop: 4 }}>
                            Odpowiedzi: {inv.responseCount}
                          </div>
                          <div style={{ marginTop: 6 }}>
                            <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 2 }}>Treść zaproszenia (do skopiowania):</label>
                            <textarea
                              readOnly
                              rows={3}
                              style={{ width: '100%', padding: 6, fontSize: 12, lineHeight: 1.4, resize: 'vertical' }}
                              value={buildShareTextBulk(inv.terms, inv.shareUrl)}
                              onFocus={(e) => e.target.select()}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 8px' }} onClick={() => handleCopyShareLink(inv.shareUrl)}>
                            Kopiuj link
                          </button>
                          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 8px' }} onClick={() => handleCopyShareLink(buildShareTextBulk(inv.terms, inv.shareUrl))}>
                            Kopiuj treść
                          </button>
                          {inv.active && (
                            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 8px', color: '#c62828' }} onClick={() => handleDeactivateShareInvitation(inv.token)}>
                              Dezaktywuj
                            </button>
                          )}
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '4px 8px', color: '#fff', background: '#c62828', borderColor: '#c62828' }}
                            onClick={() => handleDeleteShareInvitation(inv)}
                          >
                            Usuń
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!shareGenerated ? (
                <>
                  <button className="btn btn-secondary" onClick={handleCloseShareInvitationModal}>
                    Zamknij
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleGenerateShareInvitation}
                    disabled={shareIsGenerating || shareSelectedTermIds.length === 0}
                  >
                    {shareIsGenerating ? 'Generowanie...' : 'Wygeneruj link'}
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={handleCloseShareInvitationModal}>
                  Gotowe
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PreseasonGamesSummary;
